import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./EditarPlanilha.css";
import {
  planilhaService,
  type ItemAfetado,
} from "../../services/planilhaService";
import { ModalConfirmarInvalidacaoCotacoes } from "../../components/ModalConfirmarInvalidacaoCotacoes";
import {
  formatDateBR,
  formatPeriodoEvento,
} from "../../utils/orcamento-formatters";
import { fornecedorService } from "../../services/fornecedorService";
import { excelExportService } from "../../services/excelExportService";
import {
  orcamentoService,
  type VersaoPdfCotacao,
} from "../../services/orcamentoService";
import { ouvirCotacaoEditada } from "../../utils/avisoCotacaoEditada";
import apiClient from "../../services/api";
import {
  Fornecedor,
  TIPOS_LINHA_COM_ANEXO,
  TIPOS_ITEM_SEM_COTACAO,
  FornecedorEnvioBloco,
  ModoEnvioFornecedor,
  Planilha,
  PlanilhaStatus,
  ItemPlanilha,
  TipoItem,
  StatusOrcamento,
} from "@models/index";
import {
  taxaDeHonorarios,
  PERCENT_IMPOSTOS,
  PERCENT_TAXA_ADMINISTRACAO,
  calcularImpostoPorDentro,
  formatarPercentual,
} from "../../utils/planilhaTotais";
import {
  applyStatusToFornecedor,
  corrigirNumerosFornecedores,
  recalcularTotaisEMenorPreco,
  solicitacaoJaEnviada,
} from "../../utils/planilhaItemUtils";
import { NumericFormat } from "react-number-format";
import ModalEnvioIndividual from "../../components/ModalEnvioIndividual";
import ModalEnvioItem from "../../components/ModalEnvioItem";
import ModalEnvioFornecedor from "../../components/ModalEnvioFornecedor";
import { useFeedback } from "@contexts/FeedbackContext";
import { useUnsavedChanges } from "@contexts/UnsavedChangesContext";
import { useKeyboardShortcuts, ShortcutDef } from "@hooks/useKeyboardShortcuts";
import ShortcutsHelpModal from "@components/common/ShortcutsHelpModal/ShortcutsHelpModal";
import { formatarTempoRelativo, parseDataApi } from "@utils/tempoRelativo";
import { Modal } from "@components/common";
import {
  FiXCircle,
  FiDownload,
  FiFileText,
  FiBarChart2,
  FiArrowLeft,
  FiSave,
  FiSend,
  FiTrash2,
  FiPlus,
  FiClock,
  FiCheckCircle,
  FiRepeat,
  FiX,
  FiPaperclip,
  FiUpload,
  FiRefreshCw,
  FiLock,
  FiList,
  FiBell,
  FiEye,
  FiEyeOff,
  FiMoreHorizontal,
  FiCopy,
  FiExternalLink,
  FiChevronDown,
  FiEdit2,
} from "react-icons/fi";
import AsyncSelect from "react-select/async";
import ModalAdicionarFornecedor from "@/components/ModalAdicionarFornecedor";
import { exportacaoUnificadaService } from "@/services/exportacaoUnificadaService";
import { usePlanilhaLock } from "@/hooks/usePlanilhaLock";
import { usePodeEditar } from "@hooks/usePodeEditar";
import PlanilhaHistoricoModal from "@components/PlanilhaHistoricoModal";

interface SubgrupoModalState {
  open: boolean;
  targetId: string;
  nome: string;
  error: string;
}

/**
 * Gera uma assinatura estável do conteúdo persistível do editor (itens +
 * breakdown). Usada para detectar "alterações não salvas" comparando o estado
 * atual com o snapshot do último salvamento/carregamento. Só considera o que
 * vai ao backend — ignora estado de UI (modais, sticky header, etc.).
 */
function buildEditorSignature(
  itens: ItemPlanilha[],
  breakdownEquipamentos: string,
  breakdownMateriais: string,
  breakdownServicos: string,
  breakdownPercents: BreakdownPercents,
): string {
  // Considera "edição do usuário" apenas o que ele digita no grid. Ignora:
  //  - linhas de FORNECEDOR (vêm de orçamentos/respostas do servidor);
  //  - em itens normais, unitário/subtotal/total/menorPreço são DERIVADOS do menor
  //    preço dos fornecedores (recalculados no refresh) — não são edição do usuário.
  // Itens de TAXAS mantêm seus valores (são preenchidos manualmente).
  const editaveis = (itens || [])
    .filter((i) => i.tipo !== TipoItem.FORNECEDOR)
    .map((i) =>
      i.tipo === TipoItem.ITEM
        ? {
            id: i.id,
            tipo: i.tipo,
            numero: i.numero,
            item: i.item,
            descritivo: i.descritivo,
            quantidade: i.quantidade,
            diaria: i.diaria,
            observacoes: i.observacoes,
            fornecedorId: i.fornecedorId,
          }
        : i,
    );
  return JSON.stringify({
    itens: editaveis,
    breakdown: [breakdownEquipamentos, breakdownMateriais, breakdownServicos],
    breakdownPercents,
  });
}

/**
 * Reordena as linhas de FORNECEDOR dentro de cada item pelo valor unitário
 * (mais barato primeiro; zerados/sem valor por último) e reatribui o número
 * sequencial — mesma lógica do `expandItensComFornecedores` usada no carregamento.
 * Usado no refresh automático para refletir a nova ordem sem precisar de F5.
 */
/**
 * Título base da aba, capturado no carregamento do módulo (antes de qualquer
 * edição) — garante restauração confiável, sem depender de estado que possa
 * ter sido "poluído" com o título de edição.
 */
const TITULO_BASE =
  typeof document !== "undefined" && document.title
    ? document.title
    : "Sistema de Cotação - MONUMENTA";

/** Anexo de taxas e seguros. Precisa espelhar a validação do backend. */
const EXTENSOES_ANEXO_PERMITIDAS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".png",
  ".jpg",
  ".jpeg",
];
const TAMANHO_MAXIMO_ANEXO_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Nome do item para a solicitação de cotação.
 *
 * O campo "PRODUTOS E SERVIÇOS" pode ficar vazio quando a linha é descrita só
 * pelo descritivo — é o caso de item de cenografia, que tem o detalhamento
 * inteiro ali. Cair no descritivo é a mesma convenção que abrirModalItem e
 * abrirModalFornecedor já usavam para montar o rótulo na tela.
 */
const nomeDoItemParaCotacao = (item: ItemPlanilha): string =>
  item.item?.trim() || item.descritivo?.trim() || `Item ${item.numero ?? ""}`.trim();

/** Faixas do rateio de Serviços Complementares. */
type FaixaBreakdown = "equipamentos" | "materiais" | "servicos";

type BreakdownPercents = Record<FaixaBreakdown, string>;

const BREAKDOWN_PERCENTS_VAZIO: BreakdownPercents = {
  equipamentos: "",
  materiais: "",
  servicos: "",
};

/**
 * Valor em reais de uma faixa do rateio.
 *
 * O percentual manda quando existe: é o que faz o valor acompanhar a base
 * quando as cotações mudam o total de complementares. Sem percentual, vale o
 * número digitado — é o caso da planilha gravada antes deste campo, e de quem
 * escolhe um valor fixo de propósito.
 */
const valorDaFaixa = (
  percent: string,
  valorAbsoluto: string,
  base: number,
): number => {
  const pct = parseFloat(percent);
  if (percent !== "" && !Number.isNaN(pct)) {
    return (base * pct) / 100;
  }
  return parseFloat(valorAbsoluto) || 0;
};

/**
 * Quem pode digitar o quê, por linha.
 *
 * Estas três regras substituem listas de tipos repetidas em cada coluna. A
 * lista era o motivo de TRANSPORTES nascer travado: o tipo era novo e não
 * estava em nenhuma delas, então toda célula caía no ramo de leitura.
 */

/** Linha amarela do ITEM 2: nome, descritivo, quantidade e diária são digitados. */
const editaTextoDoItem2 = (row: ItemPlanilha) =>
  (row.tipo === TipoItem.ITEM || TIPOS_ITEM_SEM_COTACAO.includes(row.tipo)) &&
  !!row.numero &&
  !row.numero.startsWith("1.");

/** Linha amarela de valor manual (TAXAS, TRANSPORTES): o preço não vem de cotação. */
const editaValorManual = (row: ItemPlanilha) =>
  TIPOS_ITEM_SEM_COTACAO.includes(row.tipo) &&
  !!row.numero &&
  row.numero.startsWith("2.");

/**
 * Linha branca de TAXAS/TRANSPORTES.
 *
 * Governa os cálculos próprios da linha (subtotal = qtd x diária x unitário).
 * Despesa acessória NÃO entra: ali o subtotal é digitado e sobe para a
 * categoria por outro caminho, e incluí-la mudaria conta de planilha antiga.
 */
const linhaBrancaDeFornecedor = (row: ItemPlanilha) =>
  row.tipo === TipoItem.DESCRITIVO_TAXAS ||
  row.tipo === TipoItem.DESCRITIVO_TRANSPORTE;

/**
 * Linhas cujo nome é escolhido no select de fornecedor, e não digitado.
 *
 * Separado do de cima de propósito: despesa acessória passou a usar o select
 * no nome, mas continua com os cálculos que sempre teve. Misturar as duas
 * coisas num predicado só mudaria os totais de planilha já fechada.
 */
const nomeVemDoSelectDeFornecedor = (row: ItemPlanilha) =>
  linhaBrancaDeFornecedor(row) || row.tipo === TipoItem.DESCRITIVO_ACESSORIA;

/** Linha amarela que é pai de cada tipo de linha branca. */
const tipoAmareloDaLinhaBranca = (tipo: TipoItem): TipoItem =>
  tipo === TipoItem.DESCRITIVO_TRANSPORTE
    ? TipoItem.ITEM_TRANSPORTE
    : tipo === TipoItem.DESCRITIVO_ACESSORIA
      ? TipoItem.ITEM_ACESSORIA
      : TipoItem.ITEM_TAXAS;

/**
 * Números das linhas amarelas sob as linhas pretas TAXAS E SEGUROS que
 * passam no filtro. Todas as linhas pretas com esse nome contam: a categoria
 * pode ser duplicada ou trocada, então não há garantia de que seja uma só.
 */
const numerosDosItensDeTaxas = (
  itens: ItemPlanilha[],
  entra: (row: ItemPlanilha, indice: number) => boolean,
): Set<string> => {
  const numeros = new Set<string>();
  let dentro = false;
  itens.forEach((row, i) => {
    if (row.tipo === TipoItem.SUBGRUPO || row.tipo === TipoItem.GRUPO) {
      dentro =
        row.tipo === TipoItem.SUBGRUPO &&
        !!row.item?.toUpperCase().includes("TAXAS E SEGUROS");
      return;
    }
    if (dentro && row.numero && entra(row, i)) numeros.add(row.numero);
  });
  return numeros;
};

/**
 * E-mail com que o modal de envio abre: o da Receita, e o do cadastro quando a
 * Receita não traz nenhum.
 *
 * `email` não serve de padrão: o envio grava nele o endereço digitado no modal
 * (é para onde vão lembrete e confirmação), e o modal seguinte abria com o
 * último usado. `emailCnpj` só é escrito com dados da Receita.
 */
const emailPadraoDoFornecedor = (
  ...fontes: Array<{ email?: string; emailCnpj?: string } | null | undefined>
): string =>
  fontes.map((f) => f?.emailCnpj?.trim()).find(Boolean) ||
  fontes.map((f) => f?.email?.trim()).find(Boolean) ||
  "";

const arredondarCentavos = (valor: number) => Math.round(valor * 100) / 100;

/**
 * Linhas brancas com valor de uma categoria de despesa acessória. A branca
 * pertence à amarela pelo prefixo do número (3.2 -> 3.2.1, 3.2.2), o mesmo
 * critério de renumerarItem3 e da remoção da categoria.
 */
const brancasComValor = (dados: ItemPlanilha[], amarela: ItemPlanilha) =>
  amarela.numero
    ? dados.filter(
        (r) =>
          r.tipo === TipoItem.DESCRITIVO_ACESSORIA &&
          !!r.numero?.startsWith(amarela.numero + ".") &&
          (r.subtotal ?? 0) > 0,
      )
    : [];

/**
 * Despesa acessória: a linha amarela é a soma das brancas.
 *
 * O valor digitado na branca não subia para a amarela, e o total da categoria
 * tinha de ser digitado de novo à mão. Agora sobe e soma.
 *
 * Só vale para categoria com alguma branca com valor. Categoria antiga, com o
 * valor digitado direto na amarela e as brancas vazias, fica como está — e
 * nada é recalculado ao abrir a planilha, só quando alguém edita uma linha da
 * categoria. A exceção é `zerarSemValor`: a amarela cuja última branca com
 * valor acabou de ser removida, que volta a zero.
 *
 * Também refaz o total do subgrupo DESPESAS ACESSÓRIAS.
 */
function somarDespesasAcessorias(
  dados: ItemPlanilha[],
  zerarSemValor: Set<string> = new Set(),
): ItemPlanilha[] {
  const novo = dados.map((r) => {
    if (r.tipo !== TipoItem.ITEM_ACESSORIA) return r;
    const brancas = brancasComValor(dados, r);
    if (brancas.length === 0) {
      return r.id && zerarSemValor.has(r.id)
        ? { ...r, subtotal: 0, total: 0 }
        : r;
    }
    const soma = arredondarCentavos(
      brancas.reduce((t, b) => t + (b.subtotal ?? 0), 0),
    );
    return { ...r, subtotal: soma, total: soma };
  });

  const idxSubgrupo = novo.findIndex(
    (r) => r.tipo === TipoItem.SUBGRUPO && r.item === "DESPESAS ACESSÓRIAS",
  );
  if (idxSubgrupo >= 0) {
    const totalSubgrupo = arredondarCentavos(
      novo.reduce(
        (t, r) =>
          r.tipo === TipoItem.ITEM_ACESSORIA && r.subtotal ? t + r.subtotal : t,
        0,
      ),
    );
    novo[idxSubgrupo] = {
      ...novo[idxSubgrupo],
      subtotal: totalSubgrupo,
      total: totalSubgrupo,
    };
  }
  return novo;
}

/** Linhas que pertencem ao item amarelo logo acima delas. */
const TIPOS_FILHOS_DO_ITEM2: TipoItem[] = [
  TipoItem.FORNECEDOR,
  TipoItem.DESCRITIVO_TAXAS,
  TipoItem.DESCRITIVO_TRANSPORTE,
];

/**
 * Cópia de uma linha para duplicar, com id novo e sem nada da cotação.
 *
 * O id novo é o que faz o servidor criar registro em vez de sobrescrever o
 * original. Na linha de fornecedor fica só quem é o fornecedor: status volta a
 * "aguardando envio" e somem valor, PDF, orçamento e a observação, que ali é a
 * resposta dele. O preço do item cotado deriva do menor preço, então zera
 * junto. Valor digitado (TAXAS, TRANSPORTES) é da planilha e vai na cópia;
 * anexos não, porque são guardados pelo id da linha de origem.
 */
const copiarLinhaParaDuplicar = (row: ItemPlanilha): ItemPlanilha => {
  const copia: ItemPlanilha = {
    ...row,
    id: crypto.randomUUID(),
    fornecedores: undefined,
    menorPreco: undefined,
    orcamentoId: undefined,
    orcamentoPdfUrl: undefined,
    orcamentoPdfRejeicaoUrl: undefined,
    ultimoLembreteEm: undefined,
    ultimoAcessoEm: undefined,
    totalAcessos: undefined,
    solicitadoPorId: undefined,
    solicitadoPorNome: undefined,
    solicitadoPorEmail: undefined,
    dataResposta: undefined,
  };
  if (row.tipo === TipoItem.FORNECEDOR) {
    return {
      ...copia,
      status: StatusOrcamento.AGUARDANDO_ENVIO,
      unitario: 0,
      subtotal: undefined,
      total: undefined,
      observacoes: "",
    };
  }
  if (row.tipo === TipoItem.ITEM) {
    return { ...copia, unitario: 0, subtotal: 0, total: 0, status: undefined };
  }
  return copia;
};

function reordenarFornecedoresPorPreco(rows: ItemPlanilha[]): ItemPlanilha[] {
  const resultado: ItemPlanilha[] = [];
  let i = 0;
  while (i < rows.length) {
    const row = rows[i];
    if (
      row.tipo === TipoItem.ITEM ||
      TIPOS_ITEM_SEM_COTACAO.includes(row.tipo)
    ) {
      resultado.push(row);
      const forns: ItemPlanilha[] = [];
      let j = i + 1;
      while (j < rows.length && rows[j].tipo === TipoItem.FORNECEDOR) {
        forns.push(rows[j]);
        j++;
      }
      if (forns.length > 0) {
        const ordenados = [...forns].sort((a, b) => {
          const ua = a.unitario ?? Infinity;
          const ub = b.unitario ?? Infinity;
          if (ua === 0 && ub === 0) return 0;
          if (ua === 0) return 1;
          if (ub === 0) return -1;
          return ua - ub;
        });
        ordenados.forEach((f, idx) => {
          resultado.push({
            ...f,
            numero: row.numero ? `${row.numero}.${idx + 1}` : f.numero,
          });
        });
        i = j;
        continue;
      }
    } else {
      resultado.push(row);
    }
    i++;
  }
  return resultado;
}

function EditarPlanilhaV3() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { confirm, showSuccess, showError, showWarning, showInfo } =
    useFeedback();
  const { setDirty, guardNavigation } = useUnsavedChanges();

  // Lock de edição — controla acesso simultâneo
  // Visualizador só consulta: não pega o lock — senão travaria a planilha para
  // um Admin — e a planilha inteira fica em somente leitura para ele.
  const podeEditar = usePodeEditar();
  const somenteConsulta = !podeEditar;
  const {
    isReadOnly: travadaPorOutro,
    lockedBy,
    lockLoading,
  } = usePlanilhaLock(id ?? "", { ativo: podeEditar });
  const isReadOnly = travadaPorOutro || somenteConsulta;

  const gradeRef = useRef<HTMLDivElement>(null);

  // Marca no body, e não no container, porque os menus e modais da planilha
  // são renderizados fora dele. O CSS esconde o que tem `data-altera` e trava
  // os campos da grade. Sai junto com o editor.
  useEffect(() => {
    document.body.classList.toggle("somente-consulta", somenteConsulta);
    return () => document.body.classList.remove("somente-consulta");
  }, [somenteConsulta]);
  const [historicoModalAberto, setHistoricoModalAberto] = useState(false);

  // Atalhos de teclado + indicador de alterações não salvas
  const [shortcutsModalAberto, setShortcutsModalAberto] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveErrored, setSaveErrored] = useState(false);

  // Lembrete ao fornecedor (item 3.1)
  /** Linha de fornecedor cujo menu de ações secundárias está aberto. */
  const [acoesFornecedor, setAcoesFornecedor] = useState<ItemPlanilha | null>(
    null,
  );

  const [modalExportarAberto, setModalExportarAberto] = useState(false);

  /** Histórico de PDFs aberto pelo menu da linha do fornecedor. */
  const [historicoPdf, setHistoricoPdf] = useState<{
    orcamentoId: string;
    nome: string;
    versoes: VersaoPdfCotacao[] | null;
    erro?: string;
  } | null>(null);

  /** Linha preta cujo menu (trocar categoria, duplicar) está aberto. */
  const [menuCategoria, setMenuCategoria] = useState<ItemPlanilha | null>(
    null,
  );

  const [lembreteLoadingId, setLembreteLoadingId] = useState<string | null>(
    null,
  );
  /** orcamentoId → ISO do último lembrete (override local após envio, sem recarregar). */
  const [lembreteInfoById, setLembreteInfoById] = useState<
    Record<string, string>
  >({});
  /** Assinatura do conteúdo no último save/carregamento bem-sucedido. */
  const savedSnapshotRef = useRef<string>("");
  // Barra de ações fixa no rodapé: medimos a altura para reservar espaço no grid
  // e evitar que ela cubra as últimas linhas ao rolar.
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(88);

  // (título da aba é gerenciado via constante de módulo TITULO_BASE)

  const [isSaving, setIsSaving] = useState(false);
  // Preenchido quando o salvamento esbarra em cotações que serão invalidadas;
  // abre o modal de confirmação e segura o salvamento até a decisão.
  const [itensAfetadosInvalidacao, setItensAfetadosInvalidacao] = useState<
    ItemAfetado[] | null
  >(null);
  // Espelha o estado acima de forma síncrona: quem chama handleSave e recebe
  // null precisa distinguir "falhou" de "está esperando o usuário confirmar",
  // e o setState só reflete no próximo render.
  const salvamentoAguardandoConfirmacaoRef = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEnviandoWrike, setIsEnviandoWrike] = useState(false);
  const [wrikeResult, setWrikeResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [planilha, setPlanilha] = useState<Planilha | null>(null);
  const [loading, setLoading] = useState(true);

  // Consulta: travar o clique nos campos não impede digitar por teclado (Tab).
  // Este bloqueio cobre digitação, apagar e colar em qualquer campo da tabela.
  // Depende de `loading` porque a grade só existe depois do carregamento.
  useEffect(() => {
    const grade = gradeRef.current;
    if (!grade || !somenteConsulta) return;
    const bloquear = (e: Event) => e.preventDefault();
    grade.addEventListener("beforeinput", bloquear, true);
    grade.addEventListener("paste", bloquear, true);
    grade.addEventListener("drop", bloquear, true);
    return () => {
      grade.removeEventListener("beforeinput", bloquear, true);
      grade.removeEventListener("paste", bloquear, true);
      grade.removeEventListener("drop", bloquear, true);
    };
  }, [somenteConsulta, loading]);
  const [error, setError] = useState<string | null>(null);
  const [rowData, setRowData] = useState<ItemPlanilha[]>([]);
  const [fornecedoresMap, setFornecedoresMap] = useState<
    Map<string, Fornecedor>
  >(new Map());
  const [modalAdicionarFornecedorAberta, setModalAdicionarFornecedorAberta] =
    useState(false);
  const [cnpjParaNovoFornecedor, setCnpjParaNovoFornecedor] = useState("");

  const [fornecedorRecemCadastrado, setFornecedorRecemCadastrado] =
    useState<any>(null);
  const [rowIdParaNovoFornecedor, setRowIdParaNovoFornecedor] = useState<
    string | null
  >(null);
  // Estados das modals
  const [modalIndividualAberta, setModalIndividualAberta] = useState(false);
  const [modalItemAberta, setModalItemAberta] = useState(false);
  const [modalFornecedorAberta, setModalFornecedorAberta] = useState(false);
  const [dadosModalAtual, setDadosModalAtual] = useState<any>(null);
  const [subgrupoModal, setSubgrupoModal] = useState<SubgrupoModalState>({
    open: false,
    targetId: "",
    nome: "",
    error: "",
  });

  // Modal de Despesas Acessórias
  const [modalDespesasAberta, setModalDespesasAberta] = useState(false);
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState<
    string[]
  >([]);

  // Breakdown Serviços Complementares (campos editáveis)
  const [breakdownEquipamentos, setBreakdownEquipamentos] =
    useState<string>("");
  const [breakdownMateriais, setBreakdownMateriais] = useState<string>("");
  const [breakdownServicos, setBreakdownServicos] = useState<string>("");
  const [breakdownPercents, setBreakdownPercents] = useState<BreakdownPercents>(
    BREAKDOWN_PERCENTS_VAZIO,
  );

  // Estados para controle de upload de arquivos (DESCRITIVO_TAXAS)
  const [arquivosAtivos, setArquivosAtivos] = useState<
    Map<
      string,
      {
        id: string;
        nomeArquivo: string;
        url: string;
        contentType: string;
        tamanhoBytes: number;
      }
    >
  >(new Map());
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());

  // Categorias de despesas disponíveis
  const categoriasDisponiveis = [
    "Aéreo",
    "Hospedagem",
    "Alimentação",
    "Transporte",
  ];

  // Toda categoria pode ser adicionada quantas vezes for necessário: um evento
  // pode ter mais de um aéreo, transporte, alimentação ou hospedagem. O nome da
  // linha é editável para diferenciar uma da outra (ex: "Aéreo ida" / "Aéreo volta").
  const categoriasParaAdicionar = categoriasDisponiveis;

  // Refs para controle do sticky header
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [headerWidths, setHeaderWidths] = useState<number[]>([]);

  // Verificar se ITEM 3 já existe
  const hasItem3 = rowData.some(
    (row) => row.tipo === TipoItem.GRUPO && row.item?.includes("ITEM 3"),
  );

  // Auto-resize de textareas após carregar dados
  useEffect(() => {
    const textareas = document.querySelectorAll("textarea");
    textareas.forEach((textarea) => {
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    });
  }, [rowData]);

  // Categorias disponíveis para subgrupos
  const categorias = [
    "ESPAÇO FÍSICO",
    "INFRAESTRUTURA DE APOIO, MOBILIÁRIO E DECORAÇÃO",
    "EQUIPAMENTOS",
    "MATERIAL DE APOIO",
    "RECURSOS HUMANOS",
    "ALIMENTOS E BEBIDAS",
    "TRANSPORTES",
    "COMUNICAÇÃO",
    "PRODUTOS E SERVIÇOS ESPECIALIZADOS",
    "TAXAS E SEGUROS",
  ];

  const openCreateSubgrupoModal = (targetId: string) => {
    setSubgrupoModal({ open: true, targetId, nome: "", error: "" });
  };

  const closeCreateSubgrupoModal = () => {
    setSubgrupoModal({ open: false, targetId: "", nome: "", error: "" });
  };

  const confirmCreateSubgrupo = () => {
    const nome = subgrupoModal.nome.trim();
    if (!nome) {
      setSubgrupoModal((prev) => ({
        ...prev,
        error: "Por favor, selecione uma categoria.",
      }));
      return;
    }

    if (!subgrupoModal.targetId) {
      closeCreateSubgrupoModal();
      showWarning(
        "Não foi possível determinar o local para inserir a categoria.",
      );
      return;
    }

    handleAddSubgrupo(subgrupoModal.targetId, nome);
    closeCreateSubgrupoModal();
    showSuccess("Categoria adicionada com sucesso!");
  };

  // Abrir modal de despesas acessórias
  const abrirModalDespesas = () => {
    setCategoriasSelecionadas([]);
    setModalDespesasAberta(true);
  };

  // Confirmar criação do ITEM 3
  const confirmarCriarItem3 = () => {
    // Validar se alguma categoria foi selecionada
    if (categoriasSelecionadas.length === 0) {
      showError("Por favor, selecione pelo menos uma categoria.");
      return;
    }

    handleAddItem3(categoriasSelecionadas);
    setModalDespesasAberta(false);
    showSuccess(
      `${categoriasSelecionadas.length} categoria(s) adicionada(s) com sucesso!`,
    );
  };

  // Adicionar ITEM 3 completo ou categorias adicionais
  const handleAddItem3 = (categorias: string[]) => {
    let newRowData = [...rowData];
    const jaExisteGrupo = rowData.some(
      (row) => row.tipo === TipoItem.GRUPO && row.item?.includes("ITEM 3"),
    );

    // Se não existe o grupo, criar estrutura básica
    if (!jaExisteGrupo) {
      // Grupo principal
      newRowData.push({
        id: crypto.randomUUID(),
        tipo: TipoItem.GRUPO,
        numero: "",
        item: "ITEM 3 - DESPESAS ACESSÓRIAS",
        descritivo: "",
        quantidade: undefined,
        diaria: undefined,
        unitario: undefined,
        subtotal: undefined,
        total: undefined,
        status: undefined,
        observacoes: "",
      });

      // Subgrupo
      newRowData.push({
        id: crypto.randomUUID(),
        tipo: TipoItem.SUBGRUPO,
        numero: "",
        item: "DESPESAS ACESSÓRIAS",
        descritivo: "",
        quantidade: undefined,
        diaria: undefined,
        unitario: undefined,
        subtotal: undefined,
        total: undefined,
        status: undefined,
        observacoes: "",
      });
    }

    // Encontrar posição de inserção (final do ITEM 3)
    const item3Index = newRowData.findIndex(
      (row) => row.tipo === TipoItem.GRUPO && row.item?.includes("ITEM 3"),
    );

    let insertIndex = newRowData.length;
    if (item3Index >= 0) {
      for (let i = item3Index + 1; i < newRowData.length; i++) {
        if (newRowData[i].tipo === TipoItem.GRUPO) {
          insertIndex = i;
          break;
        }
      }
    }

    // Adicionar as novas categorias com numeração temporária.
    // O número temporário precisa ser único por categoria do lote: renumerarItem3
    // casa cada linha branca com sua categoria pelo prefixo do número, então usar
    // o mesmo "3.TEMP" para todas faria a segunda categoria roubar as linhas da
    // primeira e descartar as demais.
    const novosItens: ItemPlanilha[] = [];
    categorias.forEach((categoria, idxCategoria) => {
      const numeroTemp = `3.TEMP${idxCategoria}`;

      // Item categoria (amarelo) - número temporário
      novosItens.push({
        id: crypto.randomUUID(),
        tipo: TipoItem.ITEM_ACESSORIA,
        numero: numeroTemp,
        item: categoria,
        descritivo: "",
        quantidade: undefined,
        diaria: undefined,
        unitario: undefined,
        subtotal: 0,
        total: 0,
        status: undefined,
        observacoes: "",
      });

      // Uma linha branca por padrão. Antes nasciam três, e na prática duas
      // ficavam vazias em quase toda categoria — despesa acessória costuma ter
      // um comprovante só. Quem precisar de mais acrescenta pelo botão da
      // linha amarela.
      for (let i = 1; i <= 1; i++) {
        novosItens.push({
          id: crypto.randomUUID(),
          tipo: TipoItem.DESCRITIVO_ACESSORIA,
          numero: `${numeroTemp}.${i}`,
          // Nasce sem nome porque o campo virou select de fornecedor: um
          // "Fornecedor 1" fixo apareceria como se alguém já tivesse escolhido.
          // Linhas de planilhas antigas mantêm o nome que têm.
          item: "",
          descritivo: "",
          quantidade: undefined,
          diaria: undefined,
          unitario: undefined,
          subtotal: undefined,
          total: undefined,
          status: undefined,
          observacoes: "",
        });
      }
    });

    // Inserir novos itens
    newRowData = [
      ...newRowData.slice(0, insertIndex),
      ...novosItens,
      ...newRowData.slice(insertIndex),
    ];

    // Renumerar TODAS as categorias do ITEM 3 para garantir consistência
    newRowData = renumerarItem3(newRowData);

    setRowData(newRowData);
  };

  // Função auxiliar para renumerar o ITEM 3 completo
  const renumerarItem3 = (data: ItemPlanilha[]): ItemPlanilha[] => {
    // Primeiro, separar ITEM 3 do resto
    const item3StartIndex = data.findIndex(
      (row) => row.tipo === TipoItem.GRUPO && row.item?.includes("ITEM 3"),
    );

    if (item3StartIndex === -1) {
      return data; // ITEM 3 não existe
    }

    // Encontrar fim do ITEM 3
    let item3EndIndex = data.length;
    for (let i = item3StartIndex + 1; i < data.length; i++) {
      if (data[i].tipo === TipoItem.GRUPO) {
        item3EndIndex = i;
        break;
      }
    }

    // Separar as seções
    const before = data.slice(0, item3StartIndex);
    const item3Section = data.slice(item3StartIndex, item3EndIndex);
    const after = data.slice(item3EndIndex);

    // Reorganizar ITEM 3: agrupar categorias com suas linhas brancas
    const reorganized: ItemPlanilha[] = [];
    const grupo = item3Section.find((r) => r.tipo === TipoItem.GRUPO);
    const subgrupo = item3Section.find((r) => r.tipo === TipoItem.SUBGRUPO);

    if (grupo) reorganized.push(grupo);
    if (subgrupo) reorganized.push(subgrupo);

    // Coletar todas as categorias amarelas
    const categorias = item3Section.filter(
      (r) => r.tipo === TipoItem.ITEM_ACESSORIA,
    );

    // Coletar todas as linhas brancas
    const linhasBrancas = item3Section.filter(
      (r) => r.tipo === TipoItem.DESCRITIVO_ACESSORIA,
    );

    // Para cada categoria, adicionar ela e suas linhas brancas
    categorias.forEach((categoria, idx) => {
      const numeroCategoria = `3.${idx + 1}`;
      const numeroCategoriaAtual = categoria.numero; // Número atual da categoria (ex: "3.2")

      // Adicionar categoria renumerada
      reorganized.push({
        ...categoria,
        numero: numeroCategoria,
      });

      // Buscar linhas brancas que pertencem a ESTA categoria (pelo número atual)
      // Ex: se categoria.numero = "3.2", buscar linhas que começam com "3.2."
      const linhasDaCategoria = linhasBrancas.filter(
        (lb) => lb.numero && lb.numero.startsWith(numeroCategoriaAtual + "."),
      );

      // Ordenar as linhas da categoria pelo terceiro número (3.2.1, 3.2.2, 3.2.3)
      linhasDaCategoria.sort((a, b) => {
        const numA = parseInt(a.numero?.split(".")[2] || "0");
        const numB = parseInt(b.numero?.split(".")[2] || "0");
        return numA - numB;
      });

      // Renumera as linhas que a categoria tem, sem completar até três.
      //
      // Antes este laço ia de 1 a 3 e criava as que faltassem. Era ele, e não
      // o botão de adicionar, que devolvia as três linhas: a categoria nascia
      // com uma e voltava a três no primeiro renumber.
      linhasDaCategoria.forEach((linha, i) => {
        reorganized.push({
          ...linha,
          numero: `${numeroCategoria}.${i + 1}`,
        });
      });

      // Categoria sem nenhuma linha não existe: é nela que vai o comprovante.
      if (linhasDaCategoria.length === 0) {
        reorganized.push({
          tipo: TipoItem.DESCRITIVO_ACESSORIA,
          numero: `${numeroCategoria}.1`,
          item: "",
          descritivo: "",
          quantidade: undefined,
          diaria: undefined,
          unitario: undefined,
          subtotal: undefined,
          total: undefined,
          status: undefined,
          observacoes: "",
        });
      }
    });

    // Retornar array completo reorganizado
    return [...before, ...reorganized, ...after];
  };

  // Remover categoria específica de Despesas Acessórias
  /**
   * Acrescenta uma linha branca de fornecedor abaixo de um item amarelo de
   * despesa acessória ou de transporte.
   *
   * A linha branca é o que recebe o anexo: cada fornecedor traz o seu
   * documento, e o item amarelo só guarda o valor.
   */
  const handleAddLinhaBranca = (itemAmareloId: string) => {
    const index = rowData.findIndex((r) => r.id === itemAmareloId);
    if (index === -1) return;

    const itemAmarelo = rowData[index];
    const tipoLinha =
      itemAmarelo.tipo === TipoItem.ITEM_TRANSPORTE
        ? TipoItem.DESCRITIVO_TRANSPORTE
        : TipoItem.DESCRITIVO_ACESSORIA;

    // Insere depois da última linha branca já existente deste item.
    let insertIndex = index + 1;
    let quantas = 0;
    for (let i = index + 1; i < rowData.length; i++) {
      if (rowData[i].tipo !== tipoLinha) break;
      quantas++;
      insertIndex = i + 1;
    }

    const nova: ItemPlanilha = {
      id: crypto.randomUUID(),
      tipo: tipoLinha,
      numero: `${itemAmarelo.numero}.${quantas + 1}`,
      // O nome vem do select de fornecedor nos três blocos, então a linha nasce
      // vazia para o campo mostrar "Pesquise por um fornecedor...". Um
      // "Fornecedor N" fixo apareceria como se já houvesse alguém escolhido.
      item: "",
      descritivo: "",
      quantidade: undefined,
      diaria: undefined,
      unitario: undefined,
      subtotal: undefined,
      total: undefined,
      status: undefined,
      observacoes: "",
    };

    setRowData((prev) => {
      const novas = [
        ...prev.slice(0, insertIndex),
        nova,
        ...prev.slice(insertIndex),
      ];
      // Transporte vive no ITEM 2 e despesa acessória no ITEM 3; cada bloco
      // tem sua própria renumeração.
      return tipoLinha === TipoItem.DESCRITIVO_TRANSPORTE
        ? renumerarItens(novas)
        : renumerarItem3(novas);
    });
  };

  /** Remove uma linha branca de fornecedor. */
  const handleRemoveLinhaBranca = async (linhaId: string) => {
    const linha = rowData.find((r) => r.id === linhaId);
    if (!linha) return;

    // Só confirma quando há o que perder: a linha com anexo leva o arquivo
    // junto, e isso não dá para desfazer.
    const temAnexo = !!(linha.id && arquivosAtivos.get(linha.id));
    if (temAnexo) {
      const confirmado = await confirm({
        title: "Remover linha",
        message: `A linha "${linha.item}" tem um arquivo anexado. Remover a linha descarta o anexo.`,
        confirmText: "Remover",
        cancelText: "Cancelar",
        tone: "danger",
      });
      if (!confirmado) return;
    }

    // Branca com valor que sai muda a soma da amarela. Se era a última com
    // valor, a amarela vai a zero em vez de guardar a soma de uma linha que
    // não existe mais. Branca vazia saindo não mexe na amarela: ela pode ser
    // de uma categoria antiga, com o valor digitado direto na amarela.
    const amarela =
      linha.tipo === TipoItem.DESCRITIVO_ACESSORIA && (linha.subtotal ?? 0) > 0
        ? rowData.find(
            (r) =>
              r.tipo === TipoItem.ITEM_ACESSORIA &&
              !!r.numero &&
              !!linha.numero?.startsWith(r.numero + "."),
          )
        : undefined;

    setRowData((prev) => {
      const semALinha = prev.filter((r) => r.id !== linhaId);
      if (linha.tipo === TipoItem.DESCRITIVO_TRANSPORTE)
        return renumerarItens(semALinha);
      const renumerado = renumerarItem3(semALinha);
      return amarela?.id
        ? somarDespesasAcessorias(renumerado, new Set([amarela.id]))
        : renumerado;
    });
  };

  const handleRemoveCategoriaDespesa = async (categoriaId: string) => {
    const categoria = rowData.find((r) => r.id === categoriaId);
    if (!categoria) return;

    const confirmed = await confirm({
      title: "Remover Categoria",
      message: `Deseja remover a categoria "${categoria.item}" e suas linhas editáveis?`,
      confirmText: "Remover",
      cancelText: "Cancelar",
      tone: "danger",
    });

    if (!confirmed) return;

    // Identificar o número da categoria (ex: "3.2")
    const numeroCategoria = categoria.numero;
    if (!numeroCategoria) return;

    // Remover categoria amarela e suas linhas brancas pelo id e número
    let newRowData = rowData.filter((row) => {
      // Remover a própria categoria
      if (row.id === categoriaId) return false;

      // Remover linhas brancas desta categoria (ex: 3.2.1, 3.2.2, 3.2.3)
      if (
        row.tipo === TipoItem.DESCRITIVO_ACESSORIA &&
        row.numero &&
        row.numero.startsWith(numeroCategoria + ".")
      ) {
        return false;
      }

      return true;
    });

    // Verificar se ainda existem categorias
    const aindaTemCategorias = newRowData.some(
      (row) =>
        row.tipo === TipoItem.ITEM_ACESSORIA &&
        row.numero &&
        /^3\.\d+$/.test(row.numero),
    );

    // Se não tem mais categorias, remover ITEM 3 completo
    if (!aindaTemCategorias) {
      const item3Index = newRowData.findIndex(
        (row) => row.tipo === TipoItem.GRUPO && row.item?.includes("ITEM 3"),
      );

      if (item3Index >= 0) {
        const idsToRemove = new Set<string>();
        if (newRowData[item3Index].id)
          idsToRemove.add(newRowData[item3Index].id!);
        for (let i = item3Index + 1; i < newRowData.length; i++) {
          if (newRowData[i].tipo === TipoItem.GRUPO) break;
          if (newRowData[i].id) idsToRemove.add(newRowData[i].id!);
        }
        newRowData = newRowData.filter((r) => !idsToRemove.has(r.id!));
      }
    } else {
      // Renumerar TODAS as categorias e linhas brancas do ITEM 3
      newRowData = renumerarItem3(newRowData);
    }

    setRowData(newRowData);
    showInfo(`Categoria "${categoria.item}" removida.`);
  };

  /**
   * IDs que existem de fato no banco, colhidos do payload do servidor.
   *
   * Linhas criadas na tela recebem um `crypto.randomUUID()` só para servir de
   * key no React — esse id NÃO existe no backend. Como o save identifica os
   * itens por `itemId`, mandar um id inventado faria o backend não encontrar o
   * registro, cair no fallback por número e sobrescrever o item que ocupa
   * aquela posição. Só enviamos `itemId` para linhas que estão neste conjunto.
   */
  const idsDoServidorRef = useRef<Set<string>>(new Set());

  /**
   * Vínculos de fornecedor que o usuário removeu e ainda não foram salvos.
   *
   * O backend deixou de tratar "linha ausente do payload" como ordem de
   * apagar — bastava a tela não desenhar uma linha para o salvamento destruir
   * o valor cotado e o elo com o PDF. A remoção agora é declarada, e é aqui
   * que ela fica até o salvamento levar.
   *
   * Só as remoções de linha de fornecedor entram. Remover item, subgrupo ou
   * categoria apaga o próprio item no banco, e os vínculos vão junto por
   * cascata.
   */
  const fornecedoresRemovidosRef = useRef<Set<string>>(new Set());

  const registrarIdsDoServidor = useCallback((itens?: ItemPlanilha[]) => {
    const ids = new Set<string>();
    (itens || []).forEach((item: any) => {
      if (item?.id) ids.add(item.id);
      (item?.fornecedores || []).forEach((f: any) => {
        if (f?.id) ids.add(f.id);
      });
    });
    idsDoServidorRef.current = ids;
  }, []);

  /**
   * Devolve o id só quando ele veio do servidor. Linha criada na sessão carrega
   * um UUID gerado no cliente (ver o crypto.randomUUID em loadData e
   * handleSave), que existe apenas na tela — quem manda esse UUID para a API
   * recebe "não encontrado".
   */
  const idDoBanco = useCallback(
    (rowId?: string) =>
      rowId && idsDoServidorRef.current.has(rowId) ? rowId : undefined,
    [],
  );

  const loadData = useCallback(async () => {
    if (!id) {
      setError("ID da planilha não fornecido");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [planilhaData] = await Promise.all([planilhaService.getById(id)]);

      setPlanilha(planilhaData || null);

      // Carregar valores de breakdown se existirem
      if (planilhaData?.breakdown?.equipamentos)
        setBreakdownEquipamentos(String(planilhaData.breakdown.equipamentos));
      if (planilhaData?.breakdown?.materiais)
        setBreakdownMateriais(String(planilhaData.breakdown.materiais));
      if (planilhaData?.breakdown?.servicos)
        setBreakdownServicos(String(planilhaData.breakdown.servicos));

      // Percentual vem nulo em planilha gravada antes do campo existir: ali o
      // rateio segue sendo o valor absoluto que já estava lá.
      setBreakdownPercents({
        equipamentos:
          planilhaData?.breakdown?.percentEquipamentos != null
            ? String(planilhaData.breakdown.percentEquipamentos)
            : "",
        materiais:
          planilhaData?.breakdown?.percentMateriais != null
            ? String(planilhaData.breakdown.percentMateriais)
            : "",
        servicos:
          planilhaData?.breakdown?.percentServicos != null
            ? String(planilhaData.breakdown.percentServicos)
            : "",
      });

      // Se já tem itens salvos, carregar; senão, usar dados de exemplo
      if (planilhaData && planilhaData.itens && planilhaData.itens.length > 0) {
        // Criar mapa de fornecedores a partir dos dados que já vêm na resposta
        const fornMap = new Map<string, Fornecedor>();
        planilhaData.itens.forEach((item) => {
          if (
            (item.tipo === TipoItem.ITEM ||
              item.tipo === TipoItem.ITEM_TAXAS) &&
            Array.isArray(item.fornecedores)
          ) {
            item.fornecedores.forEach((f) => {
              // Nova estrutura: f.fornecedor contém os dados detalhados
              if (f.fornecedorId && f.fornecedor) {
                // Criar objeto fornecedor a partir dos dados completos
                fornMap.set(f.fornecedorId, {
                  id: f.fornecedor.id,
                  nome: f.fornecedor.nome,
                  email: f.fornecedor.email || "",
                  telefone: f.fornecedor.telefone || "",
                  cnpj: f.fornecedor.cnpj || "",
                  inscricaoEstadual: f.fornecedor.inscricaoEstadual || "",
                  endereco: f.fornecedor.endereco || "",
                  cidade: f.fornecedor.cidade || "",
                  estado: f.fornecedor.estado || "",
                  cep: f.fornecedor.cep || "",
                  // Outros campos podem ser preenchidos conforme necessário
                } as Fornecedor);
              }
            });
          }
        });
        setFornecedoresMap(fornMap);

        registrarIdsDoServidor(planilhaData.itens);

        // 1) Remover linhas legadas de fornecedores já salvas na planilha (evita duplicação na UI)
        const itensSemLegado = removerLinhasFornecedorLegadas(
          planilhaData.itens,
        );

        // 2) Expandir itens com linhas de fornecedores vindos do backend (status, unitário, etc.)
        const itensExpand = expandItensComFornecedores(itensSemLegado, fornMap);
        // Corrigir números dos fornecedores se estiverem faltando
        const itensCorrigidos = corrigirNumerosFornecedores(itensExpand);
        // Reorganizar ITEM 3 se existir (garantir que linhas brancas estejam após suas categorias)
        const itensReorganizados = renumerarItem3(itensCorrigidos);
        // Renumerar itens do ITEM 2 com formato de dois dígitos (2.01, 2.02...)
        const itensRenumerados = renumerarItens(itensReorganizados);
        // Recalcular totais e menor preço
        const itensRecalculados = recalcularTotaisEMenorPreco(itensRenumerados);

        // Garantir que todos os itens tenham ID
        const itensComIds = itensRecalculados.map((item) =>
          item.id ? item : { ...item, id: crypto.randomUUID() },
        );

        setRowData(itensComIds);
        // Baseline para detecção de "alterações não salvas" (item 1.4)
        savedSnapshotRef.current = buildEditorSignature(
          itensComIds,
          planilhaData?.breakdown?.equipamentos
            ? String(planilhaData.breakdown.equipamentos)
            : "",
          planilhaData?.breakdown?.materiais
            ? String(planilhaData.breakdown.materiais)
            : "",
          planilhaData?.breakdown?.servicos
            ? String(planilhaData.breakdown.servicos)
            : "",
          {
            equipamentos:
              planilhaData?.breakdown?.percentEquipamentos != null
                ? String(planilhaData.breakdown.percentEquipamentos)
                : "",
            materiais:
              planilhaData?.breakdown?.percentMateriais != null
                ? String(planilhaData.breakdown.percentMateriais)
                : "",
            servicos:
              planilhaData?.breakdown?.percentServicos != null
                ? String(planilhaData.breakdown.percentServicos)
                : "",
          },
        );
      } else {
        const sample = getSampleData();
        setRowData(sample);
        savedSnapshotRef.current = buildEditorSignature(
          sample,
          "",
          "",
          "",
          BREAKDOWN_PERCENTS_VAZIO,
        );
      }

      setError(null);
    } catch (err) {
      setError("Erro ao carregar planilha");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Carregar os anexos de todas as linhas que aceitam arquivo: o item amarelo
  // de TAXAS, e as linhas brancas de TRANSPORTES e DESPESAS ACESSÓRIAS, onde o
  // documento é de cada fornecedor e não do item inteiro.
  useEffect(() => {
    const carregarArquivosAtivos = async () => {
      const itensDescritivoTaxas = rowData.filter(
        (item) =>
          (item.tipo === TipoItem.ITEM_TAXAS ||
            TIPOS_LINHA_COM_ANEXO.includes(item.tipo)) &&
          item.id,
      );

      // Não recarregar se não houver itens com ID
      if (itensDescritivoTaxas.length === 0) return;

      const novosArquivos = new Map<string, any>();

      for (const item of itensDescritivoTaxas) {
        if (item.id) {
          // Não recarregar se já temos o arquivo
          if (arquivosAtivos.has(item.id)) {
            novosArquivos.set(item.id, arquivosAtivos.get(item.id)!);
            continue;
          }

          try {
            const response = await apiClient.get(`/itens/${item.id}/arquivo`);
            if (response.data.success && response.data.data) {
              novosArquivos.set(item.id, response.data.data);
            }
          } catch (error: any) {
            // 404 é esperado quando não há arquivo, então não logar como erro
            if (error.response?.status !== 404) {
              console.error(
                `Erro ao carregar arquivo do item ${item.id}:`,
                error,
              );
            }
          }
        }
      }

      // Só atualizar se houver mudanças
      if (novosArquivos.size > 0) {
        setArquivosAtivos(novosArquivos);
      }
    };

    if (rowData.length > 0) {
      carregarArquivosAtivos();
    }
  }, [rowData.length]); // Evita loop infinito usando apenas o length

  // Monitorar scroll e calcular larguras das colunas para sticky header
  useEffect(() => {
    const handleScroll = () => {
      if (theadRef.current) {
        const rect = theadRef.current.getBoundingClientRect();
        // Mostrar sticky header quando o thead original sair da visualização
        setShowStickyHeader(rect.bottom < 0);
      }
    };

    const calculateWidths = () => {
      if (theadRef.current) {
        const ths = theadRef.current.querySelectorAll("th");
        const widths = Array.from(ths).map((th) => th.offsetWidth);
        setHeaderWidths(widths);
      }
    };

    // Calcular larguras iniciais
    calculateWidths();

    // Adicionar listener de scroll
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", calculateWidths);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", calculateWidths);
    };
  }, [rowData]); // Recalcular quando rowData mudar

  // Aplicar fornecedor recém-cadastrado
  useEffect(() => {
    if (fornecedorRecemCadastrado && rowIdParaNovoFornecedor) {
      // Extrair CNAE principal do array de CNAEs
      const cnaePrincipal =
        fornecedorRecemCadastrado.cnaes?.find((c: any) => c.principal)?.cnae
          ?.codigo ||
        fornecedorRecemCadastrado.cnaePrincipal ||
        "";

      // Criar objeto fornecedor completo com dados da API
      const fornecedorCompleto = {
        ...fornecedorRecemCadastrado,
        cnaePrincipal, // Adicionar campo legado para compatibilidade
      };

      // Adicionar fornecedor ao mapa
      setFornecedoresMap((prevMap) => {
        const newMap = new Map(prevMap);
        newMap.set(fornecedorRecemCadastrado.id, fornecedorCompleto);
        return newMap;
      });

      const nomeExibicao =
        fornecedorRecemCadastrado.nome ||
        fornecedorRecemCadastrado.razaoSocial ||
        "Sem nome";
      // Atualizar rowData PRIMEIRO (com imutabilidade completa)
      setRowData((prevData) => {
        const newData = [...prevData];
        const targetIndex = newData.findIndex(
          (r) => r.id === rowIdParaNovoFornecedor,
        );
        if (targetIndex === -1) return prevData;
        const rowAtual = newData[targetIndex];

        // Criar objeto completamente novo para forçar re-render
        newData[targetIndex] = {
          ...rowAtual,
          item: nomeExibicao,
          fornecedorId: fornecedorRecemCadastrado.id,
          fornecedor: fornecedorCompleto,
        };

        return newData;
      });

      // Dados já foram atualizados no estado, não precisa atualizar grid

      // Limpar estados
      setFornecedorRecemCadastrado(null);
      setRowIdParaNovoFornecedor(null);
    }
  }, [fornecedorRecemCadastrado, rowIdParaNovoFornecedor]);

  // Ref para armazenar o timeout do debounce
  const debounceTimeoutRef = {
    current: null as ReturnType<typeof setTimeout> | null,
  };

  // Função de busca de fornecedores (sem debounce)
  const searchFornecedores = useCallback(async (inputValue: string) => {
    const fornecedoresResponse = await fornecedorService.getAll({
      pageSize: 1000,
      search: inputValue,
    });
    return fornecedoresResponse.items.map((fornecedor) => {
      const razaoSocial = fornecedor.razaoSocial || "";
      const nomeFantasia = fornecedor.nome || "";
      const label =
        razaoSocial && nomeFantasia && razaoSocial !== nomeFantasia
          ? `${razaoSocial} / ${nomeFantasia}`
          : razaoSocial || nomeFantasia || "Sem nome";
      return {
        value: fornecedor.id,
        label,
        data: fornecedor,
      };
    });
  }, []);

  // Função com debounce de 500ms
  const loadFornecedoresOptions = useCallback(
    (inputValue: string, callback: (options: any[]) => void) => {
      // Limpar timeout anterior se existir
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Criar novo timeout
      debounceTimeoutRef.current = setTimeout(async () => {
        const result = await searchFornecedores(inputValue);
        callback(result);
      }, 500);
    },
    [searchFornecedores],
  );

  // Limpar timeout ao desmontar o componente
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // ============= REFRESH DE RESPOSTAS DE FORNECEDORES =============

  // Timestamp do último refresh para throttle
  const lastRefreshRef = useRef<number>(0);

  // O fornecedor finaliza a cotação em duas chamadas: primeiro o status vira
  // "respondida", só depois o PDF é enviado ao blob. Se o refresh cair nessa
  // janela, a linha mostra "Recebida" sem botão de download. Estes refs agendam
  // uma nova busca automática até o PDF aparecer, sem exigir F5.
  const pdfPendenteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pdfPendenteTentativasRef = useRef<number>(0);
  const PDF_PENDENTE_MAX_TENTATIVAS = 6;
  const PDF_PENDENTE_INTERVALO_MS = 8_000;

  // Referência sempre atualizada para o refresh, usada pelo reagendamento
  // (evita a dependência circular de um useCallback que se chama a si mesmo).
  const refreshFornecedoresRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    return () => {
      if (pdfPendenteTimeoutRef.current)
        clearTimeout(pdfPendenteTimeoutRef.current);
    };
  }, []);

  // Busca dados frescos do backend e atualiza APENAS as linhas de fornecedor
  // (sem tocar nas linhas de ITEM/GRUPO/SUBGRUPO editadas pelo usuário)
  const refreshFornecedores = useCallback(async () => {
    if (!id || isRefreshing) return;

    // Throttle: no mínimo 30s entre refreshes automáticos
    const agora = Date.now();
    if (agora - lastRefreshRef.current < 30_000) return;
    lastRefreshRef.current = agora;

    setIsRefreshing(true);
    try {
      const planilhaFresca = await planilhaService.getById(id);
      if (!planilhaFresca?.itens) return;

      // Montar mapa: ItemFornecedor.id -> dados frescos
      type DadosFornecedor = {
        unitario?: number;
        status?: string;
        observacoes?: string;
        orcamentoPdfUrl?: string;
        orcamentoPdfRejeicaoUrl?: string;
        solicitadoPorNome?: string;
        solicitadoPorEmail?: string;
        solicitadoPorId?: string;
        menorPreco?: boolean;
        // Sem trazer este campo, a linha recém-finalizada voltaria do refresh
        // sem data de resposta e seria tratada como abandonada: sairia do
        // menor preço e ganharia o aviso de "preencheu e não finalizou".
        dataResposta?: string | null;
      };
      const mapaFresco = new Map<string, DadosFornecedor>();

      planilhaFresca.itens.forEach((it) => {
        if (
          (it.tipo === TipoItem.ITEM || it.tipo === TipoItem.ITEM_TAXAS) &&
          Array.isArray(it.fornecedores)
        ) {
          it.fornecedores.forEach((f: any) => {
            if (f.id) {
              mapaFresco.set(f.id, {
                unitario: f.unitario,
                status: f.status,
                observacoes: f.observacoes,
                // Extrair URL do PDF do orçamento, se disponível
                orcamentoPdfUrl: f.orcamento?.pdfUrl || f.orcamentoPdfUrl,
                orcamentoPdfRejeicaoUrl:
                  f.orcamento?.pdfRejeicaoUrl || f.orcamentoPdfRejeicaoUrl,
                solicitadoPorNome: f.solicitadoPorNome,
                solicitadoPorEmail: f.solicitadoPorEmail,
                solicitadoPorId: f.solicitadoPorId,
                dataResposta: f.dataResposta ?? null,
                menorPreco:
                  f.unitario != null &&
                  it.unitario != null &&
                  f.unitario === it.unitario,
              });
            }
          });
        }
      });

      if (mapaFresco.size === 0) return;

      let novasRespostas = 0;

      // Alguma cotação já respondida (e não rejeitada) ainda está sem o PDF?
      // Nesse caso o upload do documento ainda está a caminho.
      const aguardandoPdf = Array.from(mapaFresco.values()).some(
        (f) =>
          f.status === "respondida" &&
          !f.orcamentoPdfUrl &&
          !f.orcamentoPdfRejeicaoUrl,
      );

      setRowData((prev) => {
        const patched = prev.map((row) => {
          if (row.tipo !== TipoItem.FORNECEDOR || !row.id) return row;

          const fresh = mapaFresco.get(row.id);
          if (!fresh) return row;

          const statusMudou = fresh.status !== row.status;
          const unitarioMudou = fresh.unitario !== row.unitario;
          const observacoesMudou = fresh.observacoes !== row.observacoes;
          const pdfMudou = fresh.orcamentoPdfUrl !== row.orcamentoPdfUrl;
          const pdfRejeicaoMudou =
            fresh.orcamentoPdfRejeicaoUrl !== row.orcamentoPdfRejeicaoUrl;
          const menorPrecoMudou = fresh.menorPreco !== row.menorPreco;
          const dataRespostaMudou =
            (fresh.dataResposta ?? null) !== (row.dataResposta ?? null);

          if (
            !statusMudou &&
            !unitarioMudou &&
            !observacoesMudou &&
            !pdfMudou &&
            !pdfRejeicaoMudou &&
            !menorPrecoMudou &&
            !dataRespostaMudou
          )
            return row;

          // Contar novas respostas (quando o status passou a ser "respondida")
          if (
            statusMudou &&
            fresh.status === "respondida" &&
            row.status !== "respondida"
          ) {
            novasRespostas++;
          }

          return {
            ...row,
            unitario: fresh.unitario,
            status: fresh.status as any,
            observacoes: fresh.observacoes,
            orcamentoPdfUrl: fresh.orcamentoPdfUrl,
            orcamentoPdfRejeicaoUrl: fresh.orcamentoPdfRejeicaoUrl,
            solicitadoPorNome: fresh.solicitadoPorNome,
            solicitadoPorEmail: fresh.solicitadoPorEmail,
            solicitadoPorId: fresh.solicitadoPorId,
            dataResposta: fresh.dataResposta,
            menorPreco: fresh.menorPreco,
          };
        });

        // Reordenar fornecedores por preço e recalcular totais/menor preço —
        // mesma cadeia do carregamento (F5), para a ordem e os totais ficarem
        // corretos sem o usuário precisar recarregar a página.
        const reordenado = reordenarFornecedoresPorPreco(patched);
        const corrigido = corrigirNumerosFornecedores(reordenado);
        const item3 = renumerarItem3(corrigido);
        const renumerado = renumerarItens(item3);
        return recalcularTotaisEMenorPreco(renumerado);
      });

      if (novasRespostas > 0) {
        showInfo(
          `${novasRespostas} nova${novasRespostas > 1 ? "s" : ""} resposta${novasRespostas > 1 ? "s" : ""} de fornecedor recebida${novasRespostas > 1 ? "s" : ""}.`,
        );
      }

      // Reagenda enquanto houver cotação respondida sem documento. Sem isso o
      // botão de download só aparecia no próximo F5.
      if (pdfPendenteTimeoutRef.current) {
        clearTimeout(pdfPendenteTimeoutRef.current);
        pdfPendenteTimeoutRef.current = null;
      }

      if (!aguardandoPdf) {
        pdfPendenteTentativasRef.current = 0;
      } else if (
        pdfPendenteTentativasRef.current < PDF_PENDENTE_MAX_TENTATIVAS
      ) {
        pdfPendenteTentativasRef.current++;
        pdfPendenteTimeoutRef.current = setTimeout(() => {
          lastRefreshRef.current = 0; // ignora o throttle de 30s nesta rodada
          refreshFornecedoresRef.current?.();
        }, PDF_PENDENTE_INTERVALO_MS);
      }
    } catch {
      // Silenciar erros de refresh (operação secundária)
    } finally {
      setIsRefreshing(false);
    }
  }, [id, isRefreshing, showInfo]);

  useEffect(() => {
    refreshFornecedoresRef.current = refreshFornecedores;
  }, [refreshFornecedores]);

  // Throttle para refresh manual (sem limite de 30s)
  const refreshFornecedoresManual = useCallback(async () => {
    if (!id || isRefreshing) return;
    pdfPendenteTentativasRef.current = 0; // libera novas rodadas de espera do PDF
    lastRefreshRef.current = 0; // zera throttle para forçar execução
    await refreshFornecedores();
  }, [id, isRefreshing, refreshFornecedores]);

  // Cotação editada pela equipe em outra aba: atualiza as linhas de fornecedor
  // na hora, sem esperar o intervalo de 30s nem o clique em Atualizar
  // Respostas. É o mesmo refresh de sempre, que casa cada linha pelo id do
  // vínculo e não toca no que o usuário está editando na planilha.
  useEffect(
    () =>
      ouvirCotacaoEditada((aviso) => {
        if (aviso.planilhaId !== id) return;
        lastRefreshRef.current = 0;
        pdfPendenteTentativasRef.current = 0;
        refreshFornecedoresRef.current?.();
        showInfo("Cotação editada: valores e PDF atualizados na planilha.");
      }),
    [id, showInfo],
  );

  // Atualizar respostas automaticamente quando o usuário retorna à aba
  useEffect(() => {
    const handleFocus = () => {
      refreshFornecedores();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refreshFornecedores]);

  // ============= FUNÇÕES DE GERENCIAMENTO DE ARQUIVOS (DESCRITIVO_TAXAS) =============

  // Fazer upload de arquivo para a linha amarela de TAXAS (item_taxas)
  const handleUploadArquivo = async (data: ItemPlanilha, file: File) => {
    // Criar identificador único baseado em tipo e número (mais confiável que ID)
    const uploadKey = `${data.tipo}_${data.numero}`;

    // Validar antes de subir: erro local é imediato e explica o motivo, em vez
    // de esperar a viagem até o servidor para receber uma recusa genérica.
    const extensao = file.name.includes(".")
      ? `.${file.name.split(".").pop()!.toLowerCase()}`
      : "";

    if (!EXTENSOES_ANEXO_PERMITIDAS.includes(extensao)) {
      showError(
        `"${file.name}" não é um formato aceito. Envie um destes: ${EXTENSOES_ANEXO_PERMITIDAS.join(", ")}.`,
      );
      return;
    }

    if (file.size === 0) {
      showError(`"${file.name}" está vazio (0 bytes). Verifique o arquivo.`);
      return;
    }

    if (file.size > TAMANHO_MAXIMO_ANEXO_BYTES) {
      const tamanhoMb = (file.size / 1024 / 1024).toFixed(1);
      showError(
        `"${file.name}" tem ${tamanhoMb} MB e o limite é ${
          TAMANHO_MAXIMO_ANEXO_BYTES / 1024 / 1024
        } MB. Comprima o arquivo e tente de novo.`,
      );
      return;
    }

    try {
      // Não basta ter id: toda linha tem um, e o de linha nova é gerado no
      // cliente. Mandá-lo para a API dava "Item não encontrado" — era esse o
      // erro intermitente ao anexar, que só aparecia em linha ainda não salva.
      let itemIdFinal = idDoBanco(data.id);

      // Adicionar ao loading usando a chave única
      setUploadingFiles((prev) => new Set(prev).add(uploadKey));

      // Sem id do banco, salvar a planilha primeiro (igual ao "Solicitar Cotação")
      if (!itemIdFinal) {
        showInfo("Salvando planilha antes do upload...");
        const planilhaAtualizada = await handleSave();

        if (!planilhaAtualizada) {
          // O salvamento pode ter parado no modal de invalidação de cotações.
          // Nesse caso não houve erro — falta uma decisão do usuário.
          showError(
            salvamentoAguardandoConfirmacaoRef.current
              ? "Esta linha ainda não foi salva. Resolva a confirmação de alterações que apareceu e anexe o arquivo depois."
              : "Não foi possível salvar a planilha antes de anexar. Salve manualmente e tente de novo.",
          );
          setUploadingFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uploadKey);
            return newSet;
          });
          return;
        }

        // Encontrar o item atualizado com ID na planilha salva
        // Usar tipo e número para encontrar (mais confiável que índice)
        // Para DESCRITIVO_TAXAS, precisamos buscar dentro do item pai
        const itemSalvo = planilhaAtualizada.itens.find(
          (i) => i.tipo === data.tipo && i.numero === data.numero,
        );

        // Veio da resposta do servidor, então é id de banco por definição —
        // handleSave já registrou o conjunto novo em idsDoServidorRef.
        if (itemSalvo?.id) {
          itemIdFinal = itemSalvo.id;
          // Atualizar o rowData com o ID obtido para próximas operações
          // Usar tipo+numero para identificar unicamente a linha
          setRowData((prevData) => {
            return prevData.map((r) => {
              // Comparar por tipo E numero para garantir que é a linha correta
              if (r.tipo === data.tipo && r.numero === data.numero) {
                return { ...r, id: itemIdFinal };
              }
              return r;
            });
          });
        }

        if (!itemIdFinal) {
          showError(
            "Não foi possível obter o ID do item. Salve a planilha manualmente.",
          );
          setUploadingFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uploadKey);
            return newSet;
          });
          return;
        }
      }

      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post(
        `/itens/${itemIdFinal}/arquivo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        // Atualizar estado com o novo arquivo
        setArquivosAtivos((prev) => {
          const newMap = new Map(prev);
          newMap.set(itemIdFinal!, response.data.data);
          return newMap;
        });

        showSuccess(
          response.data.data.arquivoSubstituido
            ? "Arquivo substituído com sucesso!"
            : "Arquivo enviado com sucesso!",
        );
      }
    } catch (error: any) {
      console.error("Erro ao fazer upload:", error);

      // O servidor explica o motivo (formato, tamanho, tipo de linha). Só cai
      // no texto genérico quando não houve resposta — tipicamente rede caída.
      const mensagemServidor = error.response?.data?.message;
      const status = error.response?.status;

      if (mensagemServidor) {
        showError(mensagemServidor);
      } else if (!error.response) {
        showError(
          "Sem resposta do servidor ao enviar o arquivo. Verifique sua conexão e tente de novo.",
        );
      } else if (status === 413) {
        showError(
          `O arquivo é grande demais para o servidor aceitar. Limite: ${
            TAMANHO_MAXIMO_ANEXO_BYTES / 1024 / 1024
          } MB.`,
        );
      } else {
        showError(
          `Não foi possível anexar "${file.name}" (erro ${status}). Tente de novo em instantes.`,
        );
      }
    } finally {
      // Remover do loading usando a chave única
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(uploadKey);
        return newSet;
      });
    }
  };

  // Remover arquivo ativo de um item
  const handleRemoverArquivo = async (itemId: string) => {
    const confirmed = await confirm({
      title: "Remover Arquivo",
      message:
        "Deseja remover o arquivo anexado? Ele será mantido no histórico.",
      confirmText: "Remover",
      cancelText: "Cancelar",
      tone: "danger",
    });

    if (!confirmed) return;

    try {
      const response = await apiClient.delete(`/itens/${itemId}/arquivo`);

      if (response.data.success) {
        // Remover do estado
        setArquivosAtivos((prev) => {
          const newMap = new Map(prev);
          newMap.delete(itemId);
          return newMap;
        });

        showInfo("Arquivo removido com sucesso");
      }
    } catch (error: any) {
      console.error("Erro ao remover arquivo:", error);
      showError(error.response?.data?.message || "Erro ao remover arquivo");
    }
  };

  // Formatar tamanho de arquivo para exibição
  // const formatarTamanhoArquivo = (bytes: number): string => {
  //   if (bytes < 1024) return bytes + " B";
  //   if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  //   return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  // };

  const getSampleData = (): ItemPlanilha[] => [
    // ITEM 1 - FIXO (Padrão Petrobras)
    {
      id: crypto.randomUUID(),
      tipo: TipoItem.GRUPO,
      numero: "",
      item: "ITEM 1 - PRODUTOS E SERVIÇOS ESSENCIAIS",
      descritivo: "",
      quantidade: undefined,
      diaria: undefined,
      unitario: undefined,
      subtotal: undefined,
      total: undefined,
      status: undefined,
      observacoes: "",
    },
    {
      id: crypto.randomUUID(),
      tipo: TipoItem.SUBGRUPO,
      numero: "",
      item: "PRODUTOS E SERVIÇOS ESSENCIAIS",
      descritivo: "",
      quantidade: undefined,
      diaria: undefined,
      unitario: undefined,
      subtotal: undefined,
      total: undefined,
      status: undefined,
      observacoes: "",
    },
    {
      id: crypto.randomUUID(),
      tipo: TipoItem.ITEM,
      numero: "1.1",
      item: "Viabilização Legal da Ação - X Complexidade",
      descritivo:
        "Levantamento de todos os documentos e processos necessários para a aprovação da ação junto às autoridades locais, responsáveis pelos espaços que serão utilizados, e viabilização efetiva junto a estas autoridades",
      quantidade: undefined,
      diaria: undefined,
      unitario: 0,
      subtotal: undefined,
      total: 0,
      status: undefined,
      observacoes: "100% DE DESCONTO",
    },
    {
      id: crypto.randomUUID(),
      tipo: TipoItem.ITEM,
      numero: "1.2",
      item: "Monitoramento e medição dos resultados da ação - X Complexidade",
      descritivo:
        "Análise qualitativa e quantitativa do impacto e da aceitação da ação no período de sua implementação e nos ambientes em que esta será realizada.",
      quantidade: undefined,
      diaria: undefined,
      unitario: 0,
      subtotal: undefined,
      total: 0,
      status: undefined,
      observacoes: "100% DE DESCONTO",
    },

    // ITEM 2 - EDITÁVEL (vazio para o usuário preencher)
    {
      id: crypto.randomUUID(),
      tipo: TipoItem.GRUPO,
      numero: "",
      item: "ITEM 2 - PRODUTOS E SERVIÇOS COMPLEMENTARES",
      descritivo: "",
      quantidade: undefined,
      diaria: undefined,
      unitario: undefined,
      subtotal: undefined,
      total: undefined,
      status: undefined,
      observacoes: "",
    },
  ];

  // Expande cada item com as linhas de fornecedores retornadas pelo backend (planilha.itens[].fornecedores[])
  // para que o grid exiba uma linha por fornecedor logo abaixo do ITEM correspondente.
  const expandItensComFornecedores = (
    itensOriginais: ItemPlanilha[],
    fornecedoresCompletos?: Map<string, Fornecedor>,
  ): ItemPlanilha[] => {
    const resultado: ItemPlanilha[] = [];
    for (const it of itensOriginais) {
      // Adiciona a própria linha do item/grupo/subgrupo
      resultado.push(it);
      // ITEM_TRANSPORTE entra aqui embora o fluxo novo não use fornecedor:
      // planilha convertida antes desta correção tem vínculo gravado, e não
      // desenhar a linha é o que fazia o salvamento seguinte apagá-lo. Melhor
      // mostrar do que perder.
      if (
        (it.tipo === TipoItem.ITEM ||
          it.tipo === TipoItem.ITEM_TAXAS ||
          it.tipo === TipoItem.ITEM_TRANSPORTE) &&
        Array.isArray(it.fornecedores) &&
        it.fornecedores.length > 0
      ) {
        // Ordenar fornecedores pelo valor unitário (menor primeiro, zeros por último)
        const fornecedoresOrdenados = [...it.fornecedores].sort((a, b) => {
          const unitarioA = a.unitario ?? Infinity;
          const unitarioB = b.unitario ?? Infinity;

          // Se ambos são zero, manter ordem original
          if (unitarioA === 0 && unitarioB === 0) return 0;
          // Se A é zero, colocar por último
          if (unitarioA === 0) return 1;
          // Se B é zero, colocar por último
          if (unitarioB === 0) return -1;
          // Caso contrário, ordenar do menor para o maior
          return unitarioA - unitarioB;
        });
        // Dedupe por fornecedorId para evitar duplicação se houver dados repetidos
        const seen = new Set<string>();
        fornecedoresOrdenados.forEach((f, idx) => {
          const key = `${f.fornecedorId}-${f.id}`;
          if (seen.has(key)) return;
          seen.add(key);

          // Buscar fornecedor completo do mapa
          let nomeFornecedor =
            f.fornecedor?.nome || f.fornecedorNome || "Sem nome";

          if (fornecedoresCompletos && f.fornecedorId) {
            const fornecedorCompleto = fornecedoresCompletos.get(
              f.fornecedorId,
            );
            if (fornecedorCompleto) {
              nomeFornecedor =
                fornecedorCompleto.nome ||
                fornecedorCompleto.razaoSocial ||
                "Sem nome";
            }
          }

          resultado.push({
            id: f.id, // id da ligação item_fornecedor (para rastreamento)
            tipo: TipoItem.FORNECEDOR,
            numero: it.numero ? `${it.numero}.${idx + 1}` : undefined,
            item: nomeFornecedor,
            descritivo: undefined,
            quantidade: undefined,
            diaria: undefined,
            unitario: f.unitario,
            subtotal: undefined,
            total: undefined,
            status: f.status as any,
            observacoes: f.observacoes,
            fornecedorId: f.fornecedorId,
            // Extrair URL do PDF do orçamento, se disponível
            orcamentoPdfUrl: f.orcamento?.pdfUrl || f.orcamentoPdfUrl,
            orcamentoPdfRejeicaoUrl:
              f.orcamento?.pdfRejeicaoUrl || f.orcamentoPdfRejeicaoUrl,
            // Lembrete ao fornecedor (item 3.1) — getById devolve os campos "achatados";
            // fornecedores-detalhe devolve aninhado em `orcamento`.
            orcamentoId: f.orcamento?.id || f.orcamentoId,
            ultimoLembreteEm:
              f.orcamento?.ultimoLembreteEm || f.ultimoLembreteEm,
            // Visto por último (item 3.2)
            ultimoAcessoEm: f.orcamento?.ultimoAcessoEm || f.ultimoAcessoEm,
            totalAcessos: f.orcamento?.totalAcessos ?? f.totalAcessos,
            // Só é preenchida na finalização: é o que distingue proposta
            // entregue de preenchimento abandonado no meio.
            dataResposta: f.dataResposta ?? null,
            // Adicionar informações de quem solicitou
            solicitadoPorNome: f.solicitadoPorNome,
            solicitadoPorEmail: f.solicitadoPorEmail,
            solicitadoPorId: f.solicitadoPorId,
            menorPreco:
              f.unitario != null &&
              it.unitario != null &&
              f.unitario === it.unitario,
          });
        });
      }
    }
    return resultado;
  };

  // Remove linhas antigas (tipo FORNECEDOR) já persistidas na planilha para evitar duplicação
  const removerLinhasFornecedorLegadas = (
    itensOriginais: ItemPlanilha[],
  ): ItemPlanilha[] => {
    return (itensOriginais || []).filter((i) => i.tipo !== TipoItem.FORNECEDOR);
  };

  // Formatador de moeda
  const currencyFormatter = (params: any) => {
    if (params.value == null) return "";
    if (typeof params.value === "string") return params.value;
    return (
      "R$ " +
      parseFloat(params.value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  // Cell Renderer para botão de ação na primeira coluna
  const ActionButtonRenderer = (props: any) => {
    const { data } = props;

    // Adicionar, remover e duplicar linhas: nada disso é para quem só consulta.
    if (somenteConsulta) return null;

    // ITEM 1 - Não mostra nenhum botão (totalmente fixo)
    if (data.tipo === TipoItem.GRUPO && data.item.includes("ITEM 1")) {
      return null;
    }

    // Itens do ITEM 1 (1.1, 1.2) - Não mostra botão
    if (
      (data.tipo === TipoItem.ITEM || data.tipo === TipoItem.ITEM_TAXAS) &&
      data.numero &&
      data.numero.startsWith("1.")
    ) {
      return null;
    }

    // Subgrupo do ITEM 1 - Não mostra botão
    if (
      data.tipo === TipoItem.SUBGRUPO &&
      data.item === "PRODUTOS E SERVIÇOS ESSENCIAIS"
    ) {
      return null;
    }

    // ITEM 3 - Grupo e subgrupo não mostram botões (fixos)
    if (data.tipo === TipoItem.GRUPO && data.item.includes("ITEM 3")) {
      return null;
    }

    if (
      data.tipo === TipoItem.SUBGRUPO &&
      data.item === "DESPESAS ACESSÓRIAS"
    ) {
      // Sempre disponível: a mesma categoria pode ser adicionada mais de uma vez
      return (
        <button
          className="btn btn-info btn-xs ml-1 bg-zinc-600 border-zinc-600 text-white"
          title="Adicionar Categorias"
          onClick={abrirModalDespesas}
        >
          <FiPlus className="h-4 w-4" />
        </button>
      );
    }

    // ITEM 3 - Categorias amarelas - adicionar linha de fornecedor e remover
    if (
      data.tipo === TipoItem.ITEM_ACESSORIA &&
      data.numero &&
      /^3\.\d+$/.test(data.numero)
    ) {
      return (
        <div className="flex gap-1 justify-end">
          <button
            className="btn btn-info btn-xs bg-white border-white text-zinc-700"
            title="Adicionar fornecedor"
            onClick={() => handleAddLinhaBranca(data.id)}
          >
            <FiPlus className="h-4 w-4" />
          </button>
          <button
            className="btn btn-error btn-xs bg-white border-white"
            title="Remover Categoria"
            onClick={() => handleRemoveCategoriaDespesa(data.id)}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      );
    }

    // Linhas brancas de fornecedor: podem ser removidas uma a uma, já que
    // agora nascem só com uma e o usuário acrescenta conforme precisa.
    if (TIPOS_LINHA_COM_ANEXO.includes(data.tipo)) {
      return (
        <div className="flex gap-1 justify-end">
          <button
            className="btn btn-error btn-xs bg-white border-white"
            title="Remover esta linha"
            onClick={() => handleRemoveLinhaBranca(data.id)}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      );
    }

    // ITEM 2 - Grupo cinza - pode adicionar subgrupos
    if (data.tipo === TipoItem.GRUPO && data.item.includes("ITEM 2")) {
      return (
        <button
          className="btn btn-info btn-xs ml-1 bg-zinc-600 border-zinc-600 text-white"
          title="Adicionar Categoria"
          onClick={() => openCreateSubgrupoModal(data.id)}
        >
          <FiPlus className="h-4 w-4" />
        </button>
      );
    }

    // Subgrupo preto - pode adicionar itens amarelos + botão remover
    if (
      data.tipo === TipoItem.SUBGRUPO &&
      data.item !== "PRODUTOS E SERVIÇOS ESSENCIAIS" &&
      data.item !== "DESPESAS ACESSÓRIAS"
    ) {
      return (
        <div className="flex gap-1 justify-end">
          <button
            className="btn btn-info btn-xs ml-1 bg-zinc-600 border-zinc-600 text-white"
            title="Adicionar Item"
            onClick={() => handleAddItemToSubgrupo(data.id)}
          >
            <FiPlus className="h-4 w-4" />
          </button>
          <button
            className="btn btn-error btn-xs bg-white border-white"
            title="Remover Subgrupo e seus itens"
            onClick={() => handleRemoveSubgrupo(data.id)}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      );
    }

    // Item amarelo do ITEM 2. Três comportamentos: ITEM pede cotação a
    // fornecedores, ITEM_TAXAS tem 3 linhas fixas e ITEM_TRANSPORTE nasce vazio
    // e recebe as linhas de fornecedor pelo "+".
    if (
      (data.tipo === TipoItem.ITEM ||
        TIPOS_ITEM_SEM_COTACAO.includes(data.tipo)) &&
      data.numero &&
      data.numero.startsWith("2.")
    ) {
      // Duplicar aparece só com o mouse sobre a linha: é ação rara, e três
      // botões fixos em cada linha amarela pesariam na planilha inteira.
      const botaoDuplicar = (
        <button
          className="btn btn-ghost btn-xs invisible group-hover:visible"
          title="Duplicar item com os fornecedores"
          aria-label="Duplicar item"
          onClick={() => handleDuplicar(data.id)}
        >
          <FiCopy className="h-4 w-4" />
        </button>
      );

      // ITEM_TAXAS: apenas botão remover (sempre tem 3 linhas fixas, não pode adicionar mais)
      if (data.tipo === TipoItem.ITEM_TAXAS) {
        return (
          <div className="flex gap-1 items-center justify-end">
            {botaoDuplicar}
            <button
              className="btn btn-error btn-xs bg-white border-white"
              title="Remover Item"
              onClick={() => handleRemoveItem(data.id)}
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
        );
      }

      // ITEM_TRANSPORTE: acrescenta linhas de fornecedor, que é onde o
      // documento de cada um é anexado.
      if (data.tipo === TipoItem.ITEM_TRANSPORTE) {
        return (
          <div className="flex gap-1 items-center justify-end">
            {botaoDuplicar}
            <button
              className="btn btn-info btn-xs bg-zinc-600 border-zinc-600 text-white"
              title="Adicionar fornecedor"
              onClick={() => handleAddLinhaBranca(data.id)}
            >
              <FiPlus className="h-4 w-4" />
            </button>
            <button
              className="btn btn-error btn-xs bg-white border-white"
              title="Remover Item"
              onClick={() => handleRemoveItem(data.id)}
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
        );
      }

      // ITEM normal: pode adicionar fornecedores + botão remover
      return (
        <div className="flex gap-1 items-center justify-end">
          {botaoDuplicar}
          <button
            className="btn btn-info btn-xs bg-zinc-600 border-zinc-600 text-white"
            title="Adicionar Fornecedor"
            onClick={() => handleAddFornecedores(data.id)}
          >
            <FiPlus className="h-4 w-4" />
          </button>
          <button
            className="btn btn-error btn-xs bg-white border-white"
            title="Remover Item e seus fornecedores"
            onClick={() => handleRemoveItem(data.id)}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      );
    }

    // DESCRITIVO_TAXAS - Linhas brancas fixas - não pode deletar individualmente
    if (data.tipo === TipoItem.DESCRITIVO_TAXAS) {
      return null;
    }

    // Fornecedor - apenas botão de remover
    if (data.tipo === TipoItem.FORNECEDOR) {
      return (
        <div className="flex gap-1 justify-end">
          <button
            className="btn btn-error btn-xs bg-white border-white"
            title="Remover Fornecedor"
            onClick={() => handleRemoveFornecedor(data.id)}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      );
    }

    return <span>{data.numero || ""}</span>;
  };

  // Renderizador customizado de opções do select de fornecedor
  const formatFornecedorOption = (
    option: any,
    { context }: { context: string },
  ) => {
    if (context === "value") {
      return <span>{option.label}</span>;
    }
    const razaoSocial = option.data?.razaoSocial;
    const nomeFantasia = option.data?.nome;
    const situacao: string | undefined = option.data?.situacao;
    const showBoth =
      razaoSocial && nomeFantasia && razaoSocial !== nomeFantasia;
    return (
      <div>
        <div className="text-sm">
          {razaoSocial || nomeFantasia || option.label}
        </div>
        {showBoth && (
          <div className="text-xs text-gray-400">
            Nome fantasia: {nomeFantasia}
          </div>
        )}
        {/* O mesmo nome fantasia pode estar em dois cadastros, um com o CNPJ
            antigo, já baixado. Sem o aviso a escolha errada só aparecia no
            envio, que a API recusa. */}
        {situacao && situacao.toUpperCase() !== "ATIVA" && (
          <div className="text-xs font-semibold text-red-600">
            CNPJ {situacao.toUpperCase()} na Receita · não recebe cotação
          </div>
        )}
      </div>
    );
  };

  // Cell Renderer para item (sem HTML como string)
  const ItemCellRenderer = (props: any) => {
    const { data, value } = props;

    if (data.tipo === TipoItem.GRUPO) {
      return <div style={{ fontWeight: 700, color: "#FFFFFF" }}>{value}</div>;
    } else if (data.tipo === TipoItem.SUBGRUPO) {
      return (
        <div style={{ fontWeight: 600, color: "#FFFFFF" }}>
          <p>{value}</p>
        </div>
      );
    } else if (nomeVemDoSelectDeFornecedor(data)) {
      // Linha branca de TAXAS, TRANSPORTES ou DESPESAS ACESSÓRIAS: AsyncSelect
      // de fornecedor, igual à linha de FORNECEDOR. O bloco serve aos três; só
      // muda o tipo da linha amarela que é o pai e o das irmãs, então eles vêm
      // em variáveis em vez de ficarem fixos no código.
      const tipoLinhaBranca = data.tipo as TipoItem;
      const tipoLinhaAmarela = tipoAmareloDaLinhaBranca(tipoLinhaBranca);

      const currentIndex = rowData.findIndex((r) => r.id === data.id);
      let itemPaiIndex = -1;
      if (currentIndex >= 0) {
        for (let i = currentIndex - 1; i >= 0; i--) {
          if (rowData[i].tipo === tipoLinhaAmarela) {
            itemPaiIndex = i;
            break;
          }
          if (
            rowData[i].tipo === TipoItem.SUBGRUPO ||
            rowData[i].tipo === TipoItem.GRUPO
          )
            break;
        }
      }

      // Coletar IDs dos fornecedores já usados neste item (exceto o atual)
      const fornecedoresJaUsados = new Set<string>();
      if (itemPaiIndex >= 0) {
        for (let i = itemPaiIndex + 1; i < rowData.length; i++) {
          const row = rowData[i];
          if (row.tipo === tipoLinhaBranca && row.fornecedorId) {
            if (row.fornecedorId !== data.fornecedorId) {
              fornecedoresJaUsados.add(row.fornecedorId);
            }
          } else if (row.tipo !== tipoLinhaBranca) {
            break;
          }
        }
      }

      return (
        <AsyncSelect
          noOptionsMessage={(text) => {
            if (text.inputValue) {
              return (
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-gray-500">
                    Nenhum fornecedor encontrado
                  </span>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setRowIdParaNovoFornecedor(data.id);
                      setCnpjParaNovoFornecedor(text.inputValue);
                      setModalAdicionarFornecedorAberta(true);
                    }}
                  >
                    Adicionar novo fornecedor
                  </button>
                </div>
              );
            }
            return (
              <div className="inline-flex">
                <span className="text-sm">Pesquise por um fornecedor...</span>
              </div>
            );
          }}
          loadingMessage={() => <span>Carregando fornecedores...</span>}
          menuPortalTarget={
            typeof window !== "undefined" ? document.body : null
          }
          isSearchable={true}
          value={
            // O backend passou a gravar e devolver o fornecedorId destas linhas,
            // mas as gravadas antes disso têm só o NOME no campo `item`. Por
            // isso o rótulo sai do nome, que existe nos dois casos, e o id entra
            // como valor quando está presente.
            data.item
              ? {
                  value: data.fornecedorId || data.item,
                  label: data.item,
                  data: data.fornecedor,
                }
              : null
          }
          loadOptions={loadFornecedoresOptions}
          formatOptionLabel={formatFornecedorOption}
          isOptionDisabled={(option: any) => {
            return fornecedoresJaUsados.has(option.value);
          }}
          onChange={(selectedOption) => {
            const fornecedorSelecionado = selectedOption?.data;
            const nomeExibicao =
              selectedOption?.data?.nome ||
              selectedOption?.data?.razaoSocial ||
              "";

            const updatedData = {
              ...data,
              item: nomeExibicao,
              fornecedorId: selectedOption?.data?.id,
              fornecedor: fornecedorSelecionado
                ? {
                    id: fornecedorSelecionado.id,
                    nome: fornecedorSelecionado.nome,
                    razaoSocial: fornecedorSelecionado.razaoSocial,
                    email: fornecedorSelecionado.email,
                    cnpj: fornecedorSelecionado.cnpj,
                    telefone: fornecedorSelecionado.telefone,
                    cnaePrincipal: fornecedorSelecionado.cnaePrincipal,
                  }
                : undefined,
            };

            if (data.id) {
              setRowData((prevData) => {
                const newRowData = [...prevData];
                const idx = newRowData.findIndex((r) => r.id === data.id);
                if (idx !== -1) {
                  newRowData[idx] = updatedData;
                }
                return newRowData;
              });
            }
          }}
          styles={{
            menu: (base) => ({
              ...base,
              width: "100%",
            }),
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />
      );
    } else if (data.tipo === TipoItem.FORNECEDOR) {
      // Encontrar o item pai deste fornecedor percorrendo para trás
      const currentIndex = rowData.findIndex((r) => r.id === data.id);
      let itemPaiIndex = -1;
      if (currentIndex >= 0) {
        for (let i = currentIndex - 1; i >= 0; i--) {
          // Qualquer linha amarela serve de pai. Sem ITEM_TRANSPORTE aqui, o
          // fornecedor de um item convertido não achava o pai e a lista de
          // "já usados" vinha vazia — dava para escolher o mesmo fornecedor
          // duas vezes e quebrar o índice único no salvamento.
          if (
            rowData[i].tipo === TipoItem.ITEM ||
            TIPOS_ITEM_SEM_COTACAO.includes(rowData[i].tipo)
          ) {
            itemPaiIndex = i;
            break;
          }
          if (
            rowData[i].tipo === TipoItem.SUBGRUPO ||
            rowData[i].tipo === TipoItem.GRUPO
          )
            break;
        }
      }

      // Coletar IDs dos fornecedores já usados neste item (exceto o atual)
      const fornecedoresJaUsados = new Set<string>();
      if (itemPaiIndex >= 0) {
        for (let i = itemPaiIndex + 1; i < rowData.length; i++) {
          const row = rowData[i];
          if (row.tipo === TipoItem.FORNECEDOR && row.fornecedorId) {
            // Não incluir o fornecedor atual na lista de desabilitados
            if (row.fornecedorId !== data.fornecedorId) {
              fornecedoresJaUsados.add(row.fornecedorId);
            }
          } else if (row.tipo !== TipoItem.FORNECEDOR) {
            break; // Parar quando encontrar outro tipo de linha
          }
        }
      }

      // Fornecedor com select editável
      return (
        <AsyncSelect
          noOptionsMessage={(text) => {
            if (text.inputValue) {
              return (
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-gray-500">
                    Nenhum fornecedor encontrado
                  </span>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setRowIdParaNovoFornecedor(data.id);
                      setCnpjParaNovoFornecedor(text.inputValue);
                      setModalAdicionarFornecedorAberta(true);
                    }}
                  >
                    Adicionar novo fornecedor
                  </button>
                </div>
              );
            }
            return (
              <div className="inline-flex">
                <span className="text-sm">Pesquise por um fornecedor...</span>
              </div>
            );
          }}
          loadingMessage={() => <span>Carregando fornecedores...</span>}
          menuPortalTarget={
            typeof window !== "undefined" ? document.body : null
          }
          isSearchable={true}
          value={
            value
              ? {
                  value: data.fornecedorId,
                  label: data.item,
                  data: data.fornecedor,
                }
              : null
          }
          loadOptions={loadFornecedoresOptions}
          formatOptionLabel={formatFornecedorOption}
          isOptionDisabled={(option: any) => {
            // Desabilitar fornecedores já usados no mesmo item
            return fornecedoresJaUsados.has(option.value);
          }}
          onChange={(selectedOption) => {
            const fornecedorSelecionado = selectedOption?.data;
            const nomeExibicao =
              selectedOption?.data?.nome ||
              selectedOption?.data?.razaoSocial ||
              "";

            // Atualizar com dados completos do fornecedor
            const updatedData = {
              ...data,
              item: nomeExibicao,
              fornecedorId: selectedOption?.data?.id,
              fornecedor: fornecedorSelecionado
                ? {
                    id: fornecedorSelecionado.id,
                    nome: fornecedorSelecionado.nome,
                    razaoSocial: fornecedorSelecionado.razaoSocial,
                    email: fornecedorSelecionado.email,
                    cnpj: fornecedorSelecionado.cnpj,
                    telefone: fornecedorSelecionado.telefone,
                    cnaePrincipal: fornecedorSelecionado.cnaePrincipal,
                  }
                : undefined,
            };

            // Atualizar o estado local
            if (data.id) {
              setRowData((prevData) => {
                const newRowData = [...prevData];
                const idx = newRowData.findIndex((r) => r.id === data.id);
                if (idx !== -1) {
                  newRowData[idx] = updatedData;
                }
                return newRowData;
              });
            }
          }}
          styles={{
            menu: (base) => ({
              ...base,
              width: "100%",
            }),
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />
      );
    }
    return <span>{value}</span>;
  };

  // Cell Renderer para status (sem HTML como string)
  // Envia lembrete de 1 clique ao fornecedor (item 3.1). Trata 429/409 amigavelmente.
  /**
   * Copia o link de preenchimento para a área de transferência.
   *
   * navigator.clipboard só existe em contexto seguro; o fallback com
   * execCommand cobre navegador antigo e evita o botão não fazer nada.
   */
  const copiarLinkOrcamento = useCallback(
    async (link: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          const campo = document.createElement("textarea");
          campo.value = link;
          campo.style.position = "fixed";
          campo.style.opacity = "0";
          document.body.appendChild(campo);
          campo.select();
          document.execCommand("copy");
          document.body.removeChild(campo);
        }
        showSuccess("Link copiado. Agora é só enviar ao fornecedor.");
      } catch {
        // Sem cópia automática, pelo menos mostra o endereço para copiar à mão.
        showWarning(`Não foi possível copiar. O link é: ${link}`);
      }
    },
    [showSuccess, showWarning],
  );

  const handleLembrar = useCallback(
    async (orcamentoId: string, fornecedorNome: string) => {
      if (!orcamentoId) return;
      setLembreteLoadingId(orcamentoId);
      try {
        const res = await orcamentoService.lembrarFornecedor(orcamentoId);
        setLembreteInfoById((prev) => ({
          ...prev,
          [orcamentoId]: res.ultimoLembreteEm,
        }));
        showSuccess(
          `Lembrete enviado para ${fornecedorNome || "o fornecedor"}.`,
        );
      } catch (err: any) {
        const status = err?.response?.status;
        const meta = err?.response?.data?.metadata;
        const msg = err?.response?.data?.message as string | undefined;
        if (status === 429) {
          if (meta?.ultimoLembreteEm) {
            setLembreteInfoById((prev) => ({
              ...prev,
              [orcamentoId]: meta.ultimoLembreteEm,
            }));
          }
          showWarning(
            msg ||
              "Um lembrete já foi enviado para este fornecedor nas últimas 24 horas.",
          );
        } else if (status === 409 || status === 400) {
          showWarning(msg || "Não foi possível enviar o lembrete.");
        } else {
          showError("Não foi possível enviar o lembrete. Tente novamente.");
        }
      } finally {
        setLembreteLoadingId(null);
      }
    },
    [showSuccess, showWarning, showError],
  );

  const StatusCellRenderer = useCallback(
    (props: any) => {
      const { data, value } = props;

      // Linhas brancas que recebem anexo por fornecedor (TRANSPORTES e
      // DESPESAS ACESSÓRIAS). Diferente de TAXAS, onde o documento é do item
      // inteiro e fica na linha amarela.
      //
      // Toda linha tem o botão, não só a primeira: a regra de "só o Fornecedor
      // 1" é sobre quantas linhas a categoria cria sozinha, e quem acrescenta
      // a segunda e a terceira à mão faz isso justamente para anexar o
      // documento delas.
      if (TIPOS_LINHA_COM_ANEXO.includes(data.tipo)) {
        const itemId = data.id;
        const arquivoAtivo = itemId ? arquivosAtivos.get(itemId) : null;
        const uploadKey = `${data.tipo}_${data.numero}`;
        const isUploading = uploadingFiles.has(uploadKey);

        if (isUploading) {
          return (
            <div className="flex items-center justify-center gap-2 text-primary">
              <span className="loading loading-spinner loading-sm"></span>
              <span className="text-sm">Enviando...</span>
            </div>
          );
        }

        // Mesmo azul e mesmo tamanho do "Anexar" de TAXAS E SEGUROS: é a
        // mesma ação, e variar a cor entre blocos só faria parecer outra coisa.
        // Quando há arquivo o botão vira verde e diz "Anexado", que é o sinal
        // de estado pedido — as ações de trocar e remover ficam ao lado.
        if (arquivoAtivo) {
          return (
            <div className="flex items-center justify-center gap-2">
              <button
                className="btn btn-success btn-sm text-white gap-1"
                onClick={() => window.open(arquivoAtivo.url, "_blank")}
                title={`Anexado: ${arquivoAtivo.nomeArquivo} — clique para baixar`}
              >
                <FiCheckCircle className="h-4 w-4" />
                Anexado
              </button>

              <label
                className="btn btn-warning btn-sm text-white cursor-pointer"
                data-altera title="Substituir arquivo"
              >
                <FiUpload className="h-4 w-4" />
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadArquivo(data, file);
                    e.target.value = "";
                  }}
                />
              </label>

              <button
                className="btn btn-error btn-sm text-white"
                onClick={() => itemId && handleRemoverArquivo(itemId)}
                data-altera title="Remover arquivo"
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          );
        }

        return (
          <label
            className="btn btn-primary btn-sm cursor-pointer flex items-center justify-center"
            data-altera title="Anexar documento deste fornecedor"
          >
            <FiPaperclip className="h-4 w-4" />
            Anexar
            <input
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadArquivo(data, file);
                e.target.value = "";
              }}
            />
          </label>
        );
      }

      // Para linhas ITEM_TAXAS (amarelas), mostrar UI de upload de arquivo
      if (data.tipo === TipoItem.ITEM_TAXAS) {
        const itemId = data.id;
        const arquivoAtivo = itemId ? arquivosAtivos.get(itemId) : null;
        // Usar identificador único baseado em tipo+numero para controlar loading
        const uploadKey = `${data.tipo}_${data.numero}`;
        const isUploading = uploadingFiles.has(uploadKey);

        // Mostrar loading durante upload
        if (isUploading) {
          return (
            <div className="flex items-center justify-center gap-2 text-primary">
              <span className="loading loading-spinner loading-sm"></span>
              <span className="text-sm">Enviando...</span>
            </div>
          );
        }

        // Item já tem arquivo - mostrar apenas ícones
        if (arquivoAtivo) {
          return (
            <div className="flex items-center justify-center gap-2">
              {/* Ícone de Download */}
              <button
                className="btn btn-info btn-xs text-white"
                onClick={() => {
                  window.open(arquivoAtivo.url, "_blank");
                }}
                title={`Baixar ${arquivoAtivo.nomeArquivo}`}
              >
                <FiDownload className="h-4 w-4" />
              </button>

              {/* Ícone de Upload (substituir) */}
              <label
                className="btn btn-warning btn-xs text-white cursor-pointer"
                data-altera title="Substituir arquivo"
              >
                <FiUpload className="h-4 w-4" />
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleUploadArquivo(data, file);
                    }
                    e.target.value = "";
                  }}
                />
              </label>

              {/* Ícone de Deletar */}
              <button
                className="btn btn-error btn-xs text-white"
                onClick={() => {
                  if (itemId) {
                    handleRemoverArquivo(itemId);
                  }
                }}
                data-altera title="Remover arquivo"
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          );
        }

        // Item sem arquivo - botão para anexar (salva automaticamente se necessário)
        return (
          <label
            data-altera
            className="btn btn-primary btn-sm cursor-pointer flex items-center justify-center"
          >
            <FiPaperclip className="h-4 w-4" />
            Anexar
            <input
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleUploadArquivo(data, file);
                }
                e.target.value = "";
              }}
            />
          </label>
        );
      }

      // Para itens amarelos ITEM (não ITEM_TAXAS), mostrar botão de Solicitar Cotação
      if (
        data.tipo === TipoItem.ITEM &&
        data.numero &&
        data.numero.startsWith("2.")
      ) {
        return (
          <button
            className="btn btn-warning btn-sm w-full text-white bg-orange-400 border-orange-400 hover:bg-orange-500 hover:border-orange-500"
            data-altera title="Enviar este item para todos os fornecedores"
            onClick={() => abrirModalItem(data)}
          >
            <FiSend className="h-4 w-4" /> Solicitar cotação do item
          </button>
        );
      }

      // DESCRITIVO_TAXAS não mostra botão de status
      if (data.tipo === TipoItem.DESCRITIVO_TAXAS) {
        return null;
      }

      if (data.tipo !== TipoItem.FORNECEDOR || !value) return null;

      // Normalizar o valor para garantir match (lowercase e trim)
      const normalizedValue = String(value).toLowerCase().trim();

      // Status "Aguardando Envio" -> Botão "Solicitar Cotação"
      if (
        normalizedValue === "aguardando_envio" ||
        normalizedValue === "aguardando envio"
      ) {
        // Encontrar o item pai deste fornecedor
        const numeroItem = data.numero
          ? data.numero.split(".").slice(0, -1).join(".")
          : "";
        const itemPai = rowData.find(
          (r) => r.numero === numeroItem && r.tipo === TipoItem.ITEM,
        );

        return (
          <button
            className="btn btn-warning btn-sm w-full text-white bg-orange-400 border-orange-400 hover:bg-orange-500 hover:border-orange-500"
            data-altera title="Solicitar Cotação"
            onClick={() => {
              if (itemPai) {
                abrirModalIndividual(itemPai, data);
              }
            }}
          >
            <FiSend className="h-4 w-4" /> Solicitar cotação
          </button>
        );
      }

      // Configuração de status usando valores normalizados
      const statusConfig: Record<
        string,
        {
          badgeClass: string;
          icon: React.ReactNode;
          text: string;
          descricao: string;
        }
      > = {
        respondida: {
          badgeClass: "text-success",
          icon: <FiCheckCircle className="inline-block h-5 w-5 mr-1" />,
          text: "Recebida",
          descricao: "O fornecedor já enviou a proposta.",
        },
        enviada: {
          badgeClass: "text-orange-400",
          icon: <FiClock className="inline-block h-5 w-5 mr-1" />,
          text: "Aguardando",
          descricao: "Solicitação enviada. Aguardando resposta do fornecedor.",
        },
        reenviada: {
          badgeClass: "text-amber-500",
          icon: <FiRepeat className="inline-block h-5 w-5 mr-1" />,
          text: "Reenviado",
          descricao:
            "Solicitação reenviada ao fornecedor. Aguardando nova resposta.",
        },
        rejeitada: {
          badgeClass: "text-error",
          icon: <FiX className="inline-block h-5 w-5 mr-1" />,
          text: "Rejeitada",
          descricao: "O fornecedor recusou a solicitação.",
        },
      };

      // Tentar primeiro com o valor normalizado, depois com o valor original (para compatibilidade com enums)
      const config =
        statusConfig[normalizedValue] || statusConfig[String(value)];
      if (!config) return <span className="">{String(value)}</span>;

      return (
        <div className="flex gap-3 items-center justify-center w-full">
          <span
            className={`flex items-center justify-center ${config.badgeClass}`}
            title={
              data.solicitadoPorNome
                ? `${config.descricao}\nSolicitado por: ${data.solicitadoPorNome}`
                : config.descricao
            }
          >
            {config.icon} <span className="text-xs">{config.text}</span>
          </span>


          {/* Botões de ação */}
          <div className="flex items-center gap-1">
            {/* Botão de Download - aparece apenas se houver PDF */}
            {data.orcamentoPdfUrl && (
              <a
                href={data.orcamentoPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-neutral btn-xs"
                title="Download do orçamento"
                onClick={(e) => e.stopPropagation()}
              >
                <FiDownload className="h-4 w-4" />
              </a>
            )}
            {data.orcamentoPdfRejeicaoUrl && (
              <a
                href={data.orcamentoPdfRejeicaoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-error btn-xs"
                title="PDF de Rejeição"
                onClick={(e) => e.stopPropagation()}
              >
                <FiFileText className="h-4 w-4" />
              </a>
            )}
            {/* Botão de reenvio - aparece em todos os status exceto "Aguardando Envio" */}
            {normalizedValue !== "aguardando_envio" && (
              <button
                className="btn btn-warning btn-xs"
                data-altera title="Reenviar cotação"
                onClick={() => {
                  // Encontrar o item pai deste fornecedor
                  const numeroItem = data.numero
                    ? data.numero.split(".").slice(0, -1).join(".")
                    : "";
                  const itemPai = rowData.find(
                    (r) => r.numero === numeroItem && r.tipo === TipoItem.ITEM,
                  );
                  if (itemPai) {
                    abrirModalIndividual(itemPai, data);
                  }
                }}
              >
                <FiRepeat className="h-4 w-4" />
              </button>
            )}
            {/* Ações secundárias num menu só.
                Antes o olho e o sino ficavam soltos ao lado do download e do
                reenvio, e a coluna virava uma fileira de ícones. Baixar e
                reenviar continuam à mão porque são o que se usa toda hora; o
                resto — quando o fornecedor abriu, lembrete e o link de
                preenchimento — passa a viver aqui dentro.

                É modal, e não menu suspenso, porque o container da tabela tem
                overflow-auto e cortaria um dropdown posicionado em CSS. */}
            {data.orcamentoId && (
              <button
                className="btn btn-ghost btn-xs border border-base-300"
                title="Mais ações"
                aria-label="Mais ações deste fornecedor"
                onClick={(e) => {
                  e.stopPropagation();
                  setAcoesFornecedor(data);
                }}
              >
                <FiMoreHorizontal className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      );
    },
    [
      rowData,
      arquivosAtivos,
      uploadingFiles,
      lembreteLoadingId,
      lembreteInfoById,
      handleLembrar,
      isReadOnly,
    ],
  );

  // Adicionar subgrupo preto abaixo do grupo cinza ITEM 2
  const handleAddSubgrupo = (grupoId: string, nomeSubgrupo: string) => {
    const nomeFormatado = nomeSubgrupo.trim();
    if (!nomeFormatado) {
      return;
    }

    const novoSubgrupo: ItemPlanilha = {
      id: crypto.randomUUID(),
      tipo: TipoItem.SUBGRUPO,
      numero: "",
      item: nomeFormatado.toUpperCase(),
      descritivo: "",
      quantidade: undefined,
      diaria: undefined,
      unitario: undefined,
      subtotal: undefined,
      total: undefined,
      status: undefined,
      observacoes: "",
    };

    const isTaxasESeguros = nomeFormatado
      .toUpperCase()
      .includes("TAXAS E SEGUROS");

    const rowIndex = rowData.findIndex((r) => r.id === grupoId);
    if (rowIndex === -1) return;

    // Encontrar o índice do fim do grupo ITEM 2 (antes do próximo GRUPO)
    let fimDoGrupo = rowData.length;
    for (let i = rowIndex + 1; i < rowData.length; i++) {
      if (rowData[i].tipo === TipoItem.GRUPO) {
        fimDoGrupo = i;
        break;
      }
    }

    let insertIndex: number;

    if (isTaxasESeguros) {
      // TAXAS E SEGUROS sempre vai para o final do grupo
      insertIndex = fimDoGrupo;
    } else {
      // Para outras categorias, verificar se TAXAS E SEGUROS já existe
      // Se existir, inserir ANTES do subgrupo TAXAS E SEGUROS
      let taxasIndex = -1;
      for (let i = rowIndex + 1; i < fimDoGrupo; i++) {
        if (
          rowData[i].tipo === TipoItem.SUBGRUPO &&
          rowData[i].item?.toUpperCase().includes("TAXAS E SEGUROS")
        ) {
          taxasIndex = i;
          break;
        }
      }

      if (taxasIndex !== -1) {
        // Inserir antes do subgrupo TAXAS E SEGUROS
        insertIndex = taxasIndex;
      } else {
        // Não existe TAXAS E SEGUROS, inserir no final do grupo
        insertIndex = fimDoGrupo;
      }
    }

    const newRowData = [
      ...rowData.slice(0, insertIndex),
      novoSubgrupo,
      ...rowData.slice(insertIndex),
    ];

    setRowData(newRowData);
  };

  // Adicionar item amarelo abaixo do subgrupo preto
  const handleAddItemToSubgrupo = (subgrupoId: string) => {
    const rowIndex = rowData.findIndex((r) => r.id === subgrupoId);
    if (rowIndex === -1) return;
    const subgrupoRow = rowData[rowIndex];
    const nomeSubgrupo = subgrupoRow.item?.toUpperCase() ?? "";
    const isTaxasESeguros = nomeSubgrupo.includes("TAXAS E SEGUROS");
    // TRANSPORTES funciona como TAXAS — valor na linha amarela, sem pedir
    // cotação —, mas o documento é de cada fornecedor, então quem recebe o
    // anexo é a linha branca e não a amarela.
    const isTransportes = nomeSubgrupo.includes("TRANSPORTE");

    const tipoItem = isTaxasESeguros
      ? TipoItem.ITEM_TAXAS
      : isTransportes
        ? TipoItem.ITEM_TRANSPORTE
        : TipoItem.ITEM;

    const novoItem: ItemPlanilha = {
      id: crypto.randomUUID(),
      tipo: tipoItem,
      numero: "2.1", // Será renumerado automaticamente
      item: "", // Vazio para o usuário preencher na planilha
      descritivo: "",
      quantidade: 1,
      diaria: 1,
      unitario: 0,
      subtotal: 0,
      total: 0,
      status: undefined,
      observacoes: "",
    };

    // Inserir após o último item/fornecedor deste subgrupo
    let insertIndex = rowIndex + 1;
    for (let i = rowIndex + 1; i < rowData.length; i++) {
      if (
        rowData[i].tipo === TipoItem.SUBGRUPO ||
        rowData[i].tipo === TipoItem.GRUPO
      ) {
        break;
      }
      insertIndex = i + 1;
    }

    const itensParaAdicionar: ItemPlanilha[] = [novoItem];

    // TRANSPORTES nasce só com a linha amarela, vazia. A linha de fornecedor
    // vem depois, pelo "+" da própria linha amarela — a mesma sequência de
    // dois passos que a pessoa já usa nos itens comuns.

    // TAXAS E SEGUROS mantém as 3 linhas brancas fixas de sempre
    if (isTaxasESeguros) {
      // Adicionar 3 linhas brancas fixas
      for (let i = 1; i <= 3; i++) {
        itensParaAdicionar.push({
          id: crypto.randomUUID(),
          tipo: TipoItem.DESCRITIVO_TAXAS,
          numero: `${i}`,
          item: "",
          descritivo: "",
          quantidade: undefined,
          diaria: undefined,
          unitario: undefined,
          subtotal: undefined,
          total: undefined,
          status: undefined,
          observacoes: "",
        });
      }
    }

    // Renumerar itens automaticamente
    setRowData((oldRowData: ItemPlanilha[]) => {
      const newRowData = [
        ...oldRowData.slice(0, insertIndex),
        ...itensParaAdicionar,
        ...oldRowData.slice(insertIndex),
      ];
      return renumerarItens(newRowData);
    });

    showInfo("Item adicionado à categoria. Preencha os detalhes na planilha.");
  };

  // Adicionar fornecedor ao item amarelo (apenas para ITEM normal, não ITEM_TAXAS)
  const handleAddFornecedores = (itemId: string) => {
    const rowIndex = rowData.findIndex((r) => r.id === itemId);
    if (rowIndex === -1) return;
    const itemRow = rowData[rowIndex];

    // ITEM_TAXAS não usa esta função (sempre tem 3 linhas fixas criadas junto)
    if (itemRow.tipo === TipoItem.ITEM_TAXAS) {
      return;
    }

    // Para ITEM normal, adicionar fornecedor
    // Contar quantos fornecedores já existem para este item
    let fornecedorCount = 0;
    for (let i = rowIndex + 1; i < rowData.length; i++) {
      if (rowData[i].tipo !== TipoItem.FORNECEDOR) {
        break;
      }
      fornecedorCount++;
    }

    // Gerar o número do novo fornecedor (ex: 2.01.01, 2.01.02, etc)
    const numeroFornecedor = `${itemRow.numero}.${String(fornecedorCount + 1).padStart(2, "0")}`;

    const novoFornecedor: ItemPlanilha = {
      id: crypto.randomUUID(),
      tipo: TipoItem.FORNECEDOR,
      numero: numeroFornecedor,
      item: "", // Vazio para ser selecionado no select
      descritivo: "",
      quantidade: undefined,
      diaria: undefined,
      unitario: 0,
      subtotal: undefined,
      total: undefined,
      status: StatusOrcamento.AGUARDANDO_ENVIO,
      observacoes: "",
    };

    // Inserir logo após o item, mas no final dos fornecedores existentes
    let insertIndex = rowIndex + 1;

    // Pular fornecedores existentes para inserir no final
    for (let i = rowIndex + 1; i < rowData.length; i++) {
      if (rowData[i].tipo !== TipoItem.FORNECEDOR) {
        break;
      }
      insertIndex = i + 1;
    }

    const newRowData = [
      ...rowData.slice(0, insertIndex),
      novoFornecedor,
      ...rowData.slice(insertIndex),
    ];

    setRowData(renumerarItens(newRowData));
  };

  /** Descreve o que uma linha de fornecedor leva embora se for removida. */
  const oQueSePerdeNaLinha = (row?: ItemPlanilha): string | null => {
    if (!row) return null;
    const perdas: string[] = [];
    if (row.unitario != null && row.unitario > 0) {
      perdas.push(
        `o valor cotado de ${row.unitario.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}`,
      );
    }
    if (row.orcamentoPdfUrl) perdas.push("a proposta assinada em PDF");
    return perdas.length ? perdas.join(" e ") : null;
  };

  // Remover fornecedor
  const handleRemoveFornecedor = async (fornecedorId: string) => {
    // A confirmação precisa dizer o que some. Perguntar apenas "deseja
    // remover?" escondia que a linha podia levar junto uma proposta assinada,
    // e a remoção não tem desfazer.
    const linha = rowData.find((r) => r.id === fornecedorId);
    const perda = oQueSePerdeNaLinha(linha);

    const confirmed = await confirm({
      title: "Remover fornecedor",
      message: perda
        ? `Remover "${linha?.item || "este fornecedor"}" descarta ${perda}. Para tê-los de volta será preciso solicitar a cotação de novo.`
        : "Deseja remover este fornecedor do item selecionado?",
      confirmText: "Remover",
      cancelText: "Cancelar",
      tone: "danger",
    });

    if (!confirmed) {
      return;
    }

    // Linha que já existe no banco precisa ser declarada como removida; a
    // que nunca foi salva basta sumir do estado.
    const idNoBanco = idDoBanco(fornecedorId);
    if (idNoBanco) fornecedoresRemovidosRef.current.add(idNoBanco);

    // Remover por id e recalcular totais
    setRowData((prevData: ItemPlanilha[]) => {
      const newData = prevData.filter((row) => row.id !== fornecedorId);
      return renumerarItens(recalcularTotaisEMenorPreco(newData));
    });
    showInfo("Fornecedor removido da planilha.");
  };

  const handleRemoveItem = useCallback(
    async (itemId: string) => {
      const rowIndex = rowData.findIndex((r) => r.id === itemId);
      if (rowIndex === -1) return;
      const itemRow = rowData[rowIndex];

      // Contar o que será descartado junto com o item, para a confirmação
      // dizer o tamanho do estrago em vez de uma frase genérica.
      let cotacoesAfetadas = 0;
      let propostasAssinadas = 0;
      for (let i = rowIndex + 1; i < rowData.length; i++) {
        const filho = rowData[i];
        if (filho.tipo !== TipoItem.FORNECEDOR) break;
        if (filho.unitario != null && filho.unitario > 0) cotacoesAfetadas++;
        if (filho.orcamentoPdfUrl) propostasAssinadas++;
      }

      const detalhe =
        cotacoesAfetadas > 0
          ? ` Isso descarta ${cotacoesAfetadas} cotação(ões) já recebida(s)` +
            (propostasAssinadas > 0
              ? ` e ${propostasAssinadas} proposta(s) assinada(s).`
              : ".")
          : "";

      const confirmed = await confirm({
        title: "Remover item",
        message:
          (itemRow.tipo === TipoItem.ITEM_TAXAS
            ? "Deseja remover este item e suas 3 linhas de fornecedores?"
            : "Deseja remover este item e todos os fornecedores associados?") +
          detalhe,
        confirmText: "Remover",
        cancelText: "Cancelar",
        tone: "danger",
      });

      if (!confirmed) {
        return;
      }

      // Coletar IDs para remover (item + filhos consecutivos).
      //
      // Cada tipo de linha amarela tem o seu tipo de filho. Sem
      // ITEM_TRANSPORTE aqui, remover o item de transporte deixava as linhas
      // brancas dele na tela, sem pai: a renumeração as pendurava no item
      // anterior e elas seguiam no banco levando junto os anexos.
      const idsToRemove = new Set<string>([itemId]);
      const childType =
        itemRow.tipo === TipoItem.ITEM_TAXAS
          ? TipoItem.DESCRITIVO_TAXAS
          : itemRow.tipo === TipoItem.ITEM_TRANSPORTE
            ? TipoItem.DESCRITIVO_TRANSPORTE
            : TipoItem.FORNECEDOR;

      for (let i = rowIndex + 1; i < rowData.length; i++) {
        if (rowData[i].tipo === childType) {
          if (rowData[i].id) idsToRemove.add(rowData[i].id!);
        } else {
          break;
        }
      }

      const newRowData = rowData.filter((r) => !idsToRemove.has(r.id!));
      const itensRecalculados = recalcularTotaisEMenorPreco(newRowData);
      setRowData(renumerarItens(itensRecalculados));

      showInfo("Item e fornecedores associados removidos.");
    },
    [rowData],
  );

  // Remover subgrupo e todos os seus itens e fornecedores
  const handleRemoveSubgrupo = async (subgrupoId: string) => {
    const confirmed = await confirm({
      title: "Remover subgrupo",
      message:
        "Deseja remover este subgrupo com todos os itens e fornecedores relacionados?",
      confirmText: "Remover",
      cancelText: "Cancelar",
      tone: "danger",
    });

    if (!confirmed) {
      return;
    }

    const rowIndex = rowData.findIndex((r) => r.id === subgrupoId);
    if (rowIndex === -1) return;

    // Coletar IDs para remover (subgrupo + todos os filhos até próximo grupo/subgrupo)
    const idsToRemove = new Set<string>([subgrupoId]);
    for (let i = rowIndex + 1; i < rowData.length; i++) {
      if (
        rowData[i].tipo === TipoItem.GRUPO ||
        rowData[i].tipo === TipoItem.SUBGRUPO
      ) {
        break;
      }
      if (rowData[i].id) idsToRemove.add(rowData[i].id!);
    }

    const newRowData = rowData.filter((r) => !idsToRemove.has(r.id!));
    setRowData(renumerarItens(newRowData));
    showInfo("Subgrupo removido da planilha.");
  };

  /**
   * Abre o histórico de PDFs de uma cotação. A resposta só é aplicada se o
   * modal ainda for dessa cotação: abrir outra no meio do carregamento não
   * pode mostrar as versões da primeira com o nome da segunda.
   */
  const abrirHistoricoPdf = async (orcamentoId: string, nome: string) => {
    setHistoricoPdf({ orcamentoId, nome, versoes: null });
    try {
      const versoes = await orcamentoService.listarVersoesPdf(orcamentoId);
      setHistoricoPdf((atual) =>
        atual?.orcamentoId === orcamentoId ? { ...atual, versoes } : atual,
      );
    } catch (err: any) {
      setHistoricoPdf((atual) =>
        atual?.orcamentoId === orcamentoId
          ? {
              ...atual,
              versoes: [],
              erro:
                err?.response?.data?.message ||
                err?.message ||
                "Não foi possível carregar o histórico de PDFs.",
            }
          : atual,
      );
    }
  };

  /**
   * Duplica a linha amarela com as filhas, ou a linha preta com tudo abaixo
   * dela. A cópia entra logo depois do original e só existe na tela até salvar.
   */
  const handleDuplicar = (linhaId: string) => {
    const inicio = rowData.findIndex((r) => r.id === linhaId);
    if (inicio === -1) return;
    const origem = rowData[inicio];
    const ehCategoria = origem.tipo === TipoItem.SUBGRUPO;

    let fim = inicio + 1;
    while (fim < rowData.length) {
      const tipo = rowData[fim].tipo;
      const pertence = ehCategoria
        ? tipo !== TipoItem.GRUPO && tipo !== TipoItem.SUBGRUPO
        : TIPOS_FILHOS_DO_ITEM2.includes(tipo);
      if (!pertence) break;
      fim++;
    }

    const copias = rowData.slice(inicio, fim).map(copiarLinhaParaDuplicar);
    const novo = [...rowData.slice(0, fim), ...copias, ...rowData.slice(fim)];
    setRowData(renumerarItens(recalcularTotaisEMenorPreco(novo)));
    showInfo(
      ehCategoria
        ? `Categoria "${origem.item}" duplicada com os itens. As cotações da cópia ainda precisam ser solicitadas.`
        : "Item duplicado. As cotações da cópia ainda precisam ser solicitadas.",
    );
  };

  /**
   * Troca só o nome da linha preta. Itens, fornecedores e cotações ficam como
   * estão: o tipo de cada item amarelo foi decidido quando ele nasceu e não
   * acompanha a categoria — convertê-lo descartaria o que já foi cotado.
   */
  const handleTrocarCategoria = (linhaId: string, nome: string) => {
    setRowData((prev) =>
      prev.map((r) => (r.id === linhaId ? { ...r, item: nome } : r)),
    );
    setMenuCategoria(null);
    showInfo(`Categoria alterada para "${nome}". Salve para aplicar.`);
  };

  // Função para renumerar automaticamente os itens
  const renumerarItens = (data: ItemPlanilha[]): ItemPlanilha[] => {
    let itemCounter = 1;
    let inItem2 = false;
    let ultimoNumeroItem: string | null = null; // número do ITEM/ITEM_TAXAS pai atual
    let childCounter = 0; // conta filhos (FORNECEDOR ou DESCRITIVO_TAXAS)

    const newData = data.map((row) => {
      // Detectar quando entramos no ITEM 2
      if (row.tipo === TipoItem.GRUPO && row.item.includes("ITEM 2")) {
        inItem2 = true;
        itemCounter = 1;
        ultimoNumeroItem = null;
        childCounter = 0;
        return { ...row };
      }

      // Sair do ITEM 2 quando encontrar outro GRUPO
      if (row.tipo === TipoItem.GRUPO && !row.item.includes("ITEM 2")) {
        inItem2 = false;
        ultimoNumeroItem = null;
        return { ...row };
      }

      if (!inItem2) return { ...row };

      // SUBGRUPO - resetar referência ao item pai
      if (row.tipo === TipoItem.SUBGRUPO) {
        ultimoNumeroItem = null;
        childCounter = 0;
        return { ...row };
      }

      // Renumerar as linhas amarelas do ITEM 2 (ITEM, TAXAS e TRANSPORTE)
      if (
        (row.tipo === TipoItem.ITEM ||
          TIPOS_ITEM_SEM_COTACAO.includes(row.tipo)) &&
        row.numero &&
        !row.numero.startsWith("1.")
      ) {
        const novoNumero = `2.${String(itemCounter++).padStart(2, "0")}`;
        ultimoNumeroItem = novoNumero;
        childCounter = 0;
        return { ...row, numero: novoNumero };
      }

      // Renumerar filhos com base no item pai. DESCRITIVO_TRANSPORTE entra
      // aqui junto com os outros: sem isso as linhas acrescentadas ficariam
      // com o número provisório da criação e o par item/linha se perderia no
      // salvamento, que casa as linhas pelo prefixo do número.
      if (
        ultimoNumeroItem &&
        (row.tipo === TipoItem.FORNECEDOR ||
          row.tipo === TipoItem.DESCRITIVO_TAXAS ||
          row.tipo === TipoItem.DESCRITIVO_TRANSPORTE)
      ) {
        childCounter++;
        return {
          ...row,
          numero: `${ultimoNumeroItem}.${String(childCounter).padStart(2, "0")}`,
        };
      }

      return { ...row };
    });
    return newData;
  };

  // Abrir modal de envio individual (1 item, 1 fornecedor)
  const abrirModalIndividual = async (
    itemRow: ItemPlanilha,
    fornecedorRow: ItemPlanilha,
  ) => {
    // Salvar planilha antes de abrir modal
    try {
      const planilhaAtualizada = await handleSave();
      if (!planilhaAtualizada) return;

      // Buscar dados completos do fornecedor diretamente do backend
      let fornecedorCompleto = null;
      if (fornecedorRow.fornecedorId) {
        try {
          fornecedorCompleto = await fornecedorService.getById(
            fornecedorRow.fornecedorId,
          );
        } catch (err) {
          console.error(
            `Erro ao buscar fornecedor ${fornecedorRow.fornecedorId}:`,
            err,
          );
        }
      }

      setDadosModalAtual({
        itemNome: itemRow.item || itemRow.descritivo,
        fornecedorNome:
          fornecedorCompleto?.nome ||
          fornecedorRow.fornecedor?.nome ||
          fornecedorRow.item ||
          "",
        fornecedor: fornecedorCompleto || fornecedorRow.fornecedor,
        itemNumero: itemRow.numero,
        fornecedorNumero: fornecedorRow.numero,
        fornecedorId: fornecedorRow.fornecedorId || "",
      });

      setModalIndividualAberta(true);
    } catch (error) {
      console.error("Erro ao salvar planilha antes de abrir modal:", error);
      showError("Salve a planilha antes de enviar orçamentos.");
    }
  };

  // Abrir modal de envio por item (1 item, todos os fornecedores)
  const abrirModalItem = async (itemRow: ItemPlanilha) => {
    // Salvar planilha antes de abrir modal
    try {
      const planilhaAtualizada = await handleSave();
      if (!planilhaAtualizada) return;

      // Se for uma linha de fornecedor (ex: 2.5.1), extrair o número do item pai (2.5)
      const numeroItem =
        itemRow.tipo === TipoItem.FORNECEDOR && itemRow.numero
          ? itemRow.numero.split(".").slice(0, 2).join(".")
          : itemRow.numero;

      // Preferir fonte canonical: planilha.itens[].fornecedores (após salvar)
      let fornecedoresDoItem: Array<{
        fornecedorId: string;
        nome: string;
        email?: string;
        telefone?: string;
        numero?: string;
      }> = [];
      const itemCanonical = planilhaAtualizada.itens.find(
        (i) => i.numero === numeroItem,
      );
      if (itemCanonical && Array.isArray(itemCanonical.fornecedores)) {
        // Buscar todos os fornecedores únicos do item
        const fornecedorIds = itemCanonical.fornecedores
          ?.filter((f) => f.fornecedorId)
          ?.filter((f) => f?.status == "aguardando_envio") // garantir que não há undefined
          ?.map((f) => f.fornecedorId);

        // Buscar dados completos dos fornecedores do backend
        const fornecedoresCompletos = await Promise.all(
          fornecedorIds.map(async (id) => {
            try {
              return await fornecedorService.getById(id);
            } catch (err) {
              console.error(`Erro ao buscar fornecedor ${id}:`, err);
              return null;
            }
          }),
        );

        fornecedoresDoItem = itemCanonical.fornecedores
          ?.filter((f) => f.fornecedorId)
          ?.filter((f) => f?.status == "aguardando_envio") // garantir que não há undefined
          .map((f) => {
            // Buscar fornecedor completo na lista que acabamos de carregar
            const fornecedorCompleto = fornecedoresCompletos.find(
              (fc) => fc?.id === f.fornecedorId,
            );
            const fornecedorRow = rowData.find(
              (r) =>
                r.tipo === TipoItem.FORNECEDOR &&
                r.fornecedorId === f.fornecedorId,
            );

            return {
              fornecedorId: f.fornecedorId,
              nome:
                fornecedorCompleto?.nome ||
                fornecedorRow?.fornecedor?.nome ||
                f.fornecedor?.nome ||
                f.fornecedorNome ||
                "Fornecedor",
              email: emailPadraoDoFornecedor(
                fornecedorCompleto,
                fornecedorRow?.fornecedor,
                f.fornecedor,
              ),
              telefone:
                fornecedorCompleto?.telefone ||
                fornecedorRow?.fornecedor?.telefone ||
                f.fornecedor?.telefone ||
                "",
              numero: f.id, // usamos id do item_fornecedor como referência
            };
          });
      }

      if (fornecedoresDoItem.length === 0) {
        showWarning(
          "Adicione fornecedores a este item antes de enviar orçamentos.",
        );
        return;
      }

      setDadosModalAtual({
        itemNome: itemRow.item || itemRow.descritivo,
        itemNumero: itemRow.numero,
        fornecedores: fornecedoresDoItem,
      });
      setModalItemAberta(true);
    } catch (error) {
      console.error("Erro ao salvar planilha antes de abrir modal:", error);
      showError("Salve a planilha antes de enviar orçamentos.");
    }
  };

  // Abrir modal de envio por fornecedor (todos os itens, 1 fornecedor)
  const abrirModalFornecedor = async () => {
    // Sempre salvar antes de abrir o modal para garantir dados atualizados
    try {
      const planilhaAtualizada = await handleSave();
      if (!planilhaAtualizada || !planilhaAtualizada.id) {
        showError("Erro ao salvar planilha. Tente novamente.");
        return;
      }

      // Todos os fornecedores da planilha entram na lista, e não só os que têm
      // item pendente: incluir ou excluir um item invalida a cotação inteira do
      // fornecedor, e nesses casos pode não restar nenhum item pendente para
      // ele — o produtor ainda precisa poder reenviar o pedido completo.
      // Só TipoItem.ITEM: o envio granular da API descarta "item_taxas", então
      // contá-lo aqui mostraria no modal um item que nunca sai.
      const itensComFornecedores = (planilhaAtualizada.itens || []).filter(
        (item) =>
          item.tipo === TipoItem.ITEM &&
          Array.isArray(item.fornecedores) &&
          item.fornecedores.length > 0,
      );

      const fornecedorIdsUnicos = new Set<string>();
      itensComFornecedores.forEach((item) =>
        item.fornecedores!.forEach((f) => {
          if (f.fornecedorId) fornecedorIdsUnicos.add(f.fornecedorId);
        }),
      );

      if (fornecedorIdsUnicos.size === 0) {
        showWarning(
          "Nenhum fornecedor foi adicionado aos itens ainda. Adicione fornecedores nos itens antes de enviar orçamentos.",
        );
        return;
      }

      // Buscar dados completos de todos os fornecedores
      const fornecedoresCompletos = await Promise.all(
        Array.from(fornecedorIdsUnicos).map(async (fid) => {
          try {
            return await fornecedorService.getById(fid);
          } catch {
            return null;
          }
        }),
      );

      // Agrupar por fornecedor, carregando cada item com o status atual do
      // vínculo — é o que permite ao modal separar "primeiro envio" de
      // "reenvio" e avisar o que será perdido.
      const fornecedoresPorId = new Map<string, FornecedorEnvioBloco>();

      itensComFornecedores.forEach((item) => {
        item.fornecedores!.forEach((f) => {
          if (!f.fornecedorId) return;

          let grupo = fornecedoresPorId.get(f.fornecedorId);
          if (!grupo) {
            const completo = fornecedoresCompletos.find(
              (fc) => fc?.id === f.fornecedorId,
            );
            grupo = {
              id: f.fornecedorId,
              nome:
                completo?.nome ||
                f.fornecedor?.nome ||
                f.fornecedorNome ||
                "Fornecedor",
              email: emailPadraoDoFornecedor(completo, f.fornecedor),
              telefone: completo?.telefone || f.fornecedor?.telefone || "",
              itens: [],
            };
            fornecedoresPorId.set(f.fornecedorId, grupo);
          }

          const chave = item.id || item.numero;
          if (grupo.itens.some((i) => (i.itemId || i.numero) === chave)) return;

          grupo.itens.push({
            itemId: item.id,
            numero: item.numero || "",
            nome: item.item || item.descritivo || "",
            status:
              (f.status as StatusOrcamento) || StatusOrcamento.AGUARDANDO_ENVIO,
            pendente: !solicitacaoJaEnviada(f.status),
          });
        });
      });

      const fornecedoresList = Array.from(fornecedoresPorId.values())
        .filter((f) => f.itens.length > 0)
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

      fornecedoresList.forEach((f) =>
        f.itens.sort((a, b) =>
          a.numero.localeCompare(b.numero, "pt-BR", { numeric: true }),
        ),
      );

      if (fornecedoresList.length === 0) {
        showWarning(
          "Nenhum fornecedor foi adicionado aos itens ainda. Adicione fornecedores nos itens antes de enviar orçamentos.",
        );
        return;
      }

      setDadosModalAtual({
        fornecedores: fornecedoresList,
      });
      setModalFornecedorAberta(true);
    } catch (error) {
      showError(
        "Erro ao salvar a planilha. Salve manualmente antes de enviar orçamentos.",
      );
    }
  };

  // Enviar orçamento individual
  /**
   * Aplica o resultado do envio: status novo só para quem a API aceitou, e o
   * motivo na tela para quem ela recusou.
   *
   * A linha mudava para "enviada" antes da resposta. A API responde 200 mesmo
   * recusando fornecedor — CNPJ baixado, sem e-mail válido — e põe o motivo em
   * `resultados`, que a tela ignorava: dizia "enviada com sucesso", e o
   * salvamento seguinte gravava no banco o status de um envio que não houve.
   * Foi o que deixou um item como "reenviada" sem orçamento nenhum.
   *
   * `linhasComStatusNovo` é a planilha como ficaria se tudo fosse aceito; dela
   * sai só o status das linhas aceitas. Quem teve o orçamento criado e só o
   * e-mail falhou conta como aceito, porque o orçamento existe no banco.
   */
  const aplicarResultadoDoEnvio = (
    resposta: Awaited<ReturnType<typeof orcamentoService.enviarGranular>>,
    linhasComStatusNovo: ItemPlanilha[],
  ) => {
    const resultados = resposta?.resultados ?? [];
    const recusados = resultados.filter((r) => !r.success);
    const enviados = resultados.length - recusados.length;

    const registrados = new Set(
      resultados
        .filter((r) => (r.success || r.orcamentoId) && r.fornecedorId)
        .map((r) => r.fornecedorId as string),
    );
    if (registrados.size > 0) {
      const statusNovo = new Map(
        linhasComStatusNovo.map((l) => [l.id, l.status]),
      );
      setRowData((atual) =>
        atual.map((l) => {
          const novo = statusNovo.get(l.id);
          return l.tipo === TipoItem.FORNECEDOR &&
            l.fornecedorId &&
            registrados.has(l.fornecedorId) &&
            novo &&
            novo !== l.status
            ? applyStatusToFornecedor(l, novo)
            : l;
        }),
      );
    }

    if (recusados.length === 0) {
      showSuccess(
        enviados === 1
          ? "Solicitação de orçamento enviada com sucesso!"
          : `Solicitações enviadas para ${enviados} fornecedor(es)!`,
      );
      return;
    }

    const motivos = recusados
      .map(
        (r) =>
          `${r.fornecedorNome || "Fornecedor"}: ${r.message || "não foi enviado"}`,
      )
      .join(" | ");
    if (enviados > 0) {
      showWarning(
        `Enviada para ${enviados} fornecedor(es). Não enviada para ${recusados.length}: ${motivos}`,
      );
    } else {
      showError(`Solicitação não enviada. ${motivos}`);
    }
  };

  const enviarIndividual = async (email: string, telefone: string) => {
    if (!planilha || !dadosModalAtual) {
      showError("Recarregue a página e tente enviar novamente.");
      return;
    }

    // Validar se fornecedor foi selecionado
    if (!dadosModalAtual.fornecedorId || !dadosModalAtual.fornecedor) {
      showError(
        "Por favor, selecione um fornecedor antes de enviar o orçamento.",
      );
      return;
    }

    // Encontrar o item e construir os dados
    const itemRow = rowData.find(
      (r) => r.numero === dadosModalAtual.itemNumero,
    );
    // Antes exigia itemRow.item preenchido, e um item sem nome fazia o envio
    // desistir em silêncio: o modal fechava, nenhuma requisição saía e nada
    // era dito. O nome agora cai no descritivo, e o que resta de impeditivo
    // é explicado.
    if (!itemRow || !itemRow.numero) {
      showError(
        "Não foi possível localizar este item na planilha. Salve e tente de novo.",
      );
      return;
    }

    const fornecedores = [
      {
        fornecedor: {
          ...dadosModalAtual.fornecedor,
          email,
          telefone,
          fornecedorId: dadosModalAtual?.fornecedorId || "",
        },
        itens: [
          {
            id: itemRow.id,
            numero: itemRow.numero,
            item: nomeDoItemParaCotacao(itemRow),
            descritivo: itemRow.descritivo || "",
            quantidade: itemRow.quantidade,
            diaria: itemRow.diaria,
          },
        ],
        email,
        telefone,
      },
    ];
    // Atualizar status na linha do fornecedor
    const novoRowData = rowData.map((row) => {
      if (row.numero === dadosModalAtual.fornecedorNumero) {
        const newStatus =
          row?.status !== StatusOrcamento.AGUARDANDO_ENVIO
            ? StatusOrcamento.REENVIADA
            : StatusOrcamento.ENVIADA;
        return applyStatusToFornecedor(row, newStatus);
      }
      return row;
    });
    // O status só muda depois da resposta, e só para quem a API aceitou.
    // Ver aplicarResultadoDoEnvio.
    try {
      const resposta = await orcamentoService.enviarGranular(
        planilha.id,
        fornecedores,
      );
      aplicarResultadoDoEnvio(resposta, novoRowData);
    } catch (error) {
      console.error("Erro ao enviar orçamento:", error);
      showError("Erro ao enviar a solicitação de orçamento. Tente novamente.");
      throw error;
    }
  };

  // Enviar orçamento por item
  const enviarPorItem = async (
    fornecedoresSelecionadosRaw: Array<{
      id?: string;
      fornecedorId?: string;
      email: string;
      telefone: string;
    }>,
  ) => {
    if (!planilha || !dadosModalAtual) {
      showError("Recarregue a página e tente enviar novamente.");
      return;
    }

    // Encontrar o item
    const itemRow = rowData.find(
      (r) => r.numero === dadosModalAtual.itemNumero,
    );
    // Antes exigia itemRow.item preenchido, e um item sem nome fazia o envio
    // desistir em silêncio: o modal fechava, nenhuma requisição saía e nada
    // era dito. O nome agora cai no descritivo, e o que resta de impeditivo
    // é explicado.
    if (!itemRow || !itemRow.numero) {
      showError(
        "Não foi possível localizar este item na planilha. Salve e tente de novo.",
      );
      return;
    }

    const itemData = {
      id: itemRow.id,
      numero: itemRow.numero,
      item: nomeDoItemParaCotacao(itemRow),
      descritivo: itemRow.descritivo || "",
      quantidade: itemRow.quantidade,
      diaria: itemRow.diaria,
    };

    // Normalizar entrada: aceitar tanto { id } (ModalEnvioItem) quanto { fornecedorId }
    const fornecedoresSelecionados = fornecedoresSelecionadosRaw.map((f) => ({
      fornecedorId: f.fornecedorId ?? f.id ?? "",
      email: f.email,
      telefone: f.telefone,
    }));

    // Usar dados canonical dos fornecedores do item (planilha.itens)
    const itemCanonical = planilha.itens.find(
      (i) => i.numero === itemRow.numero,
    );
    const fornecedoresData = fornecedoresSelecionados.map((f) => {
      const canonical = itemCanonical?.fornecedores?.find(
        (cf) => cf.fornecedorId === f.fornecedorId,
      );
      const full = fornecedoresMap.get(f.fornecedorId);
      return {
        fornecedor: {
          fornecedorId: f.fornecedorId,
          nome:
            full?.nome ||
            canonical?.fornecedor?.nome ||
            canonical?.fornecedorNome ||
            "Fornecedor",
          email: f.email,
          telefone: f.telefone,
        },
        itens: [itemData],
        email: f.email,
        telefone: f.telefone,
      };
    });

    // Atualizar status de todos os fornecedores deste item
    // Atualizar status baseado em fornecedorId dos selecionados E do número do item
    const idsSelecionados = new Set(
      fornecedoresSelecionados.map((f) => f.fornecedorId),
    );
    const novoRowData = rowData.map((row) => {
      // Verificar se é fornecedor do ITEM ESPECÍFICO (começando com o número do item)
      if (
        row.tipo === TipoItem.FORNECEDOR &&
        row.fornecedorId &&
        idsSelecionados.has(row.fornecedorId) &&
        row.numero &&
        row.numero.startsWith(itemRow.numero + ".")
      ) {
        return applyStatusToFornecedor(row, StatusOrcamento.ENVIADA);
      }
      return row;
    });
    // O status só muda depois da resposta, e só para quem a API aceitou.
    // Ver aplicarResultadoDoEnvio.
    try {
      const resposta = await orcamentoService.enviarGranular(
        planilha.id,
        fornecedoresData,
      );
      aplicarResultadoDoEnvio(resposta, novoRowData);
    } catch (error) {
      console.error("Erro ao enviar orçamentos:", error);
      showError(
        "Erro ao enviar as solicitações de orçamento. Tente novamente.",
      );
      throw error;
    }
  };

  // Enviar orçamento por fornecedor (envio em bloco, N fornecedores)
  const enviarPorFornecedor = async (
    fornecedoresData: Array<{
      fornecedorId: string;
      email: string;
      telefone: string;
      modo: ModoEnvioFornecedor;
    }>,
  ) => {
    if (!planilha || !dadosModalAtual) {
      showError("Recarregue a página e tente enviar novamente.");
      return;
    }

    if (!planilha.id || !id) {
      showWarning(
        "Salve a planilha antes de enviar solicitações de orçamento.",
      );
      return;
    }

    const fornecedoresParaEnviar: Array<{
      fornecedor: {
        fornecedorId: string;
        nome: string;
        email: string;
        telefone: string;
      };
      itens: Array<{
        id?: string;
        numero: string;
        item: string;
        descritivo: string;
        quantidade?: number;
        diaria?: number;
      }>;
      email: string;
      telefone: string;
    }> = [];

    // Itens que cada fornecedor recebe, conforme o modo escolhido no modal.
    // Em "todos" a lista completa faz a API reaproveitar o orçamento existente
    // (mesmo link, mesmo PDF) e reabrir cada item para nova cotação; em
    // "pendentes" só vai o que nunca saiu, e a API abre um orçamento à parte.
    const itensEnviadosPorFornecedor = new Map<string, Set<string>>();

    for (const fornData of fornecedoresData) {
      const itensDoFornecedor = planilha.itens.filter((i) => {
        if (i.tipo !== TipoItem.ITEM) return false;
        const vinculo = i.fornecedores?.find(
          (f) => f.fornecedorId === fornData.fornecedorId,
        );
        if (!vinculo) return false;
        return (
          fornData.modo === "todos" || !solicitacaoJaEnviada(vinculo.status)
        );
      });
      if (itensDoFornecedor.length === 0) continue;

      const itens = itensDoFornecedor.map((item) => ({
        id: item.id,
        numero: item.numero!,
        item: nomeDoItemParaCotacao(item),
        descritivo: item.descritivo || "",
        quantidade: item.quantidade,
        diaria: item.diaria,
      }));

      const full = fornecedoresMap.get(fornData.fornecedorId);
      const doModal = (
        dadosModalAtual.fornecedores as FornecedorEnvioBloco[] | undefined
      )?.find((f) => f.id === fornData.fornecedorId);
      fornecedoresParaEnviar.push({
        fornecedor: {
          fornecedorId: fornData.fornecedorId,
          nome: full?.nome || doModal?.nome || "Fornecedor",
          email: fornData.email,
          telefone: fornData.telefone,
        },
        itens,
        email: fornData.email,
        telefone: fornData.telefone,
      });

      itensEnviadosPorFornecedor.set(
        fornData.fornecedorId,
        new Set(
          itensDoFornecedor
            .map((item) => item.numero)
            .filter((n): n is string => !!n),
        ),
      );
    }

    if (fornecedoresParaEnviar.length === 0) {
      showWarning("Nenhum item para enviar aos fornecedores selecionados.");
      return;
    }

    // Atualizar status apenas das linhas dos itens efetivamente enviados.
    // Quem já havia recebido o pedido vira "reenviada", espelhando o que a API
    // grava; quem nunca recebeu vira "enviada".
    const novoRowData = rowData.map((row) => {
      if (
        row.tipo !== TipoItem.FORNECEDOR ||
        !row.fornecedorId ||
        !row.numero
      ) {
        return row;
      }

      const itensDoFornecedor = itensEnviadosPorFornecedor.get(
        row.fornecedorId,
      );
      if (!itensDoFornecedor) return row;

      // Extrair número do item (ex: "2.5.1" -> "2.5")
      const numeroItem = row.numero.split(".").slice(0, 2).join(".");
      if (!itensDoFornecedor.has(numeroItem)) return row;

      return applyStatusToFornecedor(
        row,
        solicitacaoJaEnviada(row.status)
          ? StatusOrcamento.REENVIADA
          : StatusOrcamento.ENVIADA,
      );
    });
    // O status só muda depois da resposta, e só para quem a API aceitou.
    // Ver aplicarResultadoDoEnvio.
    try {
      const resposta = await orcamentoService.enviarGranular(
        planilha.id,
        fornecedoresParaEnviar,
      );
      aplicarResultadoDoEnvio(resposta, novoRowData);
    } catch (error) {
      showError(
        "Erro ao enviar as solicitações de orçamento. Tente novamente.",
      );
      throw error;
    }
  };

  // Função auxiliar para aplicar tipo correto aos itens de TAXAS E SEGUROS
  const aplicarTipoItemTaxas = (itens: ItemPlanilha[]): ItemPlanilha[] => {
    // Item com fornecedor abaixo fica como está, pela mesma razão de
    // aplicarTipoItemTransporte: ITEM_TAXAS não comporta fornecedor, e o
    // salvamento apagaria os vínculos com valor cotado e PDF. Acontece
    // quando a categoria de um bloco já cotado é trocada para TAXAS.
    const numerosTaxasESeguros = numerosDosItensDeTaxas(
      itens,
      (row, i) =>
        row.tipo === TipoItem.ITEM &&
        itens[i + 1]?.tipo !== TipoItem.FORNECEDOR,
    );

    // Aplicar tipo ITEM_TAXAS aos itens que pertencem ao subgrupo
    return itens.map((item) => {
      if (
        item.tipo === TipoItem.ITEM &&
        item.numero &&
        numerosTaxasESeguros.has(item.numero)
      ) {
        return { ...item, tipo: TipoItem.ITEM_TAXAS };
      }
      return item;
    });
  };

  /**
   * Converte para ITEM_TRANSPORTE as linhas amarelas do subgrupo TRANSPORTES.
   *
   * O tipo já é definido na criação, mas planilha montada antes desta regra
   * tem essas linhas gravadas como ITEM comum — e sem a conversão elas
   * continuariam pedindo cotação em vez de aceitar anexo.
   */
  const aplicarTipoItemTransporte = (itens: ItemPlanilha[]): ItemPlanilha[] => {
    const resultado = [...itens];
    let dentroDeTransportes = false;

    for (let i = 0; i < resultado.length; i++) {
      const row = resultado[i];

      if (row.tipo === TipoItem.SUBGRUPO || row.tipo === TipoItem.GRUPO) {
        dentroDeTransportes =
          row.tipo === TipoItem.SUBGRUPO &&
          !!row.item?.toUpperCase().includes("TRANSPORTE");
        continue;
      }

      if (dentroDeTransportes && row.tipo === TipoItem.ITEM) {
        // Item com fornecedor abaixo fica como está. TRANSPORTES existia como
        // bloco comum antes de ganhar tipo próprio, e planilha antiga tem item
        // cotado por fornecedor ali dentro. Convertê-lo trocava a linha por um
        // tipo que não comporta fornecedor: as linhas sumiam da tela e, como o
        // salvamento apaga vínculo ausente do payload, sumiam do banco junto
        // com o valor cotado e o PDF.
        const temFornecedor = resultado[i + 1]?.tipo === TipoItem.FORNECEDOR;
        if (!temFornecedor) {
          resultado[i] = { ...row, tipo: TipoItem.ITEM_TRANSPORTE };
        }
      }
    }

    return resultado;
  };

  // Função para enviar planilha para o Wrike
  // Gatilho de status disparado pelo cliente. A regra de "só avança" fica no
  // servidor (automatico: true), então aqui é só avisar e refletir o que ele
  // decidiu. Falha não interrompe nada: o usuário já recebeu o pacote, e o
  // status é acompanhamento — o seletor da listagem resolve na mão.
  const avancarStatus = async (novo: PlanilhaStatus) => {
    if (!id) return;
    try {
      const { status } = await planilhaService.atualizarStatus(id, novo, {
        automatico: true,
      });
      setPlanilha((atual) => (atual ? { ...atual, status } : atual));
    } catch (err) {
      console.error("Falha ao atualizar o status da planilha:", err);
    }
  };

  const handleEnviarParaWrike = async () => {
    if (!id || !planilha) {
      showError("Planilha não encontrada");
      return;
    }

    const confirmed = await confirm({
      title: "Enviar para o Wrike",
      message:
        "Deseja enviar esta planilha para o Wrike? Esta ação criará/atualizará a Ordem de Compra no sistema.",
      confirmText: "Enviar",
      cancelText: "Cancelar",
      tone: "default",
    });

    if (!confirmed) return;

    setIsEnviandoWrike(true);
    setWrikeResult(null);

    try {
      // Salvar a planilha antes de enviar ao Wrike
      await handleSave();

      const response = await apiClient.post(`/planilhas/${id}/wrike/oc`, {});

      if (response.data.success) {
        // O backend avisa quando algum item foi criado no Wrike sem todos os
        // orçamentos anexados — esses itens continuam pendentes para reenvio.
        const avisoAnexos: string | null =
          response.data.data?.avisoAnexos ??
          response.data.data?.AvisoAnexos ??
          null;

        setWrikeResult({
          success: !avisoAnexos,
          message:
            avisoAnexos ||
            response.data.message ||
            "Planilha enviada para o Wrike com sucesso!",
        });

        if (avisoAnexos) {
          showWarning(avisoAnexos);
        } else {
          showSuccess("Planilha enviada para o Wrike com sucesso!");
        }

        // Recarregar planilha para atualizar o número da OC
        const planilhaAtualizada = await planilhaService.getById(id);
        if (planilhaAtualizada) {
          setPlanilha(planilhaAtualizada);
        }
      } else {
        setWrikeResult({
          success: false,
          message:
            response.data.message || "Erro ao enviar planilha para o Wrike",
        });
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Erro ao enviar planilha para o Wrike";
      setWrikeResult({
        success: false,
        message: errorMessage,
      });
      showError(errorMessage);
    } finally {
      setIsEnviandoWrike(false);
    }
  };

  /**
   * Confirmação da invalidação: o usuário viu no modal exatamente o que será
   * apagado e aceitou. Repete o salvamento com a flag que autoriza o backend a
   * limpar valores, desvincular os documentos e resetar toda a linha.
   */
  const confirmarInvalidacaoCotacoes = async () => {
    setItensAfetadosInvalidacao(null);
    await handleSave(true);
  };

  const handleSave = async (confirmarResetStatus: boolean = false) => {
    if (!id || !planilha) return;

    // Trava de somente leitura aqui, e não só nos botões. Vários fluxos salvam
    // por conta própria — anexar arquivo, abrir o modal de cotação — e cada
    // novo ponto desses teria de lembrar de checar. Sem a trava, quem perdeu o
    // lock ainda conseguia gravar por cima de quem está editando.
    if (isReadOnly) {
      showWarning(
        lockedBy
          ? `A planilha está sendo editada por ${lockedBy}. Suas alterações não foram salvas.`
          : "A planilha está em modo somente leitura. Suas alterações não foram salvas.",
      );
      return null;
    }

    salvamentoAguardandoConfirmacaoRef.current = false;
    setIsSaving(true);
    setSaveErrored(false);

    try {
      // Aplicar tipo correto aos itens de TAXAS E SEGUROS e TRANSPORTES
      const itensComTipoCorreto = aplicarTipoItemTransporte(
        aplicarTipoItemTaxas(rowData || []),
      );

      // Reordenar itens: atribuir ordem sequencial baseada na posição atual no array
      const itensComOrdem = itensComTipoCorreto.map((item, index) => ({
        ...item,
        ordem: index + 1, // Ordem começa em 1
      }));

      // O backend identifica cada linha por `itemId` (e `itemFornecedorId` nas linhas
      // de fornecedor). Sem esses campos ele cai no fallback por `numero` — que é
      // reatribuído por POSIÇÃO em renumerarItens. Bastava inserir ou remover um item
      // no ITEM 2 para uma linha passar a sobrescrever o registro de outra, herdando
      // os fornecedores e orçamentos dele (e, na pior hipótese, apagando o registro
      // que ficou sem par, junto com seus orçamentos em cascata).

      let ultimoItemPaiId: string | undefined;
      const itensParaSalvar = itensComOrdem.map((row) => {
        if (row.tipo === TipoItem.FORNECEDOR) {
          // Aqui `row.id` é o vínculo item_fornecedor; o item pai é a última
          // linha de ITEM/ITEM_TAXAS vista acima desta.
          return {
            ...row,
            itemId: ultimoItemPaiId,
            itemFornecedorId: idDoBanco(row.id),
          };
        }

        if (
          row.tipo === TipoItem.ITEM ||
          TIPOS_ITEM_SEM_COTACAO.includes(row.tipo)
        ) {
          ultimoItemPaiId = idDoBanco(row.id);
        } else if (
          row.tipo === TipoItem.GRUPO ||
          row.tipo === TipoItem.SUBGRUPO
        ) {
          // Fora de um item: não deixar uma linha de fornecedor órfã se prender
          // a um item distante. Sem itemId o backend usa o fallback por número.
          ultimoItemPaiId = undefined;
        }

        return { ...row, itemId: idDoBanco(row.id) };
      });

      // `status` fica fora de propósito: quem manda nele é o seletor da
      // listagem e os gatilhos (Wrike, FACITE), pelo endpoint próprio. Enviar
      // aqui o valor carregado no início da edição faria um salvamento tardio
      // desfazer o avanço que já havia sido gravado.
      //
      // Link PIT e OC ficam fora pelo mesmo motivo: mudam pelo Editar da
      // listagem e pelo envio ao Wrike. Um valor antigo daqui desfaria a
      // troca de link — e a API trataria a volta como nova troca de projeto.
      const {
        status: _statusIgnorado,
        linkProjeto: _linkProjetoIgnorado,
        pit: _pitIgnorado,
        numeroOC: _numeroOCIgnorado,
        linkWrike: _linkWrikeIgnorado,
        ...planilhaSemStatus
      } = planilha;

      const dadosParaSalvar = {
        ...planilhaSemStatus,
        itens: itensParaSalvar,
        confirmarResetStatus,
        // Sempre presente, mesmo vazio: é a lista vazia que diz ao servidor
        // "este cliente sabe declarar remoções". Ausente, ele volta à regra
        // antiga de apagar tudo que faltar — que é o que precisamos evitar.
        fornecedoresRemovidos: Array.from(fornecedoresRemovidosRef.current),
        // O valor gravado é sempre o que está na tela: com percentual
        // preenchido ele é derivado da base, sem percentual é o número
        // digitado. Assim o Excel e o FACITE, que leem só o valor, continuam
        // recebendo exatamente o que o usuário viu.
        breakdown: {
          equipamentos: valorBreakdown("equipamentos"),
          materiais: valorBreakdown("materiais"),
          servicos: valorBreakdown("servicos"),
          percentEquipamentos: percentBreakdown("equipamentos"),
          percentMateriais: percentBreakdown("materiais"),
          percentServicos: percentBreakdown("servicos"),
        },
      };

      // Se não for uma confirmação, verificar alterações primeiro
      if (!confirmarResetStatus) {
        const verificacao = await planilhaService.verificarAlteracoes(
          id,
          dadosParaSalvar,
        );

        if (verificacao.requiresConfirmation) {
          // Entrega a decisão ao modal dedicado, que mostra item a item o que
          // será apagado. O fluxo continua em confirmarInvalidacaoCotacoes.
          salvamentoAguardandoConfirmacaoRef.current = true;
          setIsSaving(false);
          setItensAfetadosInvalidacao(verificacao.itensAfetados);
          return null;
        }
      }

      // Salvar planilha
      const planilhaAtualizada = await planilhaService.update(
        id,
        dadosParaSalvar,
      );

      // A remoção foi gravada; a lista some. Se o salvamento tivesse falhado,
      // ela permaneceria para a próxima tentativa.
      fornecedoresRemovidosRef.current.clear();

      // Atualizar estado local com dados retornados do servidor (incluindo IDs)
      setPlanilha(planilhaAtualizada);

      // Atualizar rowData com os itens retornados (que agora têm IDs)
      if (planilhaAtualizada.itens && planilhaAtualizada.itens.length > 0) {
        registrarIdsDoServidor(planilhaAtualizada.itens);

        const itensSemLegado = removerLinhasFornecedorLegadas(
          planilhaAtualizada.itens,
        );
        const itensExpand = expandItensComFornecedores(
          itensSemLegado,
          fornecedoresMap,
        );
        const itensCorrigidos = corrigirNumerosFornecedores(itensExpand);
        // Reorganizar ITEM 3 após salvar para garantir ordem correta
        const itensReorganizados = renumerarItem3(itensCorrigidos);
        // Renumerar itens do ITEM 2 com formato de dois dígitos
        const itensRenumerados = renumerarItens(itensReorganizados);
        const itensRecalculados = recalcularTotaisEMenorPreco(itensRenumerados);
        const itensComIds = itensRecalculados.map((item) =>
          item.id ? item : { ...item, id: crypto.randomUUID() },
        );
        setRowData(itensComIds);
        // Novo baseline após salvar — zera o estado de "alterações não salvas".
        savedSnapshotRef.current = buildEditorSignature(
          itensComIds,
          breakdownEquipamentos,
          breakdownMateriais,
          breakdownServicos,
          breakdownPercents,
        );
      } else {
        // Sem itens no retorno: rebaselinar a partir do estado atual.
        savedSnapshotRef.current = buildEditorSignature(
          rowData,
          breakdownEquipamentos,
          breakdownMateriais,
          breakdownServicos,
          breakdownPercents,
        );
      }

      setLastSavedAt(new Date());
      showSuccess("Planilha salva com sucesso!");
      return planilhaAtualizada;
    } catch (err) {
      setError("Erro ao salvar planilha");
      setSaveErrored(true);
      console.error(err);
      showError("Não foi possível salvar a planilha. Tente novamente.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Alterações não salvas (item 1.4) + atalhos de teclado (item 1.2) ───
  const currentSignature = useMemo(
    () =>
      buildEditorSignature(
        rowData,
        breakdownEquipamentos,
        breakdownMateriais,
        breakdownServicos,
        breakdownPercents,
      ),
    [
      rowData,
      breakdownEquipamentos,
      breakdownMateriais,
      breakdownServicos,
      breakdownPercents,
    ],
  );

  const dirty =
    !loading &&
    !isReadOnly &&
    savedSnapshotRef.current !== "" &&
    currentSignature !== savedSnapshotRef.current;

  // Reportar estado "sujo" ao guarda global de navegação (Header, Voltar, aba).
  useEffect(() => {
    setDirty(dirty);
  }, [dirty, setDirty]);

  // Ao desmontar o editor, garantir que o guarda não fique preso em "sujo"
  // e que o título da aba volte ao normal (mesmo saindo com alterações pendentes).
  useEffect(() => {
    return () => {
      setDirty(false);
      document.title = TITULO_BASE;
    };
  }, [setDirty]);

  // Indicador visual no título da aba do navegador.
  useEffect(() => {
    document.title = dirty
      ? `● Editando — ${planilha?.evento ?? "Planilha"}`
      : TITULO_BASE;
    return () => {
      document.title = TITULO_BASE;
    };
  }, [dirty, planilha?.evento]);

  // Mede a altura da barra fixa de ações e reserva esse espaço no fim do grid.
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const medir = () => setFooterHeight(el.offsetHeight);
    medir();
    const observer = new ResizeObserver(medir);
    observer.observe(el);
    window.addEventListener("resize", medir);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", medir);
    };
  }, [loading]);

  // Conjunto seguro de atalhos. Array recriado a cada render de propósito: o
  // hook o mantém via ref, evitando closure obsoleto do handleSave (dados stale).
  const shortcutDefs: ShortcutDef[] = [
    {
      combo: "mod+s",
      allowInInput: true,
      group: "Geral",
      description: "Salvar planilha",
      handler: (e) => {
        e.preventDefault();
        if (!isSaving && !isReadOnly) handleSave();
      },
    },
    {
      combo: "esc",
      group: "Geral",
      description: "Fechar a janela/modal aberto",
      // Fechamento tratado dentro de cada modal (ModalBase/Modal); listado aqui só para a ajuda.
      handler: () => {},
    },
    {
      combo: "?",
      group: "Ajuda",
      description: "Mostrar os atalhos de teclado",
      handler: () => setShortcutsModalAberto(true),
    },
  ];
  useKeyboardShortcuts(shortcutDefs, { enabled: !loading });

  // Interface para totais da planilha
  interface TotaisPlanilha {
    subtotal: number;
    totalServicosEssenciais: number;
    totalServicosComplementares: number;
    /** Parcela de complementares que está no subgrupo TAXAS E SEGUROS. */
    totalTaxasESeguros: number;
    impostos: number;
    totalHonorarios: number;
    taxaAdministracao: number;
    totalValorAS: number;
    totalDespesasAcessorias: number;
    totalGeralEvento: number;
  }

  // Funções de cálculo para os totais
  const calcularTotais = (): TotaisPlanilha => {
    let subtotal = 0;
    let totalServicosComplementares = 0;
    let totalDespesasAcessorias = 0;
    let totalTaxasESeguros = 0;

    // Itens amarelos sob TAXAS E SEGUROS, em qualquer linha preta com esse
    // nome. Contar só a primeira deixava fora a categoria duplicada ou trocada
    // para TAXAS, e o valor dela pagava honorário no lugar de taxa.
    const numerosTaxasESeguros = numerosDosItensDeTaxas(rowData, (row) =>
      row.tipo === TipoItem.ITEM || row.tipo === TipoItem.ITEM_TAXAS,
    );

    rowData.forEach((row) => {
      // Subtotal geral: apenas linhas amarelas (ITEM, ITEM_TAXAS e ITEM_ACESSORIA)
      // Não somar DESCRITIVO_ACESSORIA pois já está somado em ITEM_ACESSORIA
      if (
        (row.tipo === TipoItem.ITEM ||
          row.tipo === TipoItem.ITEM_TAXAS ||
          row.tipo === TipoItem.ITEM_ACESSORIA) &&
        row.total
      ) {
        subtotal += row.total;
      }

      // Total de Serviços Complementares (ITEM 2), incluindo TAXAS E SEGUROS
      if (
        (row.tipo === TipoItem.ITEM ||
          TIPOS_ITEM_SEM_COTACAO.includes(row.tipo)) &&
        row.numero?.startsWith("2.") &&
        row.total
      ) {
        totalServicosComplementares += row.total;

        // A parcela de Taxas e Seguros é decidida pela numeração do subgrupo,
        // e não pelo tipo da linha. Antes qualquer ITEM_TAXAS entrava aqui, e
        // com TRANSPORTES usando a mesma mecânica de valor manual o transporte
        // seria somado como taxa — inflando a taxa de administração (6%) e
        // encolhendo os honorários (do cliente), que incidem sobre o que sobra.
        if (numerosTaxasESeguros.has(row.numero)) {
          totalTaxasESeguros += row.total;
        }
      }

      // Total de Despesas Acessórias (ITEM 3)
      // Apenas linhas amarelas (ITEM_ACESSORIA), não as brancas (DESCRITIVO_ACESSORIA)
      if (row.tipo === TipoItem.ITEM_ACESSORIA && row.total) {
        totalDespesasAcessorias += row.total;
      }
    });

    // Cálculos derivados
    const totalServicosEssenciais = 0; // Sempre 0,00 conforme requisito
    // Imposto por dentro: a alíquota incide sobre o valor com imposto, não
    // sobre o custo. Ver calcularImpostoPorDentro.
    const impostos = calcularImpostoPorDentro(totalServicosComplementares);
    const totalHonorarios =
      (totalServicosComplementares - totalTaxasESeguros) *
      taxaDeHonorarios(planilha);
    const taxaAdministracao = totalTaxasESeguros * PERCENT_TAXA_ADMINISTRACAO;
    const totalValorAS =
      totalServicosComplementares +
      impostos +
      totalHonorarios +
      taxaAdministracao;
    const totalGeralEvento = totalValorAS + totalDespesasAcessorias;

    return {
      subtotal,
      totalServicosEssenciais,
      totalServicosComplementares,
      totalTaxasESeguros,
      impostos,
      totalHonorarios,
      taxaAdministracao,
      totalValorAS,
      totalDespesasAcessorias,
      totalGeralEvento,
    };
  };

  /** Valor em reais de uma faixa do rateio, com a base atual. */
  const valorBreakdown = (faixa: FaixaBreakdown): number => {
    const absolutos: Record<FaixaBreakdown, string> = {
      equipamentos: breakdownEquipamentos,
      materiais: breakdownMateriais,
      servicos: breakdownServicos,
    };
    return valorDaFaixa(
      breakdownPercents[faixa],
      absolutos[faixa],
      calcularTotais().totalServicosComplementares,
    );
  };

  /** Percentual gravado da faixa, ou null quando ela está em valor fixo. */
  const percentBreakdown = (faixa: FaixaBreakdown): number | null => {
    const pct = parseFloat(breakdownPercents[faixa]);
    return breakdownPercents[faixa] !== "" && !Number.isNaN(pct) ? pct : null;
  };

  /**
   * Digitar o percentual põe a faixa em modo proporcional: o valor passa a ser
   * derivado e acompanha a base. Apagar o percentual devolve o controle ao
   * valor digitado.
   */
  const handlePercentBreakdown = (faixa: FaixaBreakdown, valor: string) => {
    setBreakdownPercents((prev) => ({ ...prev, [faixa]: valor }));
  };

  /**
   * Digitar o valor em reais fixa a faixa nesse número e limpa o percentual.
   * É o "último manda" na prática: sem percentual não há o que recalcular, e
   * o valor deixa de se mover quando as cotações mudam a base.
   */
  const handleValorBreakdown = (faixa: FaixaBreakdown, valor: string) => {
    const setters: Record<FaixaBreakdown, (v: string) => void> = {
      equipamentos: setBreakdownEquipamentos,
      materiais: setBreakdownMateriais,
      servicos: setBreakdownServicos,
    };
    setters[faixa](valor);
    setBreakdownPercents((prev) => ({ ...prev, [faixa]: "" }));
  };

  if (loading || lockLoading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <span className="loading loading-spinner loading-lg text-primary"></span>
        <span className="ml-4 text-base-content/70">
          {lockLoading
            ? "Verificando disponibilidade..."
            : "Carregando planilha..."}
        </span>
      </div>
    );
  if (!planilha)
    return (
      <div className="alert alert-error m-8">
        <FiXCircle className="shrink-0 h-6 w-6" />
        <span>Planilha não encontrada</span>
      </div>
    );

  return (
    <div
      className={`flex flex-col min-h-screen pb-20${isReadOnly ? " pt-10" : ""}`}
    >
      {/* Loading Overlay - aparece ao salvar */}
      {isSaving && (
        <div className="fixed inset-0 bg-black/50 z-9999 flex items-center justify-center">
          <div className="bg-base-100 rounded-lg p-8 flex flex-col items-center gap-4 shadow-2xl">
            <span className="loading loading-spinner loading-lg text-primary"></span>

            <span className="text-sm text-base-content/70">
              Por favor, aguarde
            </span>
          </div>
        </div>
      )}

      {/* Loading Overlay - Enviando para o Wrike */}
      {isEnviandoWrike && (
        <div className="fixed inset-0 bg-black/50 z-9999 flex items-center justify-center">
          <div className="bg-base-100 rounded-lg p-8 flex flex-col items-center gap-4 shadow-2xl">
            <span className="loading loading-spinner loading-lg text-info"></span>
            <span className="text-lg font-semibold">
              Enviando para o Wrike...
            </span>
            <span className="text-sm text-base-content/70">
              Por favor, aguarde enquanto processamos sua solicitação
            </span>
          </div>
        </div>
      )}

      {/* Modal de Resultado do Wrike */}
      {wrikeResult && (
        <div className="fixed inset-0 bg-black/50 z-9999 flex items-center justify-center">
          <div className="bg-base-100 rounded-lg p-8 flex flex-col items-center gap-6 shadow-2xl max-w-md">
            {wrikeResult.success ? (
              <>
                <FiCheckCircle className="h-16 w-16 text-success" />
                <h3 className="text-xl font-bold text-success">Sucesso!</h3>
              </>
            ) : (
              <>
                <FiXCircle className="h-16 w-16 text-error" />
                <h3 className="text-xl font-bold text-error">Erro</h3>
              </>
            )}
            <p className="text-center text-base-content/80">
              {wrikeResult.message}
            </p>
            <button
              className="btn btn-primary btn-wide"
              onClick={() => setWrikeResult(null)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Banner de Lock — somente leitura */}
      {somenteConsulta ? (
        <div className="fixed top-0 left-0 right-0 z-50 bg-sky-600 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium shadow-lg">
          <FiEye className="h-4 w-4 shrink-0" />
          <span>
            Perfil <strong>Visualizador</strong>: você pode consultar,
            exportar e abrir os PDFs, mas não alterar a planilha.
          </span>
        </div>
      ) : (
        isReadOnly && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium shadow-lg">
            <FiLock className="h-4 w-4 shrink-0" />
            <span>
              Esta planilha está sendo editada por <strong>{lockedBy}</strong>{" "}
              — modo somente leitura. Suas alterações não serão salvas.
            </span>
          </div>
        )
      )}

      {/* Overlay invisível que bloqueia interações com a tabela quando em
          read-only por lock. O Visualizador não recebe o overlay: ele precisa
          rolar, abrir PDFs e o histórico; os campos são travados por CSS e
          pelo bloqueio de digitação, e os botões de alteração somem. */}
      {travadaPorOutro && !somenteConsulta && (
        <div
          className="fixed inset-0 z-30 cursor-not-allowed"
          style={{ top: "36px", bottom: "72px" }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          title="Planilha em modo somente leitura"
        />
      )}

      {/* Modal de Histórico */}
      {historicoModalAberto && id && (
        <PlanilhaHistoricoModal
          planilhaId={id}
          onClose={() => setHistoricoModalAberto(false)}
        />
      )}

      {/* Modal de Atalhos de Teclado (item 1.2) */}
      <ShortcutsHelpModal
        isOpen={shortcutsModalAberto}
        onClose={() => setShortcutsModalAberto(false)}
        shortcuts={shortcutDefs}
      />

      {/* Header */}
      <div className="text-base-content md:px-5 py-5">
        <h1 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <FiBarChart2 className="h-6 w-6" />
          Cotação
        </h1>
        <div
          className="flex flex-wrap justify-between gap-5 mt-4 items-center"
          id="nav-acoes"
        >
          <div className="flex flex-wrap gap-5">
            <div className="bg-white/10 p-3 rounded-lg">
              <label className="text-xs opacity-90 uppercase tracking-wider block mb-2">
                Cliente
              </label>
              <div className="text-base font-semibold">
                {planilha.clienteNome}
              </div>
            </div>
            <div className="bg-white/10 p-3 rounded-lg">
              <label className="text-xs opacity-90 uppercase tracking-wider block mb-2">
                Evento/Ação
              </label>
              <div className="text-base font-semibold">{planilha.evento}</div>
            </div>
            <div className="bg-white/10 p-3 rounded-lg">
              <label className="text-xs opacity-90 uppercase tracking-wider block mb-2">
                Data do Evento
              </label>
              <div className="text-base font-semibold">
                {planilha.dataInicio
                  ? formatPeriodoEvento(planilha.dataInicio, planilha.dataFim)
                  : planilha.data
                    ? formatDateBR(planilha.data)
                    : "-"}
              </div>
            </div>
            {planilha.localEvento && (
              <div className="bg-white/10 p-3 rounded-lg">
                <label className="text-xs opacity-90 uppercase tracking-wider block mb-2">
                  Cidade
                </label>
                <div className="text-base font-semibold">
                  {planilha.localEvento}
                </div>
              </div>
            )}
            {planilha.ufEvento && (
              <div className="bg-white/10 p-3 rounded-lg">
                <label className="text-xs opacity-90 uppercase tracking-wider block mb-2">
                  UF
                </label>
                <div className="text-base font-semibold">
                  {planilha.ufEvento}
                </div>
              </div>
            )}
            <div className="bg-white/10 p-3 rounded-lg">
              <label className="text-xs opacity-90 uppercase tracking-wider block mb-2">
                PIT
              </label>
              <div className="text-base font-semibold">
                {planilha.pit || "-"}
              </div>
            </div>
            <div className="bg-white/10 p-3 rounded-lg">
              <label className="text-xs opacity-90 uppercase tracking-wider block mb-2">
                Nº OC
              </label>
              <div className="text-base font-semibold">
                {planilha.numeroOC || "-"}
              </div>
            </div>
          </div>
          <div className="flex flex-1 gap-2 justify-end">
            {!hasItem3 && (
              <button
                data-altera
                className="btn btn-neutral btn-sm text-white"
                onClick={abrirModalDespesas}
                title="Adicionar Despesas Acessórias"
              >
                <FiPlus className="h-4 w-4" />
                Despesas Acessórias
              </button>
            )}
            {/* {hasItem3 && (
              <button
                className="btn btn-error btn-sm"
                onClick={handleRemoveItem3}
                title="Remover Despesas Acessórias"
              >
                <FiTrash2 className="h-4 w-4" />
                Remover Despesas
              </button>
            )} */}
            <button
              className="btn btn-neutral btn-sm text-white"
              onClick={() => setModalExportarAberto(true)}
            >
              <FiDownload className="h-4 w-4" />
              Exportar Excel
            </button>
            <button
              data-altera
              className="btn btn-warning btn-sm text-white bg-orange-400 border-orange-400 hover:bg-orange-500 hover:border-orange-500"
              onClick={abrirModalFornecedor}
            >
              <FiSend className="h-4 w-4" />
              Solicitar cotação em blocos
            </button>
            <button
              data-altera
              className="btn btn-neutral btn-sm text-white"
              onClick={handleEnviarParaWrike}
              disabled={isEnviandoWrike}
            >
              <FiFileText className="h-4 w-4" />
              Enviar para o Wrike
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={refreshFornecedoresManual}
              disabled={isRefreshing}
              title="Busca as respostas mais recentes dos fornecedores sem sobrescrever suas edições"
            >
              <FiRefreshCw
                className={`h-4 w-4${isRefreshing ? " animate-spin" : ""}`}
              />
              {isRefreshing ? "Atualizando..." : "Atualizar Respostas"}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error m-4">
          <FiXCircle className="shrink-0 h-6 w-6" />
          <span>{error}</span>
        </div>
      )}

      {/* Sticky Header - aparece quando scroll passa do thead original */}
      {showStickyHeader && (
        <div
          ref={stickyHeaderRef}
          className="fixed top-0 left-0 right-0 z-50 bg-base-100 "
          style={{ paddingLeft: "20px", paddingRight: "20px" }}
        >
          <table
            className="table-custom w-full"
            style={{ tableLayout: "fixed" }}
          >
            <thead>
              <tr>
                {[
                  "Ações",
                  "Nº",
                  "PRODUTOS E SERVIÇOS",
                  "DESCRITIVO",
                  "Qtde",
                  "Diária",
                  "Unitário (R$)",
                  "Sub-Totais (R$)",
                  "Status",
                  "OBSERVAÇÕES",
                ].map((header, index) => (
                  <th
                    key={index}
                    style={{
                      width: headerWidths[index]
                        ? `${headerWidths[index]}px`
                        : "auto",
                      minWidth: headerWidths[index]
                        ? `${headerWidths[index]}px`
                        : "auto",
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      )}

      {/* Barra de Navegação Rápida */}
      <div className="sticky top-0 z-40 bg-base-100 border-b border-base-300 px-5 py-2 flex flex-wrap gap-2">
        {[
          { id: "nav-essenciais", label: "Serviços Essenciais" },
          { id: "nav-complementares", label: "Complementares" },
          { id: "nav-taxas", label: "Taxas e Seguros" },
          { id: "nav-acessorias", label: "Despesas Acessórias" },
          { id: "nav-acoes", label: "Ações" },
        ].map(({ id, label }) => (
          <button
            key={id}
            className="btn btn-xs btn-ghost border border-base-300 hover:border-primary hover:text-primary"
            onClick={() => {
              const el = document.getElementById(id);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div
        ref={gradeRef}
        className="grade-planilha flex-1 overflow-auto rounded-lg border border-base-300 px-5 pt-3"
        style={{ paddingBottom: footerHeight + 24 }}
      >
        <table className="table-custom w-full">
          <thead ref={theadRef}>
            <tr>
              <th>Ações</th>
              <th style={{ width: "50px", minWidth: "50px" }}>Nº</th>
              <th>PRODUTOS E SERVIÇOS</th>
              <th style={{ width: "350px", minWidth: "350px" }}>DESCRITIVO</th>
              <th style={{ width: "80px", minWidth: "80px" }}>Qtde</th>
              <th style={{ width: "80px", minWidth: "80px" }}>Diária</th>
              <th style={{ width: "130px", minWidth: "130px" }}>
                Unitário (R$)
              </th>
              <th>Sub-Totais (R$)</th>
              <th style={{ width: "170px", minWidth: "170px" }}>Status</th>
              <th style={{ width: "200px", minWidth: "200px" }}>OBSERVAÇÕES</th>
            </tr>
          </thead>
          <tbody>
            {rowData.map((row, rowIndex) => {
              const rowClassName = (() => {
                if (row.tipo === TipoItem.GRUPO) return "cell-group-header";
                if (row.tipo === TipoItem.SUBGRUPO)
                  return "cell-subgroup-header";
                // Linhas brancas: as descritivas de qualquer bloco.
                if (
                  row.tipo === TipoItem.DESCRITIVO_ACESSORIA ||
                  row.tipo === TipoItem.DESCRITIVO_TAXAS ||
                  row.tipo === TipoItem.DESCRITIVO_TRANSPORTE
                ) {
                  return "cell-item-white";
                }
                // Linhas amarelas: a categoria do ITEM 3 e os itens do ITEM 2,
                // qualquer que seja o tipo — ITEM_TRANSPORTE entra aqui junto
                // com ITEM e ITEM_TAXAS. Antes a lista era por tipo e o
                // transporte, ausente dela, caía sem classe nenhuma e aparecia
                // branco no lugar da linha amarela.
                if (row.tipo === TipoItem.ITEM_ACESSORIA)
                  return "cell-item-yellow";
                if (
                  row.tipo === TipoItem.ITEM ||
                  TIPOS_ITEM_SEM_COTACAO.includes(row.tipo)
                ) {
                  if (!row.numero || !row.numero.startsWith("2."))
                    return "cell-item-white";
                  // A coluna Status é quem dá altura às linhas amarelas: um tem
                  // o botão de cotação, o de taxas tem o de anexar. Transporte
                  // não tem nenhum dos dois e ficava espremido, então a altura
                  // vem do CSS em vez de vir do conteúdo.
                  return row.tipo === TipoItem.ITEM_TRANSPORTE
                    ? "cell-item-yellow cell-item-sem-botao"
                    : "cell-item-yellow";
                }
                if (row.tipo === TipoItem.FORNECEDOR) return "cell-fornecedor";
                return "";
              })();

              return (
                <tr
                  key={`${row.tipo}-${row.numero || rowIndex}`}
                  className={`${rowClassName} group`}
                  id={
                    row.tipo === TipoItem.GRUPO && row.item?.includes("ITEM 1")
                      ? "nav-essenciais"
                      : row.tipo === TipoItem.GRUPO &&
                          row.item?.includes("ITEM 2")
                        ? "nav-complementares"
                        : row.tipo === TipoItem.GRUPO &&
                            row.item?.includes("ITEM 3")
                          ? "nav-acessorias"
                          : row.tipo === TipoItem.SUBGRUPO &&
                              row.item
                                ?.toUpperCase()
                                .includes("TAXAS E SEGUROS")
                            ? "nav-taxas"
                            : undefined
                  }
                >
                  {/* Ações */}
                  <td className="p-1">
                    <ActionButtonRenderer data={row} />
                  </td>

                  {/* Número */}
                  <td className="text-center p-1">
                    <span className="text-sm md:text-base">
                      {row.tipo === TipoItem.FORNECEDOR && row.numero
                        ? row.numero.split(".").pop()
                        : row.tipo === TipoItem.ITEM_ACESSORIA &&
                            row.numero &&
                            /^3\.\d+$/.test(row.numero)
                          ? row.numero
                          : row.tipo === TipoItem.DESCRITIVO_ACESSORIA &&
                              row.numero &&
                              /^3\.\d+\.\d+$/.test(row.numero)
                            ? row.numero.split(".").pop()
                            : row.numero || ""}
                    </span>
                  </td>

                  {/* Item/Produto */}
                  <td className="p-1">
                    {row.tipo === TipoItem.FORNECEDOR ? (
                      <ItemCellRenderer
                        data={row}
                        value={row.item}
                        node={{ rowIndex }}
                      />
                    ) : row.tipo === TipoItem.GRUPO ? (
                      <div style={{ fontWeight: 700, color: "#FFFFFF" }}>
                        {row.item}
                      </div>
                    ) : row.tipo === TipoItem.SUBGRUPO ? (
                      <div
                        className="flex items-center gap-2"
                        style={{ fontWeight: 600, color: "#FFFFFF" }}
                      >
                        <p>{row.item}</p>
                        {/* Um ícone só, discreto, para trocar a categoria e
                            duplicar: a linha preta já tem + e lixeira, e a
                            planilha não comporta mais botões fixos. */}
                        {!isReadOnly &&
                          row.item !== "PRODUTOS E SERVIÇOS ESSENCIAIS" &&
                          row.item !== "DESPESAS ACESSÓRIAS" && (
                            <button
                              className="btn btn-ghost btn-xs px-1 min-h-0 h-6 text-white opacity-50 hover:opacity-100 hover:bg-zinc-700"
                              title="Trocar categoria ou duplicar"
                              aria-label="Trocar categoria ou duplicar"
                              onClick={() => setMenuCategoria(row)}
                            >
                              <FiChevronDown className="h-4 w-4" />
                            </button>
                          )}
                      </div>
                    ) : nomeVemDoSelectDeFornecedor(row) ? (
                      // Linha branca de TAXAS, TRANSPORTES ou DESPESAS
                      // ACESSÓRIAS: select de fornecedor no lugar do nome
                      <ItemCellRenderer
                        data={row}
                        value={row.item}
                        node={{ rowIndex }}
                      />
                    ) : editaTextoDoItem2(row) ||
                      row.tipo === TipoItem.ITEM_ACESSORIA ? ( // Permite edição em ITEM 2, linhas brancas e categorias do ITEM 3 (para diferenciar repetições da mesma despesa)
                      <input
                        type="text"
                        className="w-full bg-transparent border-0 p-2 text-sm md:text-base focus:outline-none focus:bg-white"
                        value={row.item || ""}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRowData((prevData) => {
                            const newData = [...prevData];
                            newData[rowIndex] = {
                              ...newData[rowIndex],
                              item: newValue,
                            };
                            return newData;
                          });
                        }}
                      />
                    ) : (
                      <span className="text-sm md:text-base">{row.item}</span>
                    )}
                  </td>

                  {/* Descritivo */}
                  <td className="p-1">
                    {editaTextoDoItem2(row) ||
                    row.tipo === TipoItem.DESCRITIVO_ACESSORIA ||
                    linhaBrancaDeFornecedor(row) ||
                    row.tipo === TipoItem.ITEM_ACESSORIA ? ( // Permite edição em ITEM 2, linhas brancas e amarelas ITEM 3 e DESCRITIVO_TAXAS
                      <textarea
                        className="w-full bg-transparent border-0 p-1 min-h-8 text-sm md:text-base focus:outline-none focus:bg-white resize-none"
                        value={row.descritivo || ""}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRowData((prevData) => {
                            const newData = [...prevData];
                            newData[rowIndex] = {
                              ...newData[rowIndex],
                              descritivo: newValue,
                            };
                            return newData;
                          });
                          // Auto-resize
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                        onInput={(e) => {
                          // Auto-resize também no input direto
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = "auto";
                          target.style.height = target.scrollHeight + "px";
                        }}
                      />
                    ) : (
                      <span className="text-sm md:text-base">
                        {row.descritivo || ""}
                      </span>
                    )}
                  </td>

                  {/* Quantidade */}
                  <td className="text-center p-1">
                    {editaTextoDoItem2(row) ||
                    row.tipo === TipoItem.DESCRITIVO_ACESSORIA ||
                    linhaBrancaDeFornecedor(row) ||
                    row.tipo === TipoItem.ITEM_ACESSORIA ? ( // Permite edição em ITEM 2, linhas brancas ITEM 3 e linhas brancas de TAXAS
                      <input
                        type="number"
                        className="w-full text-center bg-transparent border-0 p-2 text-sm md:text-base focus:outline-none focus:bg-white"
                        value={row.quantidade || ""}
                        onChange={(e) => {
                          const newValue =
                            parseFloat(e.target.value) || undefined;
                          setRowData((prevData) => {
                            const newData = [...prevData];
                            newData[rowIndex] = {
                              ...newData[rowIndex],
                              quantidade: newValue,
                            };

                            // Linha branca de TAXAS/TRANSPORTES: cálculo próprio,
                            // sem impactar a linha amarela pai
                            if (linhaBrancaDeFornecedor(newData[rowIndex])) {
                              const cr = newData[rowIndex];
                              const st =
                                (cr.quantidade || 0) *
                                (cr.diaria || 0) *
                                (cr.unitario || 0);
                              newData[rowIndex] = {
                                ...cr,
                                subtotal: st,
                                total: st,
                              };
                            }

                            // Recalcular se for linha amarela do ITEM 2
                            // (ITEM, TAXAS ou TRANSPORTE)
                            if (
                              (newData[rowIndex].tipo === TipoItem.ITEM ||
                                TIPOS_ITEM_SEM_COTACAO.includes(
                                  newData[rowIndex].tipo,
                                )) &&
                              newData[rowIndex].numero?.startsWith("2.")
                            ) {
                              const currentRow = newData[rowIndex];
                              const quantidade = newValue || 0;
                              const diaria = currentRow.diaria || 0;
                              const unitario = currentRow.unitario || 0;
                              const subtotal = quantidade * diaria * unitario;
                              newData[rowIndex] = {
                                ...currentRow,
                                quantidade: newValue,
                                subtotal: subtotal,
                                total: subtotal,
                              };

                              // Se for ITEM_TAXAS, recalcular o subgrupo TAXAS E SEGUROS
                              if (
                                newData[rowIndex].tipo === TipoItem.ITEM_TAXAS
                              ) {
                                const subgrupoIndex = newData.findIndex(
                                  (r) =>
                                    r.tipo === TipoItem.SUBGRUPO &&
                                    r.item
                                      ?.toUpperCase()
                                      .includes("TAXAS E SEGUROS"),
                                );
                                if (subgrupoIndex >= 0) {
                                  let totalSubgrupo = 0;
                                  newData.forEach((r) => {
                                    if (
                                      r.tipo === TipoItem.ITEM_TAXAS &&
                                      r.total
                                    ) {
                                      totalSubgrupo += r.total;
                                    }
                                  });
                                  newData[subgrupoIndex] = {
                                    ...newData[subgrupoIndex],
                                    subtotal: totalSubgrupo,
                                    total: totalSubgrupo,
                                  };
                                }
                              }
                            }

                            // Despesa acessória: a linha calcula o próprio
                            // subtotal, e a amarela passa a ser a soma das
                            // brancas. Ver somarDespesasAcessorias.
                            const editada = newData[rowIndex];
                            if (editada.tipo === TipoItem.ITEM_ACESSORIA) {
                              const subtotal =
                                (editada.quantidade || 0) *
                                (editada.diaria || 0) *
                                (editada.unitario || 0);
                              newData[rowIndex] = {
                                ...editada,
                                subtotal,
                                total: subtotal,
                              };
                              return somarDespesasAcessorias(newData);
                            }
                            if (editada.tipo === TipoItem.DESCRITIVO_ACESSORIA) {
                              // Só com unitário: sem ele, mexer na quantidade
                              // zeraria um subtotal digitado direto na coluna.
                              if (editada.unitario) {
                                const subtotal = arredondarCentavos(
                                  (editada.quantidade || 1) *
                                    (editada.diaria || 1) *
                                    editada.unitario,
                                );
                                newData[rowIndex] = {
                                  ...editada,
                                  subtotal,
                                  total: subtotal,
                                };
                              }
                              return somarDespesasAcessorias(newData);
                            }

                            return newData;
                          });
                        }}
                      />
                    ) : (
                      <span className="text-sm md:text-base">
                        {row.quantidade || ""}
                      </span>
                    )}
                  </td>

                  {/* Diária */}
                  <td className="text-center p-1">
                    {editaTextoDoItem2(row) ||
                    row.tipo === TipoItem.DESCRITIVO_ACESSORIA ||
                    linhaBrancaDeFornecedor(row) ||
                    row.tipo === TipoItem.ITEM_ACESSORIA ? ( // Permite edição em ITEM 2, linhas brancas ITEM 3 e linhas brancas de TAXAS
                      <input
                        type="number"
                        className="w-full text-center bg-transparent border-0 p-2 text-sm md:text-base focus:outline-none focus:bg-white"
                        value={row.diaria || ""}
                        onChange={(e) => {
                          const newValue =
                            parseFloat(e.target.value) || undefined;
                          setRowData((prevData) => {
                            const newData = [...prevData];
                            newData[rowIndex] = {
                              ...newData[rowIndex],
                              diaria: newValue,
                            };

                            // Linha branca de TAXAS/TRANSPORTES: cálculo próprio,
                            // sem impactar a linha amarela pai
                            if (linhaBrancaDeFornecedor(newData[rowIndex])) {
                              const cr = newData[rowIndex];
                              const st =
                                (cr.quantidade || 0) *
                                (cr.diaria || 0) *
                                (cr.unitario || 0);
                              newData[rowIndex] = {
                                ...cr,
                                subtotal: st,
                                total: st,
                              };
                            }

                            // Recalcular se for linha amarela do ITEM 2
                            // (ITEM, TAXAS ou TRANSPORTE)
                            if (
                              (newData[rowIndex].tipo === TipoItem.ITEM ||
                                TIPOS_ITEM_SEM_COTACAO.includes(
                                  newData[rowIndex].tipo,
                                )) &&
                              newData[rowIndex].numero?.startsWith("2.")
                            ) {
                              const currentRow = newData[rowIndex];
                              const quantidade = currentRow.quantidade || 0;
                              const diaria = newValue || 0;
                              const unitario = currentRow.unitario || 0;
                              const subtotal = quantidade * diaria * unitario;
                              newData[rowIndex] = {
                                ...currentRow,
                                diaria: newValue,
                                subtotal: subtotal,
                                total: subtotal,
                              };

                              // Se for ITEM_TAXAS, recalcular o subgrupo TAXAS E SEGUROS
                              if (
                                newData[rowIndex].tipo === TipoItem.ITEM_TAXAS
                              ) {
                                const subgrupoIndex = newData.findIndex(
                                  (r) =>
                                    r.tipo === TipoItem.SUBGRUPO &&
                                    r.item
                                      ?.toUpperCase()
                                      .includes("TAXAS E SEGUROS"),
                                );
                                if (subgrupoIndex >= 0) {
                                  let totalSubgrupo = 0;
                                  newData.forEach((r) => {
                                    if (
                                      r.tipo === TipoItem.ITEM_TAXAS &&
                                      r.total
                                    ) {
                                      totalSubgrupo += r.total;
                                    }
                                  });
                                  newData[subgrupoIndex] = {
                                    ...newData[subgrupoIndex],
                                    subtotal: totalSubgrupo,
                                    total: totalSubgrupo,
                                  };
                                }
                              }
                            }

                            // Despesa acessória: a linha calcula o próprio
                            // subtotal, e a amarela passa a ser a soma das
                            // brancas. Ver somarDespesasAcessorias.
                            const editada = newData[rowIndex];
                            if (editada.tipo === TipoItem.ITEM_ACESSORIA) {
                              const subtotal =
                                (editada.quantidade || 0) *
                                (editada.diaria || 0) *
                                (editada.unitario || 0);
                              newData[rowIndex] = {
                                ...editada,
                                subtotal,
                                total: subtotal,
                              };
                              return somarDespesasAcessorias(newData);
                            }
                            if (editada.tipo === TipoItem.DESCRITIVO_ACESSORIA) {
                              // Só com unitário: sem ele, mexer na quantidade
                              // zeraria um subtotal digitado direto na coluna.
                              if (editada.unitario) {
                                const subtotal = arredondarCentavos(
                                  (editada.quantidade || 1) *
                                    (editada.diaria || 1) *
                                    editada.unitario,
                                );
                                newData[rowIndex] = {
                                  ...editada,
                                  subtotal,
                                  total: subtotal,
                                };
                              }
                              return somarDespesasAcessorias(newData);
                            }

                            return newData;
                          });
                        }}
                      />
                    ) : (
                      <span className="text-sm md:text-base">
                        {row.diaria || ""}
                      </span>
                    )}
                  </td>

                  {/* Unitário */}
                  <td className="text-right p-1">
                    {/* Linhas editáveis do ITEM 3, ITEM_TAXAS e linhas brancas de TAXAS */}
                    {row.tipo === TipoItem.DESCRITIVO_ACESSORIA ||
                    linhaBrancaDeFornecedor(row) ||
                    row.tipo === TipoItem.ITEM_ACESSORIA ||
                    editaValorManual(row) ? (
                      <input
                        type="text"
                        className="w-full text-right bg-transparent border-0 p-2 text-sm md:text-base focus:outline-none focus:bg-white"
                        value={
                          row.unitario != null
                            ? `R$ ${row.unitario.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : ""
                        }
                        placeholder="R$ 0,00"
                        onChange={(e) => {
                          const numbers = e.target.value.replace(/\D/g, "");
                          const newValue = numbers
                            ? Number(numbers) / 100
                            : undefined;

                          setRowData((prevData) => {
                            const newData = [...prevData];
                            newData[rowIndex] = {
                              ...newData[rowIndex],
                              unitario: newValue,
                            };

                            // Linha branca de TAXAS/TRANSPORTES: cálculo próprio,
                            // sem impactar a linha amarela pai
                            if (linhaBrancaDeFornecedor(newData[rowIndex])) {
                              const cr = newData[rowIndex];
                              const st =
                                (cr.quantidade || 0) *
                                (cr.diaria || 0) *
                                (cr.unitario || 0);
                              newData[rowIndex] = {
                                ...cr,
                                subtotal: st,
                                total: st,
                              };
                            }

                            // Linha amarela de valor manual: o subtotal vem do
                            // que foi digitado, não de cotação. Só TAXAS tem
                            // total no cabeçalho do subgrupo — o recálculo dele
                            // fica restrito a ITEM_TAXAS mais abaixo.
                            if (editaValorManual(newData[rowIndex])) {
                              const currentRow = newData[rowIndex];
                              const quantidade = currentRow.quantidade || 1;
                              const diaria = currentRow.diaria || 1;
                              const unitario = newValue || 0;
                              const subtotal = quantidade * diaria * unitario;
                              newData[rowIndex] = {
                                ...currentRow,
                                unitario: newValue,
                                subtotal: subtotal,
                                total: subtotal,
                              };

                              // Recalcular o subgrupo TAXAS E SEGUROS
                              const subgrupoIndex = newData.findIndex(
                                (r) =>
                                  r.tipo === TipoItem.SUBGRUPO &&
                                  r.item
                                    ?.toUpperCase()
                                    .includes("TAXAS E SEGUROS"),
                              );
                              if (subgrupoIndex >= 0) {
                                let totalSubgrupo = 0;
                                newData.forEach((r) => {
                                  if (
                                    r.tipo === TipoItem.ITEM_TAXAS &&
                                    r.total
                                  ) {
                                    totalSubgrupo += r.total;
                                  }
                                });
                                newData[subgrupoIndex] = {
                                  ...newData[subgrupoIndex],
                                  subtotal: totalSubgrupo,
                                  total: totalSubgrupo,
                                };
                              }
                            }

                            // Despesa acessória: a linha calcula o próprio
                            // subtotal, e a amarela passa a ser a soma das
                            // brancas. Ver somarDespesasAcessorias.
                            const editada = newData[rowIndex];
                            if (editada.tipo === TipoItem.ITEM_ACESSORIA) {
                              const subtotal =
                                (editada.quantidade || 0) *
                                (editada.diaria || 0) *
                                (editada.unitario || 0);
                              newData[rowIndex] = {
                                ...editada,
                                subtotal,
                                total: subtotal,
                              };
                              return somarDespesasAcessorias(newData);
                            }
                            if (editada.tipo === TipoItem.DESCRITIVO_ACESSORIA) {
                              // Só com unitário: sem ele, mexer na quantidade
                              // zeraria um subtotal digitado direto na coluna.
                              if (editada.unitario) {
                                const subtotal = arredondarCentavos(
                                  (editada.quantidade || 1) *
                                    (editada.diaria || 1) *
                                    editada.unitario,
                                );
                                newData[rowIndex] = {
                                  ...editada,
                                  subtotal,
                                  total: subtotal,
                                };
                              }
                              return somarDespesasAcessorias(newData);
                            }

                            return newData;
                          });
                        }}
                      />
                    ) : row.tipo === TipoItem.FORNECEDOR ||
                      (row.tipo === TipoItem.ITEM &&
                        row.numero &&
                        row.numero.startsWith("2.")) ? (
                      /* Fornecedores e Itens amarelos do ITEM 2 - campo bloqueado, alimentado apenas via orçamento */
                      <span
                        className={`text-sm md:text-base block p-2 ${
                          row.menorPreco ? "font-semibold text-success" : ""
                        }`}
                        title="Este valor é preenchido automaticamente pelo orçamento do fornecedor"
                      >
                        {row.unitario != null
                          ? currencyFormatter({ value: row.unitario })
                          : "R$ 0,00"}
                      </span>
                    ) : row.unitario != null ? (
                      <span className="text-sm md:text-base">
                        {currencyFormatter({ value: row.unitario })}
                      </span>
                    ) : null}
                  </td>

                  {/* Subtotal */}
                  <td className="text-right p-1">
                    {/* Tornar editável para ITEM_ACESSORIA, DESCRITIVO_ACESSORIA e ITEM_TAXAS.
                        A amarela de despesa acessória com valor nas brancas é
                        a soma delas: digitar ali seria desfeito na próxima
                        edição de uma branca. */}
                    {row.tipo === TipoItem.ITEM_ACESSORIA &&
                    brancasComValor(rowData, row).length > 0 ? (
                      <span
                        className="text-sm md:text-base font-semibold"
                        title="Soma das linhas de fornecedor abaixo"
                      >
                        {currencyFormatter({ value: row.subtotal ?? 0 })}
                      </span>
                    ) : row.tipo === TipoItem.ITEM_ACESSORIA ||
                    row.tipo === TipoItem.DESCRITIVO_ACESSORIA ||
                    editaValorManual(row) ? (
                      <input
                        type="text"
                        className="w-full text-right bg-transparent border-0 p-2 text-sm md:text-base focus:outline-none focus:bg-white"
                        value={
                          row.subtotal != null
                            ? `R$ ${row.subtotal.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : ""
                        }
                        placeholder="R$ 0,00"
                        onChange={(e) => {
                          const numbers = e.target.value.replace(/\D/g, "");
                          const newValue = numbers
                            ? Number(numbers) / 100
                            : undefined;

                          setRowData((prevData) => {
                            const newData = [...prevData];
                            newData[rowIndex] = {
                              ...newData[rowIndex],
                              subtotal: newValue,
                              total: newValue, // Total é igual ao subtotal
                            };

                            // Se for ITEM_TAXAS, recalcular o subgrupo TAXAS E SEGUROS
                            if (
                              newData[rowIndex].tipo === TipoItem.ITEM_TAXAS
                            ) {
                              const subgrupoIndex = newData.findIndex(
                                (r) =>
                                  r.tipo === TipoItem.SUBGRUPO &&
                                  r.item
                                    ?.toUpperCase()
                                    .includes("TAXAS E SEGUROS"),
                              );
                              if (subgrupoIndex >= 0) {
                                let totalSubgrupo = 0;
                                newData.forEach((r) => {
                                  if (
                                    r.tipo === TipoItem.ITEM_TAXAS &&
                                    r.total
                                  ) {
                                    totalSubgrupo += r.total;
                                  }
                                });
                                newData[subgrupoIndex] = {
                                  ...newData[subgrupoIndex],
                                  subtotal: totalSubgrupo,
                                  total: totalSubgrupo,
                                };
                              }
                            }

                            // Despesa acessória: o valor da branca sobe para
                            // a amarela, somado ao das outras brancas.
                            if (
                              newData[rowIndex].tipo === TipoItem.ITEM_ACESSORIA ||
                              newData[rowIndex].tipo ===
                                TipoItem.DESCRITIVO_ACESSORIA
                            ) {
                              // Branca que tinha valor e foi apagada: se era a
                              // última com valor, a amarela volta a zero em vez
                              // de guardar a soma antiga.
                              const branca = newData[rowIndex];
                              const tinhaValor =
                                (prevData[rowIndex].subtotal ?? 0) > 0;
                              const amarela =
                                branca.tipo === TipoItem.DESCRITIVO_ACESSORIA &&
                                tinhaValor
                                  ? newData.find(
                                      (r) =>
                                        r.tipo === TipoItem.ITEM_ACESSORIA &&
                                        !!r.numero &&
                                        !!branca.numero?.startsWith(
                                          r.numero + ".",
                                        ),
                                    )
                                  : undefined;
                              return somarDespesasAcessorias(
                                newData,
                                amarela?.id ? new Set([amarela.id]) : undefined,
                              );
                            }

                            return newData;
                          });
                        }}
                      />
                    ) : row.tipo === TipoItem.SUBGRUPO ? (
                      <span className="text-sm md:text-base font-semibold">
                        {currencyFormatter({
                          value: (() => {
                            let total = 0;
                            for (
                              let i = rowIndex + 1;
                              i < rowData.length;
                              i++
                            ) {
                              const r = rowData[i];
                              if (
                                r.tipo === TipoItem.GRUPO ||
                                r.tipo === TipoItem.SUBGRUPO
                              )
                                break;
                              // Para ITEM 2 - soma itens principais (ITEM e ITEM_TAXAS)
                              if (
                                (r.tipo === TipoItem.ITEM ||
                                  r.tipo === TipoItem.ITEM_TAXAS) &&
                                r.numero &&
                                r.numero.startsWith("2.") &&
                                !r.numero.match(/^\d+\.\d+\.\d+$/) &&
                                r.subtotal
                              ) {
                                total += r.subtotal;
                              }
                              // Para ITEM 3 - soma as categorias principais (3.1, 3.2, etc)
                              if (
                                r.tipo === TipoItem.ITEM_ACESSORIA &&
                                r.numero &&
                                /^3\.\d+$/.test(r.numero) &&
                                r.subtotal
                              ) {
                                total += r.subtotal;
                              }
                            }
                            return total > 0 ? total : null;
                          })(),
                        })}
                      </span>
                    ) : row.subtotal != null ? (
                      <span className="text-sm md:text-base">
                        {currencyFormatter({ value: row.subtotal })}
                      </span>
                    ) : null}
                  </td>

                  {/* Status */}
                  <td className="p-1">
                    <StatusCellRenderer
                      data={row}
                      value={row.status}
                      rowIndex={rowIndex}
                    />
                  </td>

                  {/* Observações */}
                  <td className="p-1">
                    {row.tipo === TipoItem.ITEM ||
                    TIPOS_ITEM_SEM_COTACAO.includes(row.tipo) ||
                    linhaBrancaDeFornecedor(row) ||
                    row.tipo === TipoItem.ITEM_ACESSORIA ||
                    row.tipo === TipoItem.DESCRITIVO_ACESSORIA ||
                    row.tipo === TipoItem.FORNECEDOR ? (
                      <textarea
                        className="w-full bg-transparent border-0 p-1 min-h-8 focus:outline-none resize-none text-xs focus:bg-white"
                        value={row.observacoes || ""}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRowData((prevData) => {
                            const newData = [...prevData];
                            newData[rowIndex] = {
                              ...newData[rowIndex],
                              observacoes: newValue,
                            };
                            return newData;
                          });
                          // Auto-resize
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                        onInput={(e) => {
                          // Auto-resize também no input direto
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = "auto";
                          target.style.height = target.scrollHeight + "px";
                        }}
                      />
                    ) : (
                      <span className="text-xs">{row.observacoes || ""}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Seção de Totais e Cálculos */}
        <div className="bg-base-100 border-t-2 border-base-300">
          {(() => {
            const totais = calcularTotais();
            const formatCurrency = (value: number) =>
              new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(value);

            return (
              <div className="divide-y divide-base-300 border-x border-base-300">
                {/* Subtotal - Linha Preta */}
                <div className="bg-black text-white p-3 grid grid-cols-2 items-center">
                  <div className="text-base font-bold ml-auto uppercase">
                    Subtotal
                  </div>
                  <div className="text-xl font-bold ml-auto">
                    {formatCurrency(totais.subtotal)}
                  </div>
                </div>

                {/* Detalhamento - Linhas Brancas */}
                <div className="bg-base-100 divide-y divide-base-200">
                  {/* Total Custos Serviços Essenciais */}
                  <div className="p-3 flex justify-between items-center">
                    <div className="text-sm">
                      Total Custos Serviços Essenciais
                    </div>
                    <div className="text-base font-semibold">
                      {formatCurrency(totais.totalServicosEssenciais)}
                    </div>
                  </div>

                  {/* Total Custos Serviços Complementares */}
                  <div className="p-3 flex justify-between items-center">
                    <div className="text-sm">
                      Total Custos Serviços Complementares
                    </div>
                    <div className="text-base font-semibold">
                      {formatCurrency(totais.totalServicosComplementares)}
                    </div>
                  </div>

                  {/* Impostos */}
                  <div className="p-3 flex justify-between items-center">
                    <div className="text-sm">
                      Impostos ({formatarPercentual(PERCENT_IMPOSTOS)})
                    </div>
                    <div className="text-base font-semibold">
                      {formatCurrency(totais.impostos)}
                    </div>
                  </div>

                  {/* Total Honorários */}
                  <div className="p-3 flex justify-between items-center">
                    <div className="text-sm">
                      Total Honorários ({formatarPercentual(taxaDeHonorarios(planilha))}
                      )
                    </div>
                    <div className="text-base font-semibold">
                      {formatCurrency(totais.totalHonorarios)}
                    </div>
                  </div>

                  {/* Taxa de Administração */}
                  <div className="p-3 flex justify-between items-center">
                    <div className="text-sm">
                      Taxa de Administração (
                      {formatarPercentual(PERCENT_TAXA_ADMINISTRACAO)})
                    </div>
                    <div className="text-base font-semibold">
                      {formatCurrency(totais.taxaAdministracao)}
                    </div>
                  </div>
                </div>

                {/* Total Valor AS - Linha Preta */}
                <div className="bg-black text-white p-3 flex justify-between items-center">
                  <div className="text-base font-bold">
                    Total (VALOR UTILIZADO NA AS)
                  </div>
                  <div className="text-xl font-bold">
                    {formatCurrency(totais.totalValorAS)}
                  </div>
                </div>

                {/* Total Despesas Acessórias - Linha Cinza */}
                <div className="bg-neutral text-white p-3 flex justify-between items-center">
                  <div className="text-base font-semibold">
                    Total Custos Despesas Acessórias
                  </div>
                  <div className="text-lg font-bold">
                    {formatCurrency(totais.totalDespesasAcessorias)}
                  </div>
                </div>

                {/* Total Geral - Linha Preta */}
                <div className="bg-black text-white p-4 flex justify-between items-center">
                  <div className="text-lg font-bold">
                    Total Geral de Custo do Evento
                  </div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(totais.totalGeralEvento)}
                  </div>
                </div>

                {/* Breakdown Serviços Complementares */}
                {(() => {
                  const sc = totais.totalServicosComplementares;
                  const breakdownRows: Array<{
                    label: string;
                    faixa: FaixaBreakdown;
                  }> = [
                    { label: "Equipamentos", faixa: "equipamentos" },
                    { label: "Materiais", faixa: "materiais" },
                    { label: "Serviços", faixa: "servicos" },
                  ];
                  return (
                    <div className="pt-5 w-full border border-t-0 border-black overflow-hidden text-sm">
                      {/* Header row */}
                      <div className="flex border-y border-black">
                        <div className="flex-1 px-3 py-2 bg-base-100 border-r border-black" />
                        <div className="w-96 bg-[#d9d9d9] grid grid-cols-[1fr_9rem] font-bold text-black">
                          <span className="px-3 py-2 border-r border-black">
                            SERVIÇOS COMPLEMENTARES
                          </span>
                          <span className="px-3 py-2 text-right">
                            {formatCurrency(sc)}
                          </span>
                        </div>
                      </div>
                      {/* Duas entradas por faixa: o percentual da base e o
                          valor. Preencher o percentual põe a linha em modo
                          proporcional e o valor passa a acompanhar o total de
                          complementares; digitar o valor limpa o percentual e
                          fixa o número. */}
                      {breakdownRows.map((br) => {
                        const percent = breakdownPercents[br.faixa];
                        const proporcional = percent !== "";
                        const valor = valorBreakdown(br.faixa);
                        return (
                          <div
                            key={br.faixa}
                            className="flex border-b border-black last:border-b-0"
                          >
                            <div className="flex-1 px-3 py-2 bg-base-100 border-r border-black" />
                            <div className="w-96 bg-base-100 grid grid-cols-[1fr_6rem_9rem] items-center">
                              <span className="px-3 py-2 border-r border-black">
                                {br.label}
                              </span>
                              <NumericFormat
                                value={percent}
                                onValueChange={(v) =>
                                  handlePercentBreakdown(
                                    br.faixa,
                                    v.value ?? "",
                                  )
                                }
                                decimalSeparator=","
                                decimalScale={2}
                                allowNegative={false}
                                suffix=" %"
                                placeholder="— %"
                                title={`Percentual sobre ${formatCurrency(sc)}`}
                                className="px-2 py-2 w-full text-right border-r border-black bg-transparent outline-none focus:outline-none focus:bg-base-200"
                              />
                              <NumericFormat
                                value={
                                  valor === 0 && !proporcional ? "" : valor
                                }
                                onValueChange={(v, info) => {
                                  // Reformatação vinda do percentual chega como
                                  // "prop" e não é digitação.
                                  if (info.source !== "event") return;

                                  // Mas "event" também não basta. O campo usa
                                  // fixedDecimalScale, e ao perder o foco a
                                  // própria biblioteca arredonda e dispara este
                                  // callback com source "event", sem ninguém
                                  // ter digitado (react-number-format 5.4.4,
                                  // _onBlur). Bastava o foco passar pelo campo
                                  // para o percentual ser apagado e o valor
                                  // congelar no último número calculado.
                                  //
                                  // Em modo proporcional, então, só conta como
                                  // digitação quando o número muda de verdade.
                                  const digitado = parseFloat(v.value ?? "");
                                  if (
                                    proporcional &&
                                    (Number.isNaN(digitado) ||
                                      Math.abs(digitado - valor) < 0.005)
                                  ) {
                                    return;
                                  }

                                  handleValorBreakdown(br.faixa, v.value ?? "");
                                }}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={false}
                                prefix="R$ "
                                placeholder="R$ 0,00"
                                title={
                                  proporcional
                                    ? "Calculado pelo percentual. Digitar aqui fixa o valor e limpa o percentual."
                                    : "Valor fixo. Preencha o percentual para acompanhar a base."
                                }
                                className={`px-3 py-2 w-full text-right font-bold bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus:shadow-none${
                                  proporcional ? " text-primary" : ""
                                }`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Actions Bar */}
      <div
        ref={footerRef}
        className="fixed bottom-0 left-0 right-0 w-full bg-base-100 border-t border-base-300 shadow-lg z-40 px-4 py-3"
      >
        <div className="flex items-center gap-4 w-full min-w-0">
          {/* Resumo dos Totais — scroll horizontal quando não couber */}
          <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 text-xs scrollbar-hide">
            {(() => {
              const totais = calcularTotais();
              const formatCurrency = (value: number) =>
                new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(value);

              return (
                <>
                  {/* Subtotal */}
                  <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                    <span className="text-gray-500 font-medium">Subtotal</span>
                    <span className="font-semibold text-gray-700">
                      {formatCurrency(totais.subtotal)}
                    </span>
                  </div>
                  <span className="text-gray-300 w-px h-8 bg-gray-300 shrink-0"></span>

                  {/* Total Serviços Essenciais */}
                  <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                    <span className="text-gray-500 font-medium">
                      Serv. Essenciais
                    </span>
                    <span className="font-semibold text-gray-700">
                      {formatCurrency(totais.totalServicosEssenciais)}
                    </span>
                  </div>
                  <span className="text-gray-300 w-px h-8 bg-gray-300 shrink-0"></span>

                  {/* Total Serviços Complementares */}
                  <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                    <span className="text-gray-500 font-medium">
                      Serv. Complementares
                    </span>
                    <span className="font-semibold text-gray-700">
                      {formatCurrency(totais.totalServicosComplementares)}
                    </span>
                  </div>
                  <span className="text-gray-300 w-px h-8 bg-gray-300 shrink-0"></span>

                  {/* Impostos */}
                  <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                    <span className="text-gray-500 font-medium">Impostos</span>
                    <span className="font-semibold text-gray-700">
                      {formatCurrency(totais.impostos)}
                    </span>
                  </div>
                  <span className="text-gray-300 w-px h-8 bg-gray-300 shrink-0"></span>

                  {/* Honorários */}
                  <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                    <span className="text-gray-500 font-medium">
                      Honorários
                    </span>
                    <span className="font-semibold text-gray-700">
                      {formatCurrency(totais.totalHonorarios)}
                    </span>
                  </div>
                  <span className="text-gray-300 w-px h-8 bg-gray-300 shrink-0"></span>

                  {/* Taxa Administração */}
                  <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                    <span className="text-gray-500 font-medium">Taxa Adm.</span>
                    <span className="font-semibold text-gray-700">
                      {formatCurrency(totais.taxaAdministracao)}
                    </span>
                  </div>
                  <span className="text-gray-300 w-px h-8 bg-gray-300 shrink-0"></span>

                  {/* Total Valor AS */}
                  <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                    <span className="text-gray-500 font-medium">Valor AS</span>
                    <span className="font-semibold text-gray-700">
                      {formatCurrency(totais.totalValorAS)}
                    </span>
                  </div>

                  {/* Total Despesas Acessórias */}
                  {totais.totalDespesasAcessorias > 0 && (
                    <>
                      <span className="text-gray-300 w-px h-8 bg-gray-300 shrink-0"></span>
                      <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                        <span className="text-gray-500 font-medium">
                          Desp. Acessórias
                        </span>
                        <span className="font-semibold text-gray-700">
                          {formatCurrency(totais.totalDespesasAcessorias)}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Total Geral - Destaque */}
                  <span className="text-gray-300 w-px h-8 bg-gray-300 shrink-0"></span>
                  <div className="flex flex-col gap-1 px-2 py-1 shrink-0">
                    <span className="text-gray-600 font-semibold">
                      TOTAL GERAL
                    </span>
                    <span className="font-bold text-gray-900">
                      {formatCurrency(totais.totalGeralEvento)}
                    </span>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-2 shrink-0 items-center">
            {/* Indicador de alterações não salvas (item 1.4) */}
            {!isReadOnly && (
              <span
                role="status"
                aria-live="polite"
                className={`text-xs font-medium flex items-center gap-1 mr-1 whitespace-nowrap ${
                  saveErrored
                    ? "text-error"
                    : isSaving
                      ? "text-base-content/60"
                      : dirty
                        ? "text-warning"
                        : "text-success"
                }`}
              >
                {saveErrored ? (
                  <button
                    type="button"
                    className="link link-error"
                    onClick={() => handleSave()}
                  >
                    ⚠ Erro ao salvar — tentar novamente
                  </button>
                ) : isSaving ? (
                  <>⟳ Salvando…</>
                ) : dirty ? (
                  <>● Alterações não salvas</>
                ) : lastSavedAt ? (
                  <>
                    ✓ Salvo às{" "}
                    {lastSavedAt.toLocaleTimeString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </>
                ) : (
                  <>✓ Tudo salvo</>
                )}
              </span>
            )}
            {/* Descoberta de atalhos de teclado (item 1.2) */}
            <button
              type="button"
              className="btn btn-ghost btn-xs text-base-content/50"
              onClick={() => setShortcutsModalAberto(true)}
              title="Atalhos de teclado"
              aria-label="Mostrar atalhos de teclado"
            >
              <kbd className="kbd kbd-xs">?</kbd>
            </button>
            <button
              className="btn btn-info btn-sm"
              onClick={() => guardNavigation(() => navigate("/planilhas"))}
            >
              <FiArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={refreshFornecedoresManual}
              disabled={isRefreshing}
              title="Busca as respostas mais recentes dos fornecedores sem sobrescrever suas edições"
            >
              <FiRefreshCw
                className={`h-4 w-4${isRefreshing ? " animate-spin" : ""}`}
              />
              {isRefreshing ? "Atualizando..." : "Atualizar Respostas"}
            </button>
            <button
              className="btn btn-neutral btn-sm text-white"
              onClick={() => setHistoricoModalAberto(true)}
              title="Ver histórico de alterações desta planilha"
            >
              <FiList className="h-4 w-4" />
              Histórico
            </button>
            <button
              data-altera
              className="btn btn-success btn-sm"
              disabled={isSaving || isReadOnly}
              onClick={() => handleSave()}
              aria-keyshortcuts="Control+S Meta+S"
              title={
                isReadOnly
                  ? "Planilha bloqueada para edição por outro usuário"
                  : "Salvar planilha (Ctrl/Cmd + S)"
              }
            >
              <FiSave className="h-4 w-4" />
              {isSaving ? (
                <>
                  <span className="loading loading-spinner loading-sm" />{" "}
                  <span>Salvando...</span>{" "}
                </>
              ) : (
                "Salvar Planilha"
              )}
            </button>
            {/* <button className="btn btn-secondary btn-sm" disabled>
              <FiUpload className="h-4 w-4" />
              Importar Excel
            </button> */}
            <button
              className="btn btn-success btn-sm"
              onClick={async () => {
                try {
                  showInfo("Gerando pacote completo...");

                  // Calcular totais para o Excel
                  const totais = {
                    ...calcularTotais(),
                    breakdownEquipamentos: valorBreakdown("equipamentos"),
                    breakdownMateriais: valorBreakdown("materiais"),
                    breakdownServicos: valorBreakdown("servicos"),
                  };

                  // Gerar pacote completo (FACITE + Excel + PDFs)
                  const result =
                    await exportacaoUnificadaService.gerarPacoteCompleto(
                      planilha,
                      rowData,
                      totais,
                    );

                  showSuccess(result.message);

                  // O FACITE é montado no navegador, então é daqui que o
                  // servidor fica sabendo. Só quando o documento realmente saiu
                  // — gerarPacoteCompleto segue adiante se o FACITE falhar.
                  if (result.faciteGerado) {
                    await avancarStatus(PlanilhaStatus.FACITE_GERADA);
                  }
                } catch (error: any) {
                  showError(error.message || "Erro ao gerar pacote completo");
                }
              }}
            >
              <FiFileText className="h-4 w-4" />
              Gerar FACITE
            </button>
          </div>
        </div>
      </div>

      {/* Modal de criação de categoria */}
      <Modal
        isOpen={subgrupoModal.open}
        onClose={closeCreateSubgrupoModal}
        title="Nova Categoria"
        footer={
          <>
            <button
              className="btn btn-ghost"
              onClick={closeCreateSubgrupoModal}
            >
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={confirmCreateSubgrupo}>
              Adicionar
            </button>
          </>
        }
      >
        <div className="form-control gap-2">
          <label className="label">
            <span className="label-text">Selecione a categoria</span>
          </label>
          <select
            className="select select-bordered w-full"
            value={subgrupoModal.nome}
            onChange={(event) =>
              setSubgrupoModal((prev) => ({
                ...prev,
                nome: event.target.value,
                error: "",
              }))
            }
            autoFocus
          >
            <option value="">Selecione uma categoria...</option>
            {categorias.map((categoria, idx) => (
              <option key={idx} value={categoria}>
                {categoria}
              </option>
            ))}
          </select>
          {subgrupoModal.error && (
            <p className="text-error text-sm">{subgrupoModal.error}</p>
          )}
        </div>
      </Modal>

      {/* Modal de Despesas Acessórias */}
      <Modal
        isOpen={modalDespesasAberta}
        onClose={() => setModalDespesasAberta(false)}
        title="Adicionar Despesas Acessórias"
        footer={
          <>
            <button
              className="btn btn-ghost"
              onClick={() => setModalDespesasAberta(false)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={confirmarCriarItem3}
              disabled={categoriasSelecionadas.length === 0}
            >
              Adicionar ({categoriasSelecionadas.length})
            </button>
          </>
        }
      >
        <div className="form-control gap-4">
          <div className="alert alert-info">
            <FiBarChart2 className="h-5 w-5" />
            <span className="text-sm">
              Selecione as categorias de despesas que deseja adicionar. A mesma
              categoria pode ser adicionada mais de uma vez — depois é só
              renomear a linha para diferenciar (ex: "Aéreo ida" e "Aéreo
              volta"). Para adicionar outras, use o botão + do subgrupo.
            </span>
          </div>

          <div>
            <label className="label">
              <span className="label-text font-semibold">
                Categorias Disponíveis
              </span>
            </label>
            <div className="space-y-2">
              {categoriasParaAdicionar.map((categoria) => (
                <label
                  key={categoria}
                  className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    checked={categoriasSelecionadas.includes(categoria)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCategoriasSelecionadas([
                          ...categoriasSelecionadas,
                          categoria,
                        ]);
                      } else {
                        setCategoriasSelecionadas(
                          categoriasSelecionadas.filter((c) => c !== categoria),
                        );
                      }
                    }}
                  />
                  <span>{categoria}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Modals de envio de orçamento */}
      <ModalConfirmarInvalidacaoCotacoes
        isOpen={itensAfetadosInvalidacao !== null}
        itensAfetados={itensAfetadosInvalidacao ?? []}
        onCancelar={() => setItensAfetadosInvalidacao(null)}
        onConfirmar={confirmarInvalidacaoCotacoes}
        salvando={isSaving}
      />

      <ModalEnvioIndividual
        isOpen={modalIndividualAberta}
        onClose={() => setModalIndividualAberta(false)}
        onSend={enviarIndividual}
        itemNome={dadosModalAtual?.itemNome || ""}
        fornecedorNome={dadosModalAtual?.fornecedorNome || ""}
        emailPadrao={emailPadraoDoFornecedor(dadosModalAtual?.fornecedor)}
        telefonePadrao={dadosModalAtual?.fornecedor?.telefone}
      />

      <ModalEnvioItem
        isOpen={modalItemAberta}
        onClose={() => setModalItemAberta(false)}
        onSend={enviarPorItem}
        itemNome={dadosModalAtual?.itemNome || ""}
        fornecedores={dadosModalAtual?.fornecedores || []}
      />

      {/* Exportar Excel: o padrão continua sendo só o que foi respondido, que
          é o que vai ao cliente e ao FACITE. "Todos" serve para acompanhar a
          cotação, e só existe aqui: o FACITE não recebe a opção. */}
      <Modal
        isOpen={modalExportarAberto}
        onClose={() => setModalExportarAberto(false)}
        title="Exportar Excel"
        size="small"
      >
        <div className="space-y-2">
          {(
            [
              {
                todos: false,
                titulo: "Somente com orçamento respondido",
                texto: "Fornecedores que ainda não responderam ficam de fora.",
              },
              {
                todos: true,
                titulo: "Todos os itens",
                texto:
                  "Inclui quem não respondeu, sem preço e com a situação no lugar dele.",
              },
            ] as const
          ).map((opcao) => (
            <button
              key={String(opcao.todos)}
              className={`btn btn-sm w-full h-auto py-2 flex-col items-start text-left ${
                opcao.todos ? "btn-outline" : "btn-neutral text-white"
              }`}
              onClick={() => {
                setModalExportarAberto(false);
                excelExportService.exportToExcel(
                  planilha,
                  rowData,
                  {
                    ...calcularTotais(),
                    // valorBreakdown, e não o estado cru: com percentual
                    // preenchido o valor é derivado da base, e ler o absoluto
                    // exportaria o número velho.
                    breakdownEquipamentos: valorBreakdown("equipamentos"),
                    breakdownMateriais: valorBreakdown("materiais"),
                    breakdownServicos: valorBreakdown("servicos"),
                  },
                  { incluirSemResposta: opcao.todos },
                );
              }}
            >
              <span className="font-semibold">{opcao.titulo}</span>
              <span className="font-normal text-xs opacity-80">
                {opcao.texto}
              </span>
            </button>
          ))}
        </div>
      </Modal>

      {/* Versões do PDF de uma cotação: a do fornecedor e as de cada edição
          da equipe. A atual é a que vale para planilha, FACITE e Wrike. */}
      <Modal
        isOpen={!!historicoPdf}
        onClose={() => setHistoricoPdf(null)}
        title={`Histórico de PDFs${historicoPdf?.nome ? ` · ${historicoPdf.nome}` : ""}`}
        size="small"
      >
        {historicoPdf &&
          (historicoPdf.versoes === null ? (
            <div className="flex justify-center py-6">
              <span className="loading loading-spinner" />
            </div>
          ) : historicoPdf.erro ? (
            <div className="text-sm text-error">{historicoPdf.erro}</div>
          ) : historicoPdf.versoes.length === 0 ? (
            <div className="text-sm opacity-70">
              Esta cotação ainda não tem PDF.
            </div>
          ) : (
            <ul className="space-y-2">
              {historicoPdf.versoes.map((v) => (
                <li
                  key={`${v.versao}-${v.pdfUrl}`}
                  className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${
                    v.atual ? "border-primary bg-primary/5" : "border-base-300"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      Versão {v.versao}
                      {v.atual && (
                        <span className="badge badge-primary badge-sm">Atual</span>
                      )}
                    </div>
                    <div className="text-xs opacity-70">
                      {v.origem === "edicao_interna"
                        ? `Editada por ${v.autor}`
                        : "Enviada pelo fornecedor"}
                      {v.criadoEm
                        ? ` · ${parseDataApi(v.criadoEm).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}`
                        : ""}
                    </div>
                  </div>
                  <a
                    className="btn btn-ghost btn-xs border border-base-300"
                    href={v.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FiDownload className="h-4 w-4" />
                    Abrir
                  </a>
                </li>
              ))}
            </ul>
          ))}
      </Modal>

      {/* Menu da linha preta. Modal pelo mesmo motivo do menu de fornecedor
          abaixo: o overflow da tabela cortaria um dropdown. */}
      <Modal
        isOpen={!!menuCategoria}
        onClose={() => setMenuCategoria(null)}
        title={menuCategoria?.item || "Categoria"}
        size="small"
      >
        {menuCategoria && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="font-medium">Trocar categoria</div>
              <div className="text-sm opacity-70">
                Muda só o nome. Itens, fornecedores e cotações já enviadas ou
                recebidas continuam como estão.
              </div>
              <div className="flex flex-col gap-1">
                {categorias
                  .filter((c) => c !== menuCategoria.item)
                  .map((c) => (
                    <button
                      key={c}
                      className="btn btn-ghost btn-sm justify-start font-normal"
                      onClick={() => handleTrocarCategoria(menuCategoria.id!, c)}
                    >
                      {c}
                    </button>
                  ))}
              </div>
            </div>

            <div className="border-t border-base-300 pt-4 space-y-2">
              <button
                className="btn btn-outline btn-sm w-full"
                onClick={() => {
                  handleDuplicar(menuCategoria.id!);
                  setMenuCategoria(null);
                }}
              >
                <FiCopy className="h-4 w-4" />
                Duplicar categoria com os itens
              </button>
              <div className="text-xs opacity-60">
                A cópia leva itens, descritivos e fornecedores escolhidos, sem
                valores cotados nem anexos.
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Ações secundárias da linha de fornecedor.
          Fica fora da tabela porque o container dela tem overflow-auto e
          cortaria qualquer menu posicionado em CSS. */}
      <Modal
        isOpen={!!acoesFornecedor}
        onClose={() => setAcoesFornecedor(null)}
        title={acoesFornecedor?.item || "Fornecedor"}
        size="small"
      >
        {acoesFornecedor &&
          (() => {
            const linha = acoesFornecedor;
            const status = String(linha.status || "").toLowerCase();
            const aguardandoResposta =
              status === "enviada" || status === "reenviada";

            // Digitou preço e saiu antes de assinar. Sem este aviso a linha
            // parece intocada, e o valor não entra em lugar nenhum.
            const preencheuSemFinalizar =
              !linha.dataResposta &&
              (linha.unitario ?? 0) > 0 &&
              status !== "rejeitada";

            const cooldownMs = 24 * 60 * 60 * 1000;
            const ultimo =
              (linha.orcamentoId && lembreteInfoById[linha.orcamentoId]) ||
              linha.ultimoLembreteEm;
            const ultimoEm = ultimo ? parseDataApi(ultimo).getTime() : null;
            const emCooldown =
              ultimoEm !== null ? Date.now() - ultimoEm < cooldownMs : false;
            const disponivelEm =
              ultimoEm !== null ? new Date(ultimoEm + cooldownMs) : null;
            const enviandoLembrete = lembreteLoadingId === linha.orcamentoId;

            const link = linha.orcamentoId
              ? `${window.location.origin}/orcamento/${linha.orcamentoId}`
              : null;

            return (
              <div className="space-y-4">
                {/* Quando o fornecedor abriu o link */}
                <div className="rounded-lg border border-base-300 p-3">
                  <div className="flex items-center gap-2 font-medium">
                    {linha.ultimoAcessoEm ? (
                      <FiEye className="h-4 w-4" />
                    ) : (
                      <FiEyeOff className="h-4 w-4 opacity-50" />
                    )}
                    {linha.ultimoAcessoEm
                      ? "Link já aberto pelo fornecedor"
                      : "O fornecedor nunca abriu o link"}
                  </div>

                  {linha.ultimoAcessoEm && (
                    <div className="text-sm opacity-70 mt-1">
                      Última vez {formatarTempoRelativo(linha.ultimoAcessoEm)} ·{" "}
                      {parseDataApi(linha.ultimoAcessoEm).toLocaleString(
                        "pt-BR",
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                      {linha.totalAcessos && linha.totalAcessos > 0
                        ? ` · ${linha.totalAcessos} acesso${
                            linha.totalAcessos > 1 ? "s" : ""
                          }`
                        : ""}
                    </div>
                  )}

                  {preencheuSemFinalizar ? (
                    <div className="mt-2 text-sm">
                      <div className="font-semibold text-warning">
                        Preencheu e não finalizou
                      </div>
                      <div className="opacity-70">
                        Chegou a informar{" "}
                        <span className="font-semibold">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(linha.unitario ?? 0)}
                        </span>{" "}
                        e saiu antes de assinar. Sem assinatura não há
                        documento, então este valor não entra no menor preço nem
                        no FACITE.
                      </div>
                    </div>
                  ) : (
                    linha.ultimoAcessoEm && (
                      <div className="text-xs opacity-50 mt-1">
                        Abrir o link não significa que já respondeu.
                      </div>
                    )
                  )}
                </div>

                {/* Link de preenchimento — só enquanto a resposta não veio */}
                {aguardandoResposta && link && (
                  <div className="rounded-lg border border-base-300 p-3 space-y-2">
                    <div className="font-medium">Link de preenchimento</div>
                    <div className="text-sm opacity-70">
                      Para quando o fornecedor não recebeu ou perdeu o e-mail.
                      É o mesmo link que foi enviado.
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-primary btn-sm flex-1"
                        onClick={() => copiarLinkOrcamento(link)}
                      >
                        <FiCopy className="h-4 w-4" />
                        Copiar link
                      </button>
                      <a
                        className="btn btn-ghost btn-sm border border-base-300"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir em nova aba"
                      >
                        <FiExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                )}

                {/* Lembrete */}
                {aguardandoResposta && linha.orcamentoId && (
                  <button
                    data-altera
                    className="btn btn-outline btn-sm w-full"
                    disabled={enviandoLembrete || emCooldown || isReadOnly}
                    onClick={() => {
                      handleLembrar(linha.orcamentoId!, linha.item);
                      setAcoesFornecedor(null);
                    }}
                    title={
                      emCooldown && disponivelEm
                        ? `Lembrete já enviado. Disponível de novo em ${disponivelEm.toLocaleString(
                            "pt-BR",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}`
                        : "Enviar lembrete ao fornecedor"
                    }
                  >
                    {enviandoLembrete ? (
                      <span className="loading loading-spinner loading-xs" />
                    ) : (
                      <FiBell className="h-4 w-4" />
                    )}
                    {emCooldown ? "Lembrete já enviado hoje" : "Enviar lembrete"}
                  </button>
                )}

                {/* Edição interna: qualquer status. Abre o mesmo portal do
                    fornecedor, já preenchido, em outra aba para a planilha
                    continuar aberta. Ao salvar lá, o PDF é gerado de novo e o
                    status da cotação não muda. */}
                {linha.orcamentoId && (
                  <div className="rounded-lg border border-base-300 p-3 space-y-2">
                    <div data-altera className="font-medium">
                      Editar cotação
                    </div>
                    <div data-altera className="text-sm opacity-70">
                      Corrige valores ou dados da proposta em nome da
                      Monumenta. O PDF é gerado de novo e substitui o atual.
                    </div>
                    <button
                      data-altera
                      className="btn btn-outline btn-sm w-full"
                      onClick={() => {
                        window.open(
                          `/orcamento/${linha.orcamentoId}?edicao=interna`,
                          "_blank",
                        );
                        setAcoesFornecedor(null);
                      }}
                    >
                      <FiEdit2 className="h-4 w-4" />
                      Editar cotação
                    </button>
                    <button
                      className="btn btn-ghost btn-sm w-full"
                      onClick={() => {
                        abrirHistoricoPdf(linha.orcamentoId!, linha.item);
                        setAcoesFornecedor(null);
                      }}
                    >
                      <FiClock className="h-4 w-4" />
                      Histórico de PDFs
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
      </Modal>

      <ModalEnvioFornecedor
        isOpen={modalFornecedorAberta}
        onClose={() => setModalFornecedorAberta(false)}
        onSend={enviarPorFornecedor}
        fornecedores={dadosModalAtual?.fornecedores || []}
      />
      <ModalAdicionarFornecedor
        isOpen={modalAdicionarFornecedorAberta}
        defaultCnpj={cnpjParaNovoFornecedor}
        onClose={(fornecedor) => {
          setModalAdicionarFornecedorAberta(false);
          setCnpjParaNovoFornecedor("");
          if (fornecedor) {
            setFornecedorRecemCadastrado(fornecedor);
            showSuccess("Fornecedor cadastrado com sucesso!");
          }
        }}
      />
    </div>
  );
}

export default EditarPlanilhaV3;
